import { OrphanVideo, listOrphanVideos, removeOrphanVideo } from '../services/orphanVideos';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Scissors, Clock, Eye, Youtube, Loader2, Globe, Lock, Zap, ExternalLink, Upload, Calendar, Save, RefreshCw, Sparkles, Image as ImageIcon, Play, Edit3, Download, Link as LinkIcon, Settings, Minimize2, Maximize2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, ListVideo, Plus, MessageSquare, Send } from 'lucide-react';
import { getFirestore, collection, getDocs, doc, getDoc, deleteField, onSnapshot, query, where } from 'firebase/firestore';
// Note documents are written through safeUpdateDoc, which re-applies video patches onto the
// note as it is on the server (a stale copy must not drop videos linked in the meantime).
import { safeUpdateDoc as updateDoc } from '../services/videoPatchWrite';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../services/firebase';
import { MindMapNode, Language, AiCheckPass } from '../types';
import { updateYouTubeVideo, isYouTubeConnected, connectYouTube, subscribeYouTube, getYouTubeVideoStatus, fetchYouTubePlaylists, fetchYouTubeVideoSnippet, fetchYouTubeAudience, fetchPlaylistMembershipMap, deleteYouTubeVideo, createYouTubePlaylist, addVideoToPlaylist, YouTubePlaylist, fetchYouTubeTranscript, fetchMyYouTubeVideos, MyYouTubeVideo, getVideosForNode, isVideoOnGithub, buildVideoAppendPatch, buildVideoUpdatePatch, getNoteVideoStatus } from '../services/youtubeService';
import { YouTubeVideoEntry, VideoIdea, VideoIdeaStatus } from '../types';
import { AutopilotButton } from './AutopilotPanel';
import { ThumbnailBatchPanel } from './ThumbnailBatchPanel';
import { ThumbnailStudio, ThumbTabs, type StudioImage, type StudioState } from './ThumbnailStudio';
import { ThumbnailEditorModal, extractThumbStyle, defaultThumbSettings, renderThumbnailFromSettings, downscaleToJpeg, type ThumbSettings, type ThumbDraft, type ThumbStyleOnly } from './ThumbnailEditorModal';
import { sanitizeForYouTube, toHashtag } from '../utils/youtubeText';
import { YouTubeManagerChat, ChatVideoSnapshot } from './YouTubeManagerChat';
import type { ManagerChatChange } from '../services/geminiService';
import { formatChaptersForDescription, extractLinkBlocks, addRelatedLinksToDescription } from '../utils/youtubeChapters';
import BatchChaptersPanel from './BatchChaptersPanel';
import TrimVideoPanel, { TrimPhase } from './TrimVideoPanel';
import { suggestVideoPlaylists, PlaylistSuggestion, generateNoteFromTranscript, generateVideoChapters, generateYouTubeDescription, generateTitleForNode, checkTranscriptQuality, discussAiCheck, generateYouTubeTopicHashtags, suggestCombinedVideo, CombinedVideoSuggestion } from '../services/geminiService';
import { resolveNoteLink } from '../services/socialService';
import { NoteMapGrid } from './NoteMapGrid';
import { getDocumentOrder } from '../utils/noteSequence';

interface NotebookMeta { id: string; title: string; }

// Quiet secondary buttons for bulk actions (one primary action stays solid).
const BULK_CHIP = 'flex items-center gap-1 whitespace-nowrap flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 hover:border-indigo-300 hover:text-indigo-600 dark:hover:text-indigo-300 disabled:opacity-40 transition-colors';

// Mirrors functions/src/index.ts's EXPORT_PENDING_STALE_MS / EXPORT_PROCESSING_STALE_MS — kept in
// sync by comment, not by import, since client and functions are separate build targets. Used only
// to decide whether to *show* a resume banner; the server re-validates staleness independently
// inside resumeVideoExportJob's transaction, so a mismatch here is a UX nit, not a correctness bug.
const EXPORT_PENDING_STALE_MS = 10 * 60 * 1000;
const EXPORT_PROCESSING_STALE_MS = 20 * 60 * 1000;

interface ExportJobDoc {
    id: string;
    status: 'pending' | 'processing' | 'done' | 'error';
    error?: string;
    storagePathFinal?: string;
    consumed?: boolean;
    nodeId?: string;
    notebookId?: string;
    createdAt?: { toMillis: () => number };
    startedAt?: { toMillis: () => number };
    heartbeatAt?: { toMillis: () => number };
    progress?: number; // 0-100, written by the Cloud Run worker while it renders
    phase?: string;
}

/** The most recent job that is pending/processing and NOT stale — i.e. running normally right now. */
function findRunningJob(jobs: ExportJobDoc[]): ExportJobDoc | null {
    const running = jobs.filter(j => (j.status === 'pending' || j.status === 'processing') && !findJobNeedingAttention([j]));
    running.sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0));
    return running[0] ?? null;
}

/** The most recent job for a note that still needs the user's attention, or null if none does. */
function findJobNeedingAttention(jobs: ExportJobDoc[]): ExportJobDoc | null {
    const now = Date.now();
    const candidates = jobs.filter(j => {
        if (j.status === 'error') return true;
        if (j.status === 'done') return !j.consumed;
        if (j.status === 'pending') return now - (j.createdAt?.toMillis() ?? 0) > EXPORT_PENDING_STALE_MS;
        if (j.status === 'processing') {
            const lastSeen = j.heartbeatAt?.toMillis() ?? j.startedAt?.toMillis() ?? 0;
            return now - lastSeen > EXPORT_PROCESSING_STALE_MS;
        }
        return false;
    });
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0));
    return candidates[0];
}

// YouTube serves a video's thumbnail from one fixed URL, and i.ytimg.com answers 404 to that URL
// with any extra query string — so a "?v=" cache-buster cannot be used. Instead, when a thumbnail
// has been pushed, the browser's cached copy is refreshed with a cache-bypassing request and the
// <img> is remounted to pick it up. Each push is refreshed once per page load.
function ytThumbUrl(video: { youtubeVideoId: string }, size: 'mqdefault' | 'hqdefault'): string {
    return `https://i.ytimg.com/vi/${video.youtubeVideoId}/${size}.jpg`;
}

const refreshedThumbs = new Set<string>();

function YtThumb({ video, size, className, alt }: {
    video: { youtubeVideoId: string; thumbnailPushedAt?: number; youtubeUploadedAt?: number };
    size: 'mqdefault' | 'hqdefault';
    className?: string;
    alt?: string;
}) {
    const url = ytThumbUrl(video, size);
    const [version, setVersion] = useState(0);
    useEffect(() => {
        // Also reload once per page load for a recently uploaded video: its thumbnail is the one
        // most likely to have just changed, including changes made outside this app.
        const recent = !!video.youtubeUploadedAt && Date.now() - video.youtubeUploadedAt < 14 * 24 * 3600 * 1000;
        if (!video.thumbnailPushedAt && !recent) return;
        const key = `${url}|${video.thumbnailPushedAt ?? 'recent'}`;
        if (refreshedThumbs.has(key)) return;
        refreshedThumbs.add(key);
        let alive = true;
        fetch(url, { mode: 'no-cors', cache: 'reload' }).then(() => { if (alive) setVersion(v => v + 1); }).catch(() => {});
        return () => { alive = false; };
    }, [url, video.thumbnailPushedAt, video.youtubeUploadedAt]);
    return <img key={version} src={url} alt={alt} className={className} onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />;
}

// Shared by the manual "Đưa vào mô tả" button and the automatic "AI tóm tắt" flow — a genuinely
// three-sided block (what's right / what's an actual mistake / what's correct-but-unclear), never
// a one-line "AI approved this" stamp. Per explicit product decision: a description that only ever
// lists strengths reads as biased rather than a real independent check, so every severity is shown
// — nothing is filtered out as "not worth showing a viewer" anymore.
function formatAiCheckForDescription(
    check: { score: number; checkSummary: string; strengths: string[]; findings: { severity: string; note: string }[] },
    vn: boolean,
): string {
    const lines: string[] = [`📊 AI Check: ${check.score}/100${check.checkSummary ? ` — ${check.checkSummary}` : ''}`];
    if (check.strengths.length > 0) {
        lines.push('', vn ? '✓ Điểm đúng:' : "✓ What's right:");
        check.strengths.forEach(s => lines.push(`- ${s}`));
    }
    // Always shown, never omitted when empty — an absent section could just mean "not checked",
    // but an explicit "none found" line is an actual claim the viewer can hold the check to.
    const mistakes = check.findings.filter(f => f.severity === 'major_error' || f.severity === 'minor_correction');
    lines.push('', vn ? '⚠ Ý sai:' : '⚠ Incorrect points:');
    if (mistakes.length > 0) {
        mistakes.forEach(f => lines.push(`- ${f.note}`));
    } else {
        lines.push(vn ? '- Không có — không phát hiện lỗi lớn hay lỗi nhỏ nào trong nội dung nói.' : '- None — no major or minor errors found in what was said.');
    }
    const unclear = check.findings.filter(f => f.severity === 'clarification' || f.severity === 'optional_improvement');
    if (unclear.length > 0) {
        lines.push('', vn ? '💡 Ý đúng nhưng có thể giải thích rõ hơn:' : '💡 Right idea, could be explained more clearly:');
        unclear.forEach(f => lines.push(`- ${f.note}`));
    }
    return sanitizeForYouTube(lines.join('\n'), vn ? 'vietnamese' : 'english');
}

// Swaps a freshly-formatted AI Check block into an existing description in place of whatever
// AI Check block is already there (header/summary + its ✓/⚠/💡 sub-paragraphs), leaving every
// other paragraph (topic summary, note link, footer, hashtags) untouched. Returns null if the
// description has no existing block to replace, so the caller can fall back to appending instead.
function replaceCheckBlock(description: string, newBlock: string): string | null {
    const blocks = description.split(/\n\n+/);
    const startIdx = blocks.findIndex(b => b.trimStart().startsWith('📊 AI Check:'));
    if (startIdx === -1) return null;
    let endIdx = startIdx;
    const subPrefixes = ['✓', '⚠', '💡'];
    while (endIdx + 1 < blocks.length && subPrefixes.some(p => blocks[endIdx + 1].trimStart().startsWith(p))) {
        endIdx++;
    }
    return [...blocks.slice(0, startIdx), newBlock, ...blocks.slice(endIdx + 1)].join('\n\n');
}

interface Props {
    allNotebooksMeta: NotebookMeta[];
    language: Language;
    onClose: () => void;
    onOpenUpload: (nodeId: string, notebookId: string, initialFile?: File, sourceJobId?: string) => void;
    onOpenNote?: (notebookId: string, nodeId: string) => void;
    // A note was changed here by a direct server write; lets the app keep its in-memory copy in step.
    onNodePatched?: (notebookId: string, nodeId: string, patch: Partial<MindMapNode>) => void;
}

type VideoStatus = 'none' | 'private' | 'scheduled' | 'public' | 'mixed';

interface EnhancedNode extends MindMapNode {
    _notebookId: string;
    _notebookTitle: string;
    _branchId?: string;
}

const STATUS_CONFIG: Record<VideoStatus, { label: string; labelVn: string; color: string; icon: React.ReactNode }> = {
    none:      { label: 'No video',  labelVn: 'Chưa có',    color: 'text-gray-400',                        icon: <Upload size={11} /> },
    private:   { label: 'Private',   labelVn: 'Riêng tư',   color: 'text-amber-500 dark:text-amber-400',   icon: <Lock size={11} /> },
    scheduled: { label: 'Scheduled', labelVn: 'Đã lên lịch',color: 'text-blue-500 dark:text-blue-400',     icon: <Calendar size={11} /> },
    public:    { label: 'Published', labelVn: 'Đã đăng',    color: 'text-green-500 dark:text-green-400',   icon: <Globe size={11} /> },
    mixed:     { label: 'Mixed',     labelVn: 'Hỗn hợp',    color: 'text-purple-500 dark:text-purple-400', icon: <ListVideo size={11} /> },
};

function qualityColor(score?: number) {
    if (score == null) return 'bg-gray-100 dark:bg-slate-700 text-gray-400';
    if (score >= 75) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
    if (score >= 45) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
    return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
}

const VIDEO_IDEA_STATUS_LIST: VideoIdeaStatus[] = ['suggested', 'planned', 'recorded', 'reviewed', 'published', 'dismissed'];
const IDEA_STATUS_INFO: Record<VideoIdeaStatus, { vi: string; en: string; color: string }> = {
    suggested: { vi: 'Gợi ý', en: 'Suggested', color: 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300' },
    planned: { vi: 'Đã lên kế hoạch', en: 'Planned', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
    recorded: { vi: 'Đã quay', en: 'Recorded', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
    reviewed: { vi: 'Đã duyệt', en: 'Reviewed', color: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400' },
    published: { vi: 'Đã đăng', en: 'Published', color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
    dismissed: { vi: 'Đã bỏ qua', en: 'Dismissed', color: 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500' },
};

export function YouTubeManagerModal({ allNotebooksMeta, language, onClose, onOpenUpload, onOpenNote, onNodePatched }: Props) {
    const vn = language === 'vietnamese';
    const db = getFirestore();
    const auth = getAuth();

    const [activeNotebookMeta, setActiveNotebookMeta] = useState<NotebookMeta>(allNotebooksMeta[0] || { id: '', title: '' });
    const [allNodes, setAllNodes] = useState<EnhancedNode[]>([]);
    const [branches, setBranches] = useState<EnhancedNode[]>([]);
    // Default order follows siblingOrder (however the author last arranged the canvas) — which
    // doesn't always match a clean "chapter 1, 1.1, 1.2... chapter 2" reading order, especially
    // for a notebook built up over many sessions. 'alpha' is an explicit escape hatch: a natural
    // (numeric-aware) sort of the title, so "1.2" < "1.10" < "2.1" instead of breaking on plain
    // string comparison — requested after siblingOrder didn't reflect the intended order.
    const [branchSortMode, setBranchSortMode] = useState<'default' | 'alpha'>('default');
    const branchesRef = useRef<EnhancedNode[]>([]);
    const notebookPublishUrlRef = useRef<string | null>(null);
    const [loadingNodes, setLoadingNodes] = useState(false);
    const [selectedBranchIds, setSelectedBranchIds] = useState<Set<string>>(new Set());

    // Resizable column widths
    const [branchColWidth, setBranchColWidth] = useState(220);
    const [noteColWidth, setNoteColWidth] = useState(320);
    const dragStateRef = useRef<{ col: 'branch' | 'note'; startX: number; startW: number } | null>(null);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
    const selectedVideoIdRef = useRef<string | null>(null); // for long-running work that outlives a render
    selectedVideoIdRef.current = selectedVideoId;
    const [ytConnected, setYtConnected] = useState(isYouTubeConnected());
    const [statusFilter, setStatusFilter] = useState<VideoStatus | 'all'>('all');
    type SortMode = 'default' | 'quality-desc' | 'quality-asc' | 'ai-desc' | 'ai-asc';
    const [sortMode, setSortMode] = useState<SortMode>('default');
    const [notebookPublishUrl, setNotebookPublishUrl] = useState<string | null>(null);

    // Flat cross-note video list tab — a video-centric view of the same allNodes data (scoped to
    // the active notebook, same as the tree view), for browsing/publishing/flagging every video
    // one by one instead of drilling into branches → notes. Score display deliberately deferred —
    // the user will specify scoring criteria for this view in a follow-up.
    const [viewMode, setViewMode] = useState<'notes' | 'videos' | 'ideas' | 'map'>('notes');
    const [videoTabFilter, setVideoTabFilter] = useState<'all' | 'private' | 'scheduled' | 'public' | 'unchecked'>('all');
    type VideoSortMode = 'default' | 'time-desc' | 'time-asc';
    const [videoSortMode, setVideoSortMode] = useState<VideoSortMode>('time-desc');

    // "Ý tưởng video" tab — a flat backlog across EVERY notebook (not just the active one, unlike
    // the other two tabs) of every note that already has a Video-Worthy Score, so the author can
    // scan/sort by score and pick what to record next instead of that data staying buried one note
    // at a time. Deliberately includes notes that already have a video (not just unrecorded ones) —
    // the point is a single coverage view ("how much of what's worth recording have I recorded"),
    // not just a to-do list. Lazy-loaded on first visit to this tab (cross-notebook fetch), not on
    // modal open, since most sessions never touch it.
    const [videoIdeas, setVideoIdeas] = useState<VideoIdea[]>([]);
    const [loadingIdeas, setLoadingIdeas] = useState(false);
    const [ideasLoadedOnce, setIdeasLoadedOnce] = useState(false);
    const [isMigratingIdeas, setIsMigratingIdeas] = useState(false);
    const [migrateIdeasMsg, setMigrateIdeasMsg] = useState('');
    const [ideaSort, setIdeaSort] = useState<'score-desc' | 'score-asc'>('score-desc');
    const [ideaNotebookFilter, setIdeaNotebookFilter] = useState(''); // '' = all notebooks
    // Which idea's detail shows on the right — separate from selectedNodeId (the Notes tab's own
    // concept) so clicking a row here just updates this panel in place. Explicitly does NOT
    // navigate to the Notes tab; only the dedicated jump button/icon does that (see
    // handleGoToIdeaNote) — clicking a row to just glance at the detail was, per explicit
    // feedback, the whole point of this tab having its own detail pane at all.
    const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
    // Set right before switching to the Notes tab from an idea card's "go to note" click — loadNodes
    // (triggered by the resulting activeNotebookMeta change) picks this up once that notebook's
    // nodes finish loading and selects the note, same as clicking it directly in the sidebar would.
    const pendingSelectNodeIdRef = useRef<string | null>(null);

    // Combine existing videos — select 2+ videos in the Video tab, order them, and merge into one
    // new video via the generalized multi-source export pipeline (see plan doc). Key = `${nodeId}::${videoId}`.
    const [checkedVideoIds, setCheckedVideoIds] = useState<Set<string>>(new Set());
    const [showCombinePanel, setShowCombinePanel] = useState(false);
    const [combineOrder, setCombineOrder] = useState<{ key: string; node: EnhancedNode; video: YouTubeVideoEntry }[]>([]);
    const [combineTargetNodeId, setCombineTargetNodeId] = useState<string | null>(null);
    const [combinePhase, setCombinePhase] = useState<'idle' | 'pending' | 'processing' | 'done' | 'error'>('idle');
    // Server-side render progress (0-100) from the Cloud Run worker, for combine / resumed jobs.
    const [combineProgress, setCombineProgress] = useState<number | null>(null);
    const [resumeProgress, setResumeProgress] = useState<{ status?: string; pct?: number; phase?: string } | null>(null);
    const [combineError, setCombineError] = useState('');
    const combineUnsubRef = useRef<(() => void) | null>(null);
    // Autopilot on (same switch as the record window): a combine/trim is uploaded to YouTube and
    // processed by the server like a fresh recording, instead of opening the manual upload window.
    const [autopilotOn, setAutopilotOn] = useState(false);
    useEffect(() => {
        const u = auth.currentUser?.uid;
        if (!u) return;
        return onSnapshot(doc(db, `users/${u}/autopilot/config`), snap => setAutopilotOn(snap.data()?.enabled === true), () => {});
    }, []);
    // Set once a combine was handed to the server ('uploading' → 'done'); shown in the panel.
    const [combineAuto, setCombineAuto] = useState<null | { state: 'uploading' | 'done' | 'error'; error?: string }>(null);
    useEffect(() => () => { combineUnsubRef.current?.(); }, []); // unsubscribe if the modal unmounts mid-combine

    // Resumable export jobs for the selected note — see "Resumable video export/upload jobs" plan.
    const [noteExportJobs, setNoteExportJobs] = useState<ExportJobDoc[]>([]);
    const [resumingJobId, setResumingJobId] = useState<string | null>(null);
    const [jobActionError, setJobActionError] = useState('');
    const resumeTrackUnsubRef = useRef<(() => void) | null>(null);
    useEffect(() => () => { resumeTrackUnsubRef.current?.(); }, []);

    // Detail panel state
    const [editTitle, setEditTitle] = useState('');
    const [editDesc, setEditDesc] = useState('');
    // "Add a link to another video": pick videos, their links are put into the description box
    // (nothing reaches YouTube until the usual Save).
    // ── Videos whose note was deleted ──
    const [orphans, setOrphans] = useState<OrphanVideo[]>([]);
    const [showOrphans, setShowOrphans] = useState(false);
    const [orphanQuery, setOrphanQuery] = useState<Record<string, string>>({});
    const [orphanBusy, setOrphanBusy] = useState<string | null>(null);
    const [orphanMsg, setOrphanMsg] = useState('');
    const [showLinkPicker, setShowLinkPicker] = useState(false);
    const [linkQuery, setLinkQuery] = useState('');
    const [linkPicked, setLinkPicked] = useState<string[]>([]);
    const [scheduleDate, setScheduleDate] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [isGenDesc, setIsGenDesc] = useState(false);
    const [isGenHashtags, setIsGenHashtags] = useState(false);
    const [isGenTitle, setIsGenTitle] = useState(false);
    // Checklist checkmarks (see the "Chuẩn bị video" panel) — Check's own done-state is
    // transcriptCheck itself; these three exist because title/desc/hashtag can't otherwise tell
    // "never generated" apart from "generated then hand-edited".
    const [genTitleDone, setGenTitleDone] = useState(false);
    const [genDescDone, setGenDescDone] = useState(false);
    const [genHashtagDone, setGenHashtagDone] = useState(false);
    const [isRunningAllSteps, setIsRunningAllSteps] = useState(false);

    // Batch scoring state
    const [isBatchScoring, setIsBatchScoring] = useState(false);
    // Non-null while the "rescore everything vs. skip already-scored" choice dialog is open —
    // holds the exact note IDs to run runBatchScore on once the user picks a mode — always
    // resolved to notes up front (not branch IDs), so both the "no checkboxes, one branch" path
    // and the "branches/notes checked" path share the exact same dialog and function.
    const [pendingScoreNodeIds, setPendingScoreNodeIds] = useState<string[] | null>(null);
    const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
    const [showBatchChapters, setShowBatchChapters] = useState(false);
    // Trim a part of an existing video into a new one (TrimVideoPanel picks the range; the export
    // job below does the cutting).
    const [trimTarget, setTrimTarget] = useState<{ node: EnhancedNode; video: YouTubeVideoEntry; autoSuggest?: boolean } | null>(null);
    const [trimPhase, setTrimPhase] = useState<TrimPhase>('idle');
    const [trimProgress, setTrimProgress] = useState<number | null>(null);
    const [trimError, setTrimError] = useState('');
    const trimUnsubRef = useRef<(() => void) | null>(null);
    // Long jobs can run in the background: the panel is hidden (kept mounted) and a pill in the
    // corner shows progress and brings it back.
    const [trimMinimized, setTrimMinimized] = useState(false);
    const [chaptersMinimized, setChaptersMinimized] = useState(false);
    const [chaptersBusy, setChaptersBusy] = useState(false);
    useEffect(() => { if (!trimTarget) setTrimMinimized(false); }, [trimTarget]);
    useEffect(() => { if (!showBatchChapters) { setChaptersMinimized(false); setChaptersBusy(false); } }, [showBatchChapters]);
    const [scoreQuotaError, setScoreQuotaError] = useState('');

    // Combined-video suggestion (multiple selected notes → one bigger video concept)
    const [isSuggestingCombined, setIsSuggestingCombined] = useState(false);
    const [combinedSuggestion, setCombinedSuggestion] = useState<CombinedVideoSuggestion | null>(null);
    const [combinedSuggestError, setCombinedSuggestError] = useState('');
    const batchAbortRef = useRef(false);

    // Checkbox selection — notes
    const [checkedNodeIds, setCheckedNodeIds] = useState<Set<string>>(new Set());
    const [scoringNodeIds, setScoringNodeIds] = useState<Set<string>>(new Set());

    // Checkbox selection — branches
    const [checkedBranchIds, setCheckedBranchIds] = useState<Set<string>>(new Set());

    // Minimized mode (keep scoring loop alive while on canvas)
    const [minimized, setMinimized] = useState(false);

    // Scoring settings
    const [scoringCriteria, setScoringCriteria] = useState('');
    const [showScoringSettings, setShowScoringSettings] = useState(false);
    const [isSavingCriteria, setIsSavingCriteria] = useState(false);

    // Thumbnail editor
    // One Thumbnail window with two tabs: the Studio (ready-made layouts) and the hand-made editor.
    const [thumbTab, setThumbTabState] = useState<'studio' | 'editor' | null>(null);
    const setThumbTab = (t: 'studio' | 'editor') => { setThumbTabState(t); try { localStorage.setItem('yt_thumb_tab', t); } catch { /* ignore */ } };
    const openThumbWindow = () => { let t: 'studio' | 'editor' = 'studio'; try { if (localStorage.getItem('yt_thumb_tab') === 'editor') t = 'editor'; } catch { /* ignore */ } setThumbTabState(t); };
    const showThumbEditor = thumbTab === 'editor';
    const showThumbStudio = thumbTab === 'studio';
    const setShowThumbEditor = (on: boolean) => { if (on) openThumbWindow(); else setThumbTabState(null); };
    const setShowThumbStudio = (on: boolean) => { if (on) setThumbTabState('studio'); else setThumbTabState(null); };
    const [showThumbBatch, setShowThumbBatch] = useState(false);
    const [thumbBatchMsg, setThumbBatchMsg] = useState('');
    const [thumbDataUrl, setThumbDataUrl] = useState('');
    const [isUploadingThumb, setIsUploadingThumb] = useState(false);
    const [thumbMsg, setThumbMsg] = useState('');

    // Optional explicit scope ("what this clip explains") fed into checkTranscriptQuality so it
    // grades only this, not the whole note — the video is a spoken companion to an already-checked note.
    const [clipFocusInput, setClipFocusInput] = useState('');
    const [isSavingClipFocus, setIsSavingClipFocus] = useState(false);

    // Transcript
    const [transcript, setTranscript] = useState<string | null>(null);
    const [isFetchingTranscript, setIsFetchingTranscript] = useState(false);
    const [transcriptError, setTranscriptError] = useState('');
    const [showTranscript, setShowTranscript] = useState(false);
    const [showRecordingChecks, setShowRecordingChecks] = useState(false);
    const [transcriptCheck, setTranscriptCheck] = useState<{ recommendation: 'publish' | 'publish_with_correction' | 're_record'; strengths: string[]; findings: { severity: string; note: string }[]; checkSummary: string; score: number; passes?: AiCheckPass[] } | null>(null);
    const [isCheckingTranscript, setIsCheckingTranscript] = useState(false);

    // "Argue with AI Check" — ephemeral chat, never persisted (only an accepted revised verdict is).
    const [showDiscussPanel, setShowDiscussPanel] = useState(false);
    const [discussMessages, setDiscussMessages] = useState<{ role: 'user' | 'ai'; text: string; revisedVerdict?: NonNullable<typeof transcriptCheck> }[]>([]);
    const [discussInput, setDiscussInput] = useState('');
    const [discussLoading, setDiscussLoading] = useState(false);

    // Manual link
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkInputValue, setLinkInputValue] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    const [linkError, setLinkError] = useState('');

    // Playlist
    const [playlists, setPlaylists] = useState<YouTubePlaylist[]>([]);
    const [playlistsLoaded, setPlaylistsLoaded] = useState(false);
    const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false);
    const [playlistMsg, setPlaylistMsg] = useState('');
    const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
    const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
    const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);
    const [addingToPlaylistId, setAddingToPlaylistId] = useState<string | null>(null);

    // Orphan video scanner
    const [showOrphanScanner, setShowOrphanScanner] = useState(false);
    const [isScanningOrphans, setIsScanningOrphans] = useState(false);
    const [orphanMatches, setOrphanMatches] = useState<{ video: MyYouTubeVideo; nodeId: string; nodeTitle: string; notebookId: string }[]>([]);
    const [orphanScanDone, setOrphanScanDone] = useState(false);
    const [linkingOrphan, setLinkingOrphan] = useState<string | null>(null);

    const loadNodes = useCallback(async (meta: NotebookMeta) => {
        const uid = auth.currentUser?.uid;
        if (!uid || !meta.id) return;
        setLoadingNodes(true);
        try {
            const snap = await getDocs(collection(db, `users/${uid}/notebooks/${meta.id}/nodes`));
            const rawNodes: EnhancedNode[] = snap.docs
                .map(d => ({ id: d.id, ...d.data(), _notebookId: meta.id, _notebookTitle: meta.title } as EnhancedNode))
                .filter(n => n.parentId != null);

            // Build node map for ancestry lookup
            const nodeMap = new Map(rawNodes.map(n => [n.id, n]));
            const nodeIds = new Set(rawNodes.map(n => n.id));

            // L1 branches: direct children of root (parent not in our set)
            const l1Nodes = rawNodes
                .filter(n => !nodeIds.has(n.parentId!))
                .sort((a, b) => (a.siblingOrder ?? 0) - (b.siblingOrder ?? 0) || (a.title || '').localeCompare(b.title || ''));

            // Find L1 ancestor for each node
            function findBranchId(nodeId: string): string {
                let current = nodeMap.get(nodeId);
                while (current?.parentId && nodeIds.has(current.parentId)) {
                    current = nodeMap.get(current.parentId);
                }
                return current?.id || nodeId;
            }

            // Notes: non-label nodes with a branchId assigned, in document (reading) order — a
            // pre-order walk of the tree, siblings by siblingOrder. Firestore returns docs by id,
            // i.e. effectively shuffled, which put a branch's own title note in the middle of its
            // children (visible on the Map tab) and scrambled the default order of the other tabs.
            const docOrder = new Map(getDocumentOrder(rawNodes).map((id, i) => [id, i]));
            const notes: EnhancedNode[] = rawNodes
                .filter(n => n.nodeType !== 'label')
                .map(n => ({ ...n, _branchId: findBranchId(n.id) }))
                .sort((a, b) => (docOrder.get(a.id) ?? 0) - (docOrder.get(b.id) ?? 0));

            // Load notebook publishUrl for GitHub note links
            const nbDoc = await getDoc(doc(db, `users/${uid}/notebooks/${meta.id}`));
            const pubUrl = nbDoc.data()?.lastPublishUrl ?? null;
            setNotebookPublishUrl(pubUrl);
            notebookPublishUrlRef.current = pubUrl;

            setBranches(l1Nodes);
            branchesRef.current = l1Nodes;
            setAllNodes(notes);

            // Jumped here from the "Ý tưởng video" tab's "go to note" click — select it now that
            // this notebook's notes have actually loaded (see pendingSelectNodeIdRef's declaration).
            if (pendingSelectNodeIdRef.current) {
                const target = pendingSelectNodeIdRef.current;
                pendingSelectNodeIdRef.current = null;
                setSelectedNodeId(target);
                const targetNode = notes.find(n => n.id === target);
                if (targetNode?._branchId) setSelectedBranchIds(new Set([targetNode._branchId]));
            } else if (l1Nodes.length > 0) {
                // Auto-select first branch
                setSelectedBranchIds(prev => prev.size > 0 ? prev : new Set([l1Nodes[0].id]));
            }
        } finally {
            setLoadingNodes(false);
        }
    }, [db, auth]);

    useEffect(() => {
        setSelectedBranchIds(new Set());
        if (!pendingSelectNodeIdRef.current) setSelectedNodeId(null);
        setAllNodes([]);
        setBranches([]);
        loadNodes(activeNotebookMeta);
    }, [activeNotebookMeta]);

    // "Ý tưởng video" tab data — queries the flat users/{uid}/videoIdeas collection directly (one
    // doc per idea, already tagged with notebookId/nodeId/notebookTitle/noteTitle), so this is a
    // single query across every notebook at once instead of looping every notebook's nodes.
    const loadAllVideoIdeas = useCallback(async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        setLoadingIdeas(true);
        try {
            const snap = await getDocs(collection(db, `users/${uid}/videoIdeas`));
            const flat = snap.docs.map(d => ({ id: d.id, ...d.data() } as VideoIdea));
            setVideoIdeas(flat);
            setSelectedIdeaId(prev => (prev && flat.some(n => n.id === prev)) ? prev : (flat[0]?.id ?? null));
        } finally {
            setLoadingIdeas(false);
        }
    }, [db, auth]);

    // One-time import of notes already scored under the old single-angle system (see
    // migrateVideoIdeasFromLegacyScores — idempotent, safe to click more than once). A real
    // button instead of a dev-console snippet, so nobody but the author of this feature ever
    // needs to open devtools to use it.
    const handleMigrateLegacyIdeas = useCallback(async () => {
        setIsMigratingIdeas(true);
        setMigrateIdeasMsg('');
        try {
            const fn = httpsCallable(functions, 'migrateVideoIdeasFromLegacyScores');
            const res = await fn({}) as any;
            setMigrateIdeasMsg(vn
                ? `✓ Đã nhập ${res.data.migrated} ý tưởng cũ (bỏ qua ${res.data.skipped} note không có sẵn)`
                : `✓ Imported ${res.data.migrated} legacy ideas (skipped ${res.data.skipped} notes with none)`);
            await loadAllVideoIdeas();
        } catch (e: any) {
            setMigrateIdeasMsg(e?.message || (vn ? 'Nhập thất bại' : 'Import failed'));
        } finally {
            setIsMigratingIdeas(false);
        }
    }, [vn, loadAllVideoIdeas]);

    // Status is the one field the client may change on a VideoIdea (see firestore.rules — every
    // other field is server-owned). Goes through a callable rather than a direct Firestore write
    // so the rule can stay a flat deny instead of needing field-level write validation.
    const handleUpdateIdeaStatus = useCallback(async (ideaId: string, status: VideoIdeaStatus) => {
        setVideoIdeas(prev => prev.map(v => v.id === ideaId ? { ...v, status } : v)); // optimistic
        try {
            const fn = httpsCallable(functions, 'updateVideoIdeaStatus');
            await fn({ ideaId, status });
        } catch {
            loadAllVideoIdeas(); // revert the optimistic update by refetching the real state
        }
    }, [loadAllVideoIdeas]);

    // The Notes tab's detail panel shows a selected note's video ideas without requiring a visit
    // to the "Ý tưởng video" tab first (which is where videoIdeas state otherwise gets populated,
    // lazily, on its own). Fetches just this one note's ideas and merges them into the same
    // shared videoIdeas state, so switching to the Ideas tab later doesn't refetch what's already
    // known and a status change made from either tab stays in sync.
    useEffect(() => {
        if (!selectedNodeId) return;
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        let cancelled = false;
        (async () => {
            const q = query(collection(db, `users/${uid}/videoIdeas`), where('nodeId', '==', selectedNodeId));
            const snap = await getDocs(q);
            if (cancelled) return;
            const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() } as VideoIdea));
            setVideoIdeas(prev => [...prev.filter(v => v.nodeId !== selectedNodeId), ...fetched]);
        })();
        return () => { cancelled = true; };
    }, [selectedNodeId, db, auth]);

    useEffect(() => {
        if (viewMode === 'ideas' && !ideasLoadedOnce) {
            setIdeasLoadedOnce(true);
            loadAllVideoIdeas();
        }
    }, [viewMode, ideasLoadedOnce, loadAllVideoIdeas]);

    // Idea card click → jump to the Notes tab with that idea's source note selected. If it's in a
    // different notebook than the one currently active there, switching activeNotebookMeta
    // re-triggers loadNodes, which picks up pendingSelectNodeIdRef once loaded (above) and looks
    // up the branch from the freshly-loaded notes; if it's already the active notebook, look the
    // branch up from allNodes (already loaded) directly since no reload will fire.
    const handleGoToIdeaNote = useCallback((n: VideoIdea) => {
        const meta = allNotebooksMeta.find(m => m.id === n.notebookId);
        if (!meta) return;
        setViewMode('notes');
        if (activeNotebookMeta.id === meta.id) {
            setSelectedNodeId(n.nodeId);
            const targetNode = allNodes.find(x => x.id === n.nodeId);
            if (targetNode?._branchId) setSelectedBranchIds(new Set([targetNode._branchId]));
        } else {
            pendingSelectNodeIdRef.current = n.nodeId;
            setActiveNotebookMeta(meta);
        }
    }, [allNotebooksMeta, activeNotebookMeta, allNodes]);

    useEffect(() => {
        const unsub = subscribeYouTube(setYtConnected);
        return () => { unsub(); };
    }, []);

    // Load scoring criteria from Firestore on mount
    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        getDoc(doc(db, `users/${uid}`)).then(snap => {
            const criteria = snap.data()?.youtubeScoringCriteria;
            if (criteria) setScoringCriteria(criteria);
        }).catch(() => {});
    }, []);

    const handleSaveScoringCriteria = async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        setIsSavingCriteria(true);
        try {
            await updateDoc(doc(db, `users/${uid}`), { youtubeScoringCriteria: scoringCriteria });
        } catch { } finally { setIsSavingCriteria(false); }
    };

    const selectedNode = allNodes.find(n => n.id === selectedNodeId) ?? null;
    const selectedNodeVideos = selectedNode ? getVideosForNode(selectedNode) : [];
    const selectedVideo = selectedNodeVideos.find(v => v.youtubeVideoId === selectedVideoId) ?? null;

    // When the selected note changes: auto-open its only video, otherwise show the video-list / no-video state.
    useEffect(() => {
        if (!selectedNode) { setSelectedVideoId(null); return; }
        const videos = getVideosForNode(selectedNode);
        const wanted = pendingVideoIdRef.current;
        pendingVideoIdRef.current = null;
        setSelectedVideoId(wanted && videos.some(v => v.youtubeVideoId === wanted) ? wanted : (videos.length === 1 ? videos[0].youtubeVideoId : null));
    }, [selectedNodeId]);

    // Track this note's export jobs so a stuck/finished-but-unresolved one can be surfaced with a
    // resume/continue-upload banner, regardless of what other videos already exist for this note.
    useEffect(() => {
        setNoteExportJobs([]);
        setJobActionError('');
        resumeTrackUnsubRef.current?.();
        resumeTrackUnsubRef.current = null;
        setResumingJobId(null);
        if (!selectedNode) return;
        const uidNow = auth.currentUser?.uid;
        if (!uidNow) return;
        const noteKey = `${selectedNode._notebookId}:${selectedNode.id}`;
        const q = query(collection(db, `users/${uidNow}/videoExportJobs`), where('noteKey', '==', noteKey));
        const unsub = onSnapshot(q, snap => {
            setNoteExportJobs(snap.docs.map(d => ({ id: d.id, ...d.data() } as ExportJobDoc)));
        });
        return () => unsub();
    }, [selectedNodeId]);

    const attentionJob = findJobNeedingAttention(noteExportJobs);
    const runningJob = attentionJob ? null : findRunningJob(noteExportJobs);

    const handleDismissJob = async (job: ExportJobDoc) => {
        const uidNow = auth.currentUser?.uid;
        if (!uidNow) return;
        try { await updateDoc(doc(db, `users/${uidNow}/videoExportJobs/${job.id}`), { consumed: true }); } catch { /* best-effort */ }
    };

    const handleContinueUpload = async (job: ExportJobDoc) => {
        if (!job.storagePathFinal || !job.nodeId || !job.notebookId) return;
        setJobActionError('');
        try {
            const url = await getDownloadURL(storageRef(storage, job.storagePathFinal));
            const res = await fetch(url);
            const blob = await res.blob();
            const file = new File([blob], `studyboard-${Date.now()}.mp4`, { type: 'video/mp4' });
            onOpenUpload(job.nodeId, job.notebookId, file, job.id);
        } catch (e: any) {
            setJobActionError(e?.message || (vn ? 'Không tải được video đã xử lý' : 'Failed to fetch the processed video'));
        }
    };

    // Resets + re-triggers a stuck job server-side, then watches the SAME job doc through to
    // completion. Hand-off on completion uses the job document's own nodeId/notebookId — never
    // `selectedNode` — since the user may have navigated to a different note while this ran.
    const handleResumeJob = async (job: ExportJobDoc) => {
        const uidNow = auth.currentUser?.uid;
        if (!uidNow) return;
        setResumingJobId(job.id);
        setResumeProgress(null);
        setJobActionError('');
        try {
            const resume = httpsCallable(functions, 'resumeVideoExportJob');
            await resume({ jobId: job.id });
            const jobRef = doc(db, `users/${uidNow}/videoExportJobs/${job.id}`);
            resumeTrackUnsubRef.current = onSnapshot(jobRef, async (snap) => {
                const data = snap.data() as ExportJobDoc | undefined;
                if (!data) return;
                setResumeProgress({ status: data.status, pct: typeof (data as any).progress === 'number' ? (data as any).progress : undefined, phase: (data as any).phase });
                if (data.status === 'done') {
                    resumeTrackUnsubRef.current?.();
                    setResumingJobId(null);
                    if (!data.storagePathFinal || !data.nodeId || !data.notebookId) return;
                    try {
                        const url = await getDownloadURL(storageRef(storage, data.storagePathFinal));
                        const res = await fetch(url);
                        const blob = await res.blob();
                        const file = new File([blob], `studyboard-${Date.now()}.mp4`, { type: 'video/mp4' });
                        onOpenUpload(data.nodeId, data.notebookId, file, job.id);
                    } catch (e: any) {
                        setJobActionError(e?.message || (vn ? 'Không tải được video đã xử lý' : 'Failed to fetch the processed video'));
                    }
                } else if (data.status === 'error') {
                    resumeTrackUnsubRef.current?.();
                    setResumingJobId(null);
                    setJobActionError(data.error || (vn ? 'Xử lý lại thất bại' : 'Resume failed'));
                }
            });
        } catch (e: any) {
            setResumingJobId(null);
            setJobActionError(e?.message || (vn ? 'Không thể tiếp tục xử lý job này' : 'Could not resume this job'));
        }
    };

    // When the selected video changes: reset all per-video editor/UI state.
    useEffect(() => {
        if (!selectedVideo) return;
        setEditTitle(selectedVideo.youtubeTitle || selectedNode?.title || '');
        setEditDesc(selectedVideo.youtubeDescription || '');
        setScheduleDate('');
        setSaveMsg('');
        setThumbMsg('');
        setPlaylistMsg('');
        setShowCreatePlaylist(false);
        setNewPlaylistTitle('');
        // Both the transcript text and the AI Check verdict on it are persisted — restore them here
        // so they survive closing/reopening the note instead of silently disappearing and forcing
        // an unnecessary re-fetch (which costs YouTube API quota) or re-check.
        setTranscript(selectedVideo.youtubeTranscript || null);
        setTranscriptError('');
        setTranscriptCheck(selectedVideo.postCheckRecommendation != null ? {
            recommendation: selectedVideo.postCheckRecommendation,
            strengths: selectedVideo.postCheckStrengths || [],
            findings: selectedVideo.postCheckFindings || [],
            checkSummary: selectedVideo.postCheckSummary || '',
            score: selectedVideo.postCheckScore ?? 0,
            passes: selectedVideo.postCheckPasses || undefined,
        } : null);
        setShowTranscript(false);
        setShowDiscussPanel(false);
        setDiscussMessages([]);
        setDiscussInput('');
        setClipFocusInput(selectedVideo.videoClipFocus || '');
        // Reset to empty — the thumbnail card falls back to showing the video's actual current
        // YouTube thumbnail (same CDN URL the list already uses) until the user opens the editor
        // and generates a new one to push. Previously this auto-generated a blank placeholder here
        // (no screenshots passed in), which rendered as an empty canvas.
        setThumbDataUrl('');
        // Tracks whether Title/Description/Hashtag were generated at least once this session, purely
        // for the checklist's checkmarks — Check's own "done" state is transcriptCheck itself, which
        // is already restored above from persisted data.
        setGenTitleDone(false);
        setGenDescDone(false);
        setGenHashtagDone(false);
    }, [selectedVideoId]);

    // Videos linked earlier (e.g. via the orphan scanner) may carry only an ID — with an empty
    // description and a fallback title, saving would overwrite the real ones on YouTube. Pull the
    // real title/description once, persist them, and show them in the editor.
    const hydratedVideoIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        if (!selectedNode || !selectedVideo || !ytConnected) return;
        if (selectedVideo.youtubeTitle || selectedVideo.youtubeDescription) return;
        const vid = selectedVideo.youtubeVideoId;
        if (hydratedVideoIdsRef.current.has(vid)) return;
        hydratedVideoIdsRef.current.add(vid);
        const targetNode = selectedNode;
        fetchYouTubeVideoSnippet(vid).then(async sn => {
            if (!sn) { hydratedVideoIdsRef.current.delete(vid); return; }
            const uid = auth.currentUser?.uid;
            if (!uid) return;
            try {
                const patch = buildVideoUpdatePatch(targetNode, vid, { youtubeTitle: sn.title, youtubeDescription: sn.description, videoTitle: sn.title, videoUrl: `https://youtu.be/${vid}` });
                await updateDoc(doc(db, `users/${uid}/notebooks/${targetNode._notebookId}/nodes/${targetNode.id}`), patch);
                setAllNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, ...patch } : n));
                setEditTitle(sn.title);
                setEditDesc(sn.description);
            } catch { /* best-effort */ }
        });
    }, [selectedVideoId, ytConnected]);

    // Per-video choice of whether its link appears on the note's GitHub page (applied on the next sync).
    const [githubToggleMsg, setGithubToggleMsg] = useState('');
    useEffect(() => { setGithubToggleMsg(''); }, [selectedVideoId]);
    const handleToggleGithub = async (checked: boolean) => {
        if (!selectedNode || !selectedVideo) return;
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        setGithubToggleMsg('');
        try {
            const patch = buildVideoUpdatePatch(selectedNode, selectedVideo.youtubeVideoId, { showOnGithub: checked });
            await updateDoc(doc(db, `users/${uid}/notebooks/${selectedNode._notebookId}/nodes/${selectedNode.id}`), patch);
            setAllNodes(prev => prev.map(n => n.id === selectedNode.id ? { ...n, ...patch } : n));
            setGithubToggleMsg(vn ? '✓ Sẽ áp dụng ở lần sync GitHub kế tiếp' : '✓ Applies on the next GitHub sync');
        } catch (e: any) {
            setGithubToggleMsg(describeSaveError(e, vn ? 'Lỗi khi lưu' : 'Save failed', false));
        }
    };

    // "Is this video made for kids?" — YouTube requires an answer before a video can go public, so it
    // is shown and editable here instead of only in YouTube Studio. 'unset' = never declared.
    const [videoAudience, setVideoAudience] = useState<'kids' | 'notkids' | 'unset' | null>(null);
    const [audienceSaving, setAudienceSaving] = useState(false);
    const [audienceMsg, setAudienceMsg] = useState('');
    // Looked up once per video per session — re-selecting a video must not ask YouTube again.
    const audienceCacheRef = useRef<Record<string, 'kids' | 'notkids' | 'unset'>>({});
    useEffect(() => {
        setAudienceMsg('');
        if (!selectedVideo || !ytConnected) { setVideoAudience(null); return; }
        const vid = selectedVideo.youtubeVideoId;
        if (audienceCacheRef.current[vid]) { setVideoAudience(audienceCacheRef.current[vid]); return; }
        setVideoAudience(null);
        let cancelled = false;
        fetchYouTubeAudience(vid).then(a => {
            if (a) audienceCacheRef.current[vid] = a;
            if (!cancelled) setVideoAudience(a);
        });
        return () => { cancelled = true; };
    }, [selectedVideoId, ytConnected]);
    const handleSetAudience = async (kids: boolean) => {
        if (!selectedVideo || !ytConnected) return;
        setAudienceSaving(true); setAudienceMsg('');
        try {
            await updateYouTubeVideo(selectedVideo.youtubeVideoId, { madeForKids: kids });
            setVideoAudience(kids ? 'kids' : 'notkids');
            audienceCacheRef.current[selectedVideo.youtubeVideoId] = kids ? 'kids' : 'notkids';
            setAudienceMsg(vn ? '✓ Đã khai báo' : '✓ Declared');
        } catch (e: any) {
            setAudienceMsg(describeSaveError(e, vn ? 'Lỗi khi khai báo' : 'Declaration failed', false));
        } finally { setAudienceSaving(false); }
    };

    // Which branch(es) drive column 2. Ticking a branch checkbox always wins over a plain click:
    // otherwise a branch you merely clicked on earlier (still in selectedBranchIds) would keep
    // showing its notes mixed in — unchecked — alongside a branch you then ticked, which is
    // exactly the "why are some checked and some not" confusion. Once every checkbox is
    // cleared, view falls back to the plain click-driven selection.
    const viewBranchIds = checkedBranchIds.size > 0 ? checkedBranchIds : selectedBranchIds;

    // Notes for the currently viewed branch(es)
    const branchNotes = allNodes.filter(n => {
        if (!n._branchId || !viewBranchIds.has(n._branchId)) return false;
        if (statusFilter === 'all') return true;
        return getNoteVideoStatus(n) === statusFilter;
    });

    // Sorted view — unscored notes always sink to the bottom regardless of direction
    const sortedBranchNotes = (() => {
        if (sortMode === 'default') return branchNotes;
        const getVal = (n: EnhancedNode) => sortMode.startsWith('quality') ? n.videoWorthinessScore : n.analysis?.score;
        const dir = sortMode.endsWith('desc') ? -1 : 1;
        return [...branchNotes].sort((a, b) => {
            const va = getVal(a), vb = getVal(b);
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            return (va - vb) * dir;
        });
    })();

    // Stats per branch
    const branchStats = useCallback((branchId: string) => {
        const notes = allNodes.filter(n => n._branchId === branchId);
        const scored = notes.filter(n => n.videoAnalysisCheckedAt != null).length;
        return {
            total: notes.length,
            withVideo: notes.filter(n => getVideosForNode(n).length > 0).length,
            scored,
            scoredPct: notes.length > 0 ? Math.round((scored / notes.length) * 100) : 0,
        };
    }, [allNodes]);

    // Global counts for filter tabs
    const branchAllNotes = allNodes.filter(n => n._branchId && viewBranchIds.has(n._branchId));
    const counts = {
        all: branchAllNotes.length,
        none: branchAllNotes.filter(n => getNoteVideoStatus(n) === 'none').length,
        private: branchAllNotes.filter(n => getNoteVideoStatus(n) === 'private').length,
        scheduled: branchAllNotes.filter(n => getNoteVideoStatus(n) === 'scheduled').length,
        public: branchAllNotes.filter(n => getNoteVideoStatus(n) === 'public').length,
        mixed: branchAllNotes.filter(n => getNoteVideoStatus(n) === 'mixed').length,
    };

    const globalCounts = {
        total: allNodes.length,
        published: allNodes.filter(n => getNoteVideoStatus(n) === 'public').length,
    };

    // Flat cross-note video list — every video attached to any note in the active notebook, each
    // paired with its owning node so actions can build the correct Firestore patch/path.
    const flatVideos = allNodes.flatMap(n => getVideosForNode(n).map(v => ({ video: v, node: n })));
    // "Needs check" covers both a video never checked at all AND one checked before the 3-angle
    // rework (has an old-format postCheckRecommendation but no postCheckPasses breakdown) — both
    // should be picked up by batch check, so re-running it also upgrades stale results instead of
    // requiring the user to manually re-check every already-checked video one by one.
    const needsCheck = (video: YouTubeVideoEntry) =>
        (video.youtubeStatus || 'private') !== 'public' && (!video.postCheckPasses || video.postCheckPasses.length === 0);
    const videoTabCounts = {
        all: flatVideos.length,
        private: flatVideos.filter(x => (x.video.youtubeStatus || 'private') === 'private').length,
        scheduled: flatVideos.filter(x => x.video.youtubeStatus === 'scheduled').length,
        public: flatVideos.filter(x => x.video.youtubeStatus === 'public').length,
        unchecked: flatVideos.filter(x => needsCheck(x.video)).length,
    };
    const filteredFlatVideosUnsorted = flatVideos.filter(x => {
        if (videoTabFilter === 'unchecked') return needsCheck(x.video);
        if (videoTabFilter === 'all') return true;
        return (x.video.youtubeStatus || 'private') === videoTabFilter;
    });
    // Time = when the video was uploaded to YouTube (youtubeUploadedAt), not when its title/
    // description were last edited — "the video I created yesterday" means upload time. Videos
    // with no timestamp (older data from before this field existed) sink to the bottom regardless
    // of direction instead of floating to the top of "newest first".
    const filteredFlatVideos = videoSortMode === 'default' ? filteredFlatVideosUnsorted : [...filteredFlatVideosUnsorted].sort((a, b) => {
        const ta = a.video.youtubeUploadedAt, tb = b.video.youtubeUploadedAt;
        if (ta == null && tb == null) return 0;
        if (ta == null) return 1;
        if (tb == null) return -1;
        return videoSortMode === 'time-desc' ? tb - ta : ta - tb;
    });

    const parseVideoId = (input: string): string | null => {
        const s = input.trim();
        // Plain video ID (11 chars, alphanumeric + - _)
        if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
        try {
            const url = new URL(s);
            // youtu.be/<id>
            if (url.hostname === 'youtu.be') return url.pathname.slice(1).split('?')[0] || null;
            // youtube.com/watch?v=<id>  or  /shorts/<id>  or  /embed/<id>
            return url.searchParams.get('v') || url.pathname.split('/').pop() || null;
        } catch { return null; }
    };

    const handleLinkVideo = async () => {
        if (!selectedNode) return;
        const videoId = parseVideoId(linkInputValue);
        if (!videoId) { setLinkError(vn ? 'URL hoặc ID không hợp lệ' : 'Invalid URL or video ID'); return; }
        setIsLinking(true); setLinkError('');
        try {
            const uid = auth.currentUser?.uid;
            if (!uid) throw new Error('not logged in');
            const nodeRef = doc(db, `users/${uid}/notebooks/${selectedNode._notebookId}/nodes/${selectedNode.id}`);
            const patch = buildVideoAppendPatch(selectedNode, { youtubeVideoId: videoId, youtubeStatus: 'private', youtubeUploadedAt: Date.now() });
            await updateDoc(nodeRef, patch);
            setAllNodes(prev => prev.map(n => n.id === selectedNode.id ? { ...n, ...patch } : n));
            setShowLinkInput(false);
            setLinkInputValue('');
        } catch (e: any) {
            setLinkError(e.message || 'Error');
        } finally {
            setIsLinking(false);
        }
    };

    const handleScanOrphans = async () => {
        setIsScanningOrphans(true);
        setOrphanMatches([]);
        setOrphanScanDone(false);
        try {
            const videos = await fetchMyYouTubeVideos(50);
            const uid = auth.currentUser?.uid;
            if (!uid) return;

            // Build a map of nodeId → { title, notebookId } across all loaded nodes
            const nodeMap = new Map<string, { title: string; notebookId: string }>();
            allNodes.forEach(n => {
                nodeMap.set(n.id, { title: n.title || '(no title)', notebookId: n._notebookId });
            });

            const matches: typeof orphanMatches = [];
            for (const video of videos) {
                // Parse description for #node-{id} pattern
                const match = video.description.match(/#node-([a-zA-Z0-9_-]+)/);
                if (!match) continue;
                const nodeId = match[1];
                const entry = nodeMap.get(nodeId);
                if (!entry) continue;
                // Skip if this video is already linked to some note (in slot 0 or the youtubeVideos array)
                const alreadyLinked = allNodes.some(n => getVideosForNode(n).some(v => v.youtubeVideoId === video.id));
                if (alreadyLinked) continue;
                matches.push({ video, nodeId, nodeTitle: entry.title, notebookId: entry.notebookId });
            }
            setOrphanMatches(matches);
            setOrphanScanDone(true);
        } catch { setOrphanScanDone(true); } finally { setIsScanningOrphans(false); }
    };

    const handleConfirmOrphanLink = async (match: typeof orphanMatches[0]) => {
        setLinkingOrphan(match.video.id);
        try {
            const uid = auth.currentUser?.uid;
            if (!uid) return;
            const targetNode = allNodes.find(n => n.id === match.nodeId);
            if (!targetNode) return;
            const nodeRef = doc(db, `users/${uid}/notebooks/${match.notebookId}/nodes/${match.nodeId}`);
            const patch = buildVideoAppendPatch(targetNode, { youtubeVideoId: match.video.id, youtubeStatus: match.video.privacyStatus === 'public' ? 'public' : 'private', youtubeUploadedAt: Date.now(), youtubeTitle: match.video.title, youtubeDescription: match.video.description, videoUrl: `https://youtu.be/${match.video.id}`, videoTitle: match.video.title });
            await updateDoc(nodeRef, patch);
            setAllNodes(prev => prev.map(n => n.id === match.nodeId ? { ...n, ...patch } : n));
            setOrphanMatches(prev => prev.filter(m => m.video.id !== match.video.id));
        } catch { } finally { setLinkingOrphan(null); }
    };

    // Shows the real reason a save/publish failed instead of a generic label. YouTube rejects < and >
    // in titles/descriptions, which is easy to hit with math text ("epsilon < 0"), so hint at it.
    const describeSaveError = (e: any, label: string, hasAngleBrackets: boolean) => {
        if (e?.message === 'not_connected') return vn ? 'YouTube chưa kết nối hoặc phiên đã hết hạn — bấm "Kết nối YouTube" ở góc trên phải' : 'YouTube not connected or session expired — click "Connect YouTube" at the top right';
        // This list is loaded once when the manager opens — a note deleted in the meantime still
        // shows here, and any save against it fails with Firestore's raw "No document to update".
        if (e?.code === 'not-found' || /No document to update/i.test(e?.message || '')) {
            return vn ? 'Note gốc của video này đã bị xóa — đóng rồi mở lại YouTube Manager để cập nhật danh sách' : "This video's note was deleted — close and reopen YouTube Manager to refresh the list";
        }
        const detail = e?.message || String(e);
        const hint = hasAngleBrackets && /invalid|description|title/i.test(detail)
            ? (vn ? ' — Gợi ý: YouTube không cho phép ký tự < hoặc > trong tiêu đề/mô tả' : ' — Hint: YouTube does not allow < or > in the title/description')
            : '';
        return `${label}: ${detail}${hint}`;
    };

    const playlistListRef = useRef<HTMLDivElement>(null);
    const playlistErrText = (e: any) => e?.message === 'not_connected'
        ? (vn ? 'YouTube chưa kết nối hoặc phiên đã hết hạn — bấm "Kết nối YouTube" ở góc trên phải' : 'YouTube not connected or session expired — click "Connect YouTube" at the top right')
        : (e?.message || String(e));
    // AI playlist suggestion — judged from the transcript when the video has one cached, otherwise
    // from the note. Kept per video and cleared when another video is selected.
    const [plSuggest, setPlSuggest] = useState<{ videoId: string; suggestions: PlaylistSuggestion[]; missingTopic: string; source: 'transcript' | 'note' } | null>(null);
    const [isSuggestingPl, setIsSuggestingPl] = useState(false);
    const handleSuggestPlaylists = async () => {
        if (!selectedVideo || !selectedNode || isSuggestingPl || playlists.length === 0) return;
        setIsSuggestingPl(true); setPlaylistMsg('');
        try {
            const transcript = selectedVideo.youtubeTranscript || '';
            const r = await suggestVideoPlaylists({
                playlists: playlists.map(p => ({ id: p.id, title: p.title })),
                videoTitle: selectedVideo.youtubeTitle || '', noteTitle: selectedNode.title || '',
                notebookTitle: selectedNode._notebookTitle || '', noteContent: selectedNode.content || '', transcript,
            }, language);
            setPlSuggest({ videoId: selectedVideo.youtubeVideoId, ...r, source: transcript ? 'transcript' : 'note' });
        } catch (e: any) {
            setPlaylistMsg(`${vn ? 'Không gợi ý được' : 'Could not suggest'}: ${e?.message || e}`);
        } finally { setIsSuggestingPl(false); }
    };
    const handleAddToPlaylist = async (pl: YouTubePlaylist) => {
        if (!selectedVideo) return;
        setAddingToPlaylistId(pl.id);
        setPlaylistMsg('');
        try {
            await addVideoToPlaylist(pl.id, selectedVideo.youtubeVideoId);
            setPlaylistMsg(`✓ ${vn ? 'Đã thêm vào' : 'Added to'} "${pl.title}"`);
            setPlaylists(prev => prev.map(p => p.id === pl.id ? { ...p, itemCount: p.itemCount + 1 } : p));
            addToPlaylistCache(selectedVideo.youtubeVideoId, pl.title);
        } catch (e: any) {
            setPlaylistMsg(`${vn ? 'Thêm thất bại' : 'Failed to add'}: ${playlistErrText(e)}`);
        } finally { setAddingToPlaylistId(null); }
    };
    const handleCreatePlaylist = async () => {
        const title = newPlaylistTitle.trim();
        if (!title || !selectedVideo) return;
        setIsCreatingPlaylist(true);
        setPlaylistMsg('');
        let pl: YouTubePlaylist;
        try {
            pl = await createYouTubePlaylist(title);
        } catch (e: any) {
            setPlaylistMsg(`${vn ? 'Tạo thất bại' : 'Failed to create'}: ${playlistErrText(e)}`);
            setIsCreatingPlaylist(false);
            return;
        }
        setPlaylists(prev => [pl, ...prev]);
        addPlaylistToCache(pl);
        setNewPlaylistTitle('');
        // The list is usually scrolled somewhere else — bring the new playlist (at the top) into view.
        setTimeout(() => playlistListRef.current?.scrollTo({ top: 0 }), 0);
        try {
            await addVideoToPlaylist(pl.id, selectedVideo.youtubeVideoId);
            setPlaylistMsg(`✓ ${vn ? 'Tạo và thêm vào' : 'Created and added to'} "${pl.title}"`);
            setPlaylists(prev => prev.map(p => p.id === pl.id ? { ...p, itemCount: 1 } : p));
            addToPlaylistCache(selectedVideo.youtubeVideoId, pl.title);
        } catch (e: any) {
            setPlaylistMsg(`${vn ? 'Đã tạo playlist' : 'Playlist created'} "${pl.title}" — ${vn ? 'nhưng thêm video thất bại' : 'but adding the video failed'}: ${playlistErrText(e)}`);
        } finally { setIsCreatingPlaylist(false); }
    };

    // Delete a video everywhere: permanently off the YouTube channel, and out of this note (which also
    // removes its GitHub-page link on the next sync). The Storage backup copy is left alone — storage
    // rules only let the client write raw.webm, and it is the one file that can't be re-created.
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeletingVideo, setIsDeletingVideo] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const handleDeleteVideo = async () => {
        if (!selectedNode || !selectedVideo) return;
        const vid = selectedVideo.youtubeVideoId;
        const targetNode = selectedNode;
        setIsDeletingVideo(true); setDeleteError('');
        try {
            await deleteYouTubeVideo(vid);
            const uid = auth.currentUser?.uid;
            if (!uid) throw new Error('not signed in');
            const slot0Fields = ['youtubeVideoId', 'youtubeStatus', 'youtubeTitle', 'youtubeDescription', 'youtubeUploadedAt', 'youtubeScheduledAt',
                'videoUrl', 'videoTitle', 'videoDuration', 'videoSegments', 'videoClipFocus', 'videoStoragePath',
                'postCheckScore', 'postCheckRecommendation', 'postCheckStrengths', 'postCheckPasses', 'postCheckFindings', 'postCheckSummary', 'postCheckCheckedAt',
                'youtubeTranscript', 'youtubeTimedTranscript', 'showOnGithub'];
            let fsPatch: Record<string, unknown> = {};
            let localPatch: Record<string, unknown> = {};
            if (targetNode.youtubeVideoId === vid) {
                slot0Fields.forEach(f => { fsPatch[f] = deleteField(); localPatch[f] = undefined; });
            } else {
                const rest = (targetNode.youtubeVideos || []).filter(v => v.youtubeVideoId !== vid);
                fsPatch = { youtubeVideos: rest };
                localPatch = { youtubeVideos: rest };
            }
            await updateDoc(doc(db, `users/${uid}/notebooks/${targetNode._notebookId}/nodes/${targetNode.id}`), fsPatch);
            setAllNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, ...localPatch } : n));
            setSelectedVideoId(null);
            setShowDeleteConfirm(false);
            // The server takes the video out of the book's GitHub page/README, of "related video"
            // links on other notes and of link lists in other videos' descriptions.
            httpsCallable(functions, 'videoDeletedCleanup')({ videoId: vid, notebookId: targetNode._notebookId })
                .catch(e => console.warn('[delete] cleanup request failed — the twice-daily sweep will catch it', e));
        } catch (e: any) {
            setDeleteError(describeSaveError(e, vn ? 'Xóa thất bại' : 'Delete failed', false));
        } finally { setIsDeletingVideo(false); }
    };

    // ── Assistant chat ──────────────────────────────────────────────────────────
    // Snapshot handed to the assistant: what the Manager already holds, no extra YouTube calls.
    const [showChat, setShowChat] = useState(false);
    const buildChatSnapshot = (): ChatVideoSnapshot[] => flatVideos.map(({ video, node }) => {
        const day = (ms?: number) => ms ? new Date(ms).toISOString().slice(0, 10) : null;
        const desc = video.youtubeDescription || '';
        return {
            id: video.youtubeVideoId,
            note: node.title || '',
            noteExcerpt: (node.content || '').replace(/\s+/g, ' ').slice(0, 250),
            title: video.youtubeTitle || video.videoTitle || null,
            descriptionLength: desc.length,
            descriptionStart: desc.slice(0, 300),
            descriptionEnd: desc.length > 300 ? desc.slice(-300) : '',
            descriptionHasAiCheckBlock: desc.includes('AI Check:'),
            descriptionHasNotesLink: /github\.com\/.+#node-/i.test(desc),
            status: video.youtubeStatus || 'private',
            uploadedAt: day(video.youtubeUploadedAt),
            aiCheck: video.postCheckRecommendation != null ? {
                score: video.postCheckScore ?? null,
                recommendation: video.postCheckRecommendation,
                checkedAt: day(video.postCheckCheckedAt),
                threeAngles: !!video.postCheckPasses && video.postCheckPasses.length > 0,
            } : null,
            hasTranscript: !!video.youtubeTranscript,
            onGithub: isVideoOnGithub(node, video),
            editedInStudyBoardAt: day(video.youtubeEditedAt),
            playlists: playlistCache ? (playlistCache.map[video.youtubeVideoId] ?? []) : null,
        };
    });
    // Sequential applies must each see the previous patch (an array-entry patch rewrites the whole
    // youtubeVideos array), so keep a ref that is updated synchronously after every write.
    // Selects one video (column-1 row click, or a chat chip): pick its note, then that video. Selecting
    // a note resets the selected video (a note with 2+ videos would otherwise land on its note-level
    // list), so the wanted video id is parked in a ref that the note-change effect consumes.
    const pendingVideoIdRef = useRef<string | null>(null);
    const openVideoFromChat = (videoId: string) => {
        const entry = flatVideos.find(x => x.video.youtubeVideoId === videoId);
        if (!entry) return;
        setViewMode('videos');
        if (selectedNodeId === entry.node.id) { setSelectedVideoId(videoId); return; }
        pendingVideoIdRef.current = videoId;
        setSelectedNodeId(entry.node.id);
    };
    const chatNodesRef = useRef<EnhancedNode[]>([]);
    chatNodesRef.current = allNodes;
    const applyChatChange = async (c: ManagerChatChange) => {
        const uid = auth.currentUser?.uid;
        if (!uid) throw new Error('not signed in');
        const found = chatNodesRef.current.flatMap(n => getVideosForNode(n).map(v => ({ n, v }))).find(x => x.v.youtubeVideoId === c.videoId);
        if (!found) throw new Error(vn ? 'Không tìm thấy video' : 'Video not found');
        const { n, v } = found;
        const lang = vn ? 'vietnamese' : 'english';
        let fields: Partial<YouTubeVideoEntry>;
        if (c.field === 'title') {
            const t = sanitizeForYouTube(c.newValue.trim(), lang).slice(0, 100);
            if (!t) throw new Error('empty title');
            await updateYouTubeVideo(c.videoId, { title: t });
            fields = { youtubeTitle: t, youtubeEditedAt: Date.now() };
            if (selectedVideoId === c.videoId) setEditTitle(t);
        } else if (c.field === 'description') {
            const d = sanitizeForYouTube(c.newValue.trim(), lang).slice(0, 5000);
            if (!d) throw new Error('empty description');
            await updateYouTubeVideo(c.videoId, { description: d });
            fields = { youtubeDescription: d, youtubeEditedAt: Date.now() };
            if (selectedVideoId === c.videoId) setEditDesc(d);
        } else if (c.field === 'showOnGithub') {
            fields = { showOnGithub: c.newValue === 'true' };
        } else {
            throw new Error('unsupported change');
        }
        const patch = buildVideoUpdatePatch(n, v.youtubeVideoId, fields);
        await updateDoc(doc(db, `users/${uid}/notebooks/${n._notebookId}/nodes/${n.id}`), patch);
        chatNodesRef.current = chatNodesRef.current.map(x => x.id === n.id ? { ...x, ...patch } : x);
        setAllNodes(prev => prev.map(x => x.id === n.id ? { ...x, ...patch } : x));
    };

    // ── Playlist names per video (column 1) ────────────────────────────────────
    // One sync reads every playlist of the channel and is cached on this device; the list shows it
    // without a request per video. Kept current locally when a video is added to a playlist here.
    const PLAYLIST_CACHE_KEY = 'sb_yt_playlist_membership_v1';
    const [playlistCache, setPlaylistCache] = useState<{ syncedAt: number; map: Record<string, string[]>; playlists?: YouTubePlaylist[] } | null>(() => {
        try { return JSON.parse(localStorage.getItem(PLAYLIST_CACHE_KEY) || 'null'); } catch { return null; }
    });
    const [isSyncingPlaylists, setIsSyncingPlaylists] = useState(false);
    const [playlistSyncError, setPlaylistSyncError] = useState('');
    const savePlaylistCache = (c: { syncedAt: number; map: Record<string, string[]>; playlists?: YouTubePlaylist[] }) => {
        setPlaylistCache(c);
        try { localStorage.setItem(PLAYLIST_CACHE_KEY, JSON.stringify(c)); } catch { /* ignore */ }
    };
    const handleSyncPlaylists = async () => {
        setIsSyncingPlaylists(true); setPlaylistSyncError('');
        try {
            const { map, playlists: synced } = await fetchPlaylistMembershipMap();
            savePlaylistCache({ syncedAt: Date.now(), map, playlists: synced });
            setPlaylists(synced);
            setPlaylistsLoaded(true);
        } catch (e: any) {
            setPlaylistSyncError(e?.message === 'not_connected'
                ? (vn ? 'YouTube chưa kết nối hoặc hết phiên' : 'YouTube not connected or session expired')
                : (e?.message || String(e)));
        } finally { setIsSyncingPlaylists(false); }
    };
    const addToPlaylistCache = (videoId: string, playlistTitle: string) => {
        if (!playlistCache) return;
        const cur = playlistCache.map[videoId] || [];
        if (cur.includes(playlistTitle)) return;
        savePlaylistCache({ ...playlistCache, map: { ...playlistCache.map, [videoId]: [...cur, playlistTitle] } });
    };
    const addPlaylistToCache = (pl: YouTubePlaylist) => {
        if (!playlistCache) return;
        savePlaylistCache({ ...playlistCache, playlists: [pl, ...(playlistCache.playlists || [])] });
    };
    const formatShortTime = (ms: number) => new Date(ms).toLocaleString(vn ? 'vi-VN' : undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    // Which playlists this video is already in, read from the playlist-sync cache (one pass over the
    // channel serves every video). Asking YouTube per video cost ~1 quota unit per playlist each time.
    const memberIds: string[] | undefined = selectedVideo && playlistCache
        ? playlists.filter(p => (playlistCache.map[selectedVideo.youtubeVideoId] || []).includes(p.title)).map(p => p.id)
        : undefined;


    // Hover-eye floating description preview for column-1 rows. Hiding is debounced (not instant on
    // mouseleave) so the cursor can travel from the eye icon into the popup itself to scroll a long
    // description — it only actually closes once the pointer has left BOTH the icon and the popup
    // for a beat, whichever of the two mouseenter handlers cancels the pending hide.
    const [descPreview, setDescPreview] = useState<{ videoId: string; top: number; left: number } | null>(null);
    const descPreviewHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cancelHideDescPreview = () => {
        if (descPreviewHideTimer.current) { clearTimeout(descPreviewHideTimer.current); descPreviewHideTimer.current = null; }
    };
    const scheduleHideDescPreview = () => {
        cancelHideDescPreview();
        descPreviewHideTimer.current = setTimeout(() => setDescPreview(null), 200);
    };
    useEffect(() => () => cancelHideDescPreview(), []);

    // ── Thumbnail drafts + copy/paste style ─────────────────────────────────────────────────────
    const buildNoteImages = (n: MindMapNode): { src: string; label: string }[] => {
        const out: { src: string; label: string }[] = [];
        if (n.imageBase64) {
            const raw = n.imageBase64;
            out.push({ src: raw.startsWith('http') || raw.startsWith('data:') ? raw : `data:${n.imageMimeType || 'image/jpeg'};base64,${raw}`, label: vn ? 'Ảnh chính' : 'Main' });
        }
        // Hand-drawn sketches (the drawing feature, ScreenDrawingOverlay/SketchPad) weren't selectable
        // here before — only screenshots/attached images were. They live in two places depending on
        // when they were made: the legacy single drawingBase64 field, or newer attachments['drawing'].
        if (n.drawingBase64) {
            out.push({ src: n.drawingBase64, label: vn ? 'Bản vẽ' : 'Drawing' });
        }
        (n.attachments || []).filter(a => a.type === 'image').forEach((a, i) => {
            out.push({ src: a.content.startsWith('data:') || a.content.startsWith('http') ? a.content : `data:${a.mimeType || 'image/jpeg'};base64,${a.content}`, label: `${vn ? 'Ảnh' : 'Image'} ${i + 2}` });
        });
        (n.attachments || []).filter(a => a.type === 'drawing').forEach((a, i) => {
            out.push({ src: a.content.startsWith('data:') || a.content.startsWith('http') ? a.content : `data:${a.mimeType || 'image/png'};base64,${a.content}`, label: `${vn ? 'Bản vẽ' : 'Drawing'} ${i + 2}` });
        });
        return out;
    };

    // Pictures for the Thumbnail Studio: screenshots/images and hand drawings, labelled stably so a
    // video's saved choice of picture still points at the same one next time.
    const studioImagesOf = (n: MindMapNode): StudioImage[] => {
        const out: StudioImage[] = [];
        const src = (c: string, mime?: string) => c.startsWith('http') || c.startsWith('data:') ? c : `data:${mime || 'image/png'};base64,${c}`;
        if (n.imageBase64) out.push({ src: src(n.imageBase64, n.imageMimeType), label: 'main', kind: 'shot' });
        (n.attachments || []).filter(a => a.type === 'image').forEach(a => out.push({ src: src(a.content, a.mimeType), label: a.id, kind: 'shot' }));
        if (n.drawingBase64) out.push({ src: src(n.drawingBase64), label: 'drawing', kind: 'drawing' });
        (n.attachments || []).filter(a => a.type === 'drawing').forEach(a => out.push({ src: src(a.content, a.mimeType), label: a.id, kind: 'drawing' }));
        return out;
    };

    // Writes fields onto one video, always from the latest local copy of its note — several videos of
    // the same note are patched back to back, and an array-entry patch rewrites the whole array.
    const patchVideoById = async (videoId: string, makeFields: (v: YouTubeVideoEntry) => Partial<YouTubeVideoEntry>) => {
        const uid = auth.currentUser?.uid;
        if (!uid) throw new Error('not signed in');
        const found = chatNodesRef.current.flatMap(n => getVideosForNode(n).map(v => ({ n, v }))).find(x => x.v.youtubeVideoId === videoId);
        if (!found) throw new Error(vn ? 'Không tìm thấy video' : 'Video not found');
        const patch = buildVideoUpdatePatch(found.n, videoId, makeFields(found.v));
        await updateDoc(doc(db, `users/${uid}/notebooks/${found.n._notebookId}/nodes/${found.n.id}`), patch);
        chatNodesRef.current = chatNodesRef.current.map(x => x.id === found.n.id ? { ...x, ...patch } : x);
        setAllNodes(prev => prev.map(x => x.id === found.n.id ? { ...x, ...patch } : x));
    };
    const MAX_THUMB_DRAFTS = 6;
    const saveThumbDraft = async (draft: ThumbDraft) => {
        if (!selectedVideo) return;
        await patchVideoById(selectedVideo.youtubeVideoId, v => {
            const list = (v.thumbnailDrafts || []) as unknown as ThumbDraft[];
            const next = list.some(d => d.id === draft.id) ? list.map(d => d.id === draft.id ? draft : d) : [...list, draft];
            return { thumbnailDrafts: next.slice(-MAX_THUMB_DRAFTS) as unknown as YouTubeVideoEntry['thumbnailDrafts'] };
        });
    };
    const deleteThumbDraft = async (id: string) => {
        if (!selectedVideo) return;
        await patchVideoById(selectedVideo.youtubeVideoId, v => ({ thumbnailDrafts: ((v.thumbnailDrafts || []) as unknown as ThumbDraft[]).filter(d => d.id !== id) as unknown as YouTubeVideoEntry['thumbnailDrafts'] }));
    };

    // "Copy style" carries look only (colors, fonts, sizes, layout…) — never a video's own title/images.
    const STYLE_CLIP_KEY = 'sb_thumb_style_clipboard';
    const [copiedStyle, setCopiedStyle] = useState<{ style: ThumbStyleOnly; fromTitle: string; fromVideoId?: string } | null>(() => {
        try { return JSON.parse(localStorage.getItem(STYLE_CLIP_KEY) || 'null'); } catch { return null; }
    });
    const [rowMenu, setRowMenu] = useState<{ x: number; y: number; videoId: string } | null>(null);
    const styleSourceOf = (v: YouTubeVideoEntry): ThumbSettings | null => {
        const live = v.thumbnailSettings as Partial<ThumbSettings> | undefined;
        if (live && live.v === 1) return live as ThumbSettings;
        const drafts = (v.thumbnailDrafts || []) as unknown as ThumbDraft[];
        return drafts.length ? drafts[drafts.length - 1].settings : null;
    };
    const copyStyleFrom = (video: YouTubeVideoEntry) => {
        const src = styleSourceOf(video);
        if (!src) return;
        const c = { style: extractThumbStyle(src), fromTitle: video.youtubeTitle || video.videoTitle || video.youtubeVideoId, fromVideoId: video.youtubeVideoId };
        setCopiedStyle(c);
        try { localStorage.setItem(STYLE_CLIP_KEY, JSON.stringify(c)); } catch { /* ignore */ }
    };

    type StyleItem = { key: string; videoId: string; title: string; preview: string; settings: ThumbSettings | null; status: 'draft' | 'pushing' | 'pushed' | 'error'; error?: string; selected: boolean };
    const [styleReview, setStyleReview] = useState<{ phase: 'generating' | 'review' | 'pushing'; done: number; total: number; items: StyleItem[]; message?: string } | null>(null);

    // Pastes the copied style onto videos as a new DRAFT each — nothing is pushed to YouTube (and no
    // quota spent) until the user reviews the results and chooses to.
    const applyStyleToVideos = async (targets: { node: EnhancedNode; video: YouTubeVideoEntry }[]) => {
        if (!copiedStyle || targets.length === 0) return;
        setStyleReview({ phase: 'generating', done: 0, total: targets.length, items: [] });
        const items: StyleItem[] = [];
        for (const { node, video } of targets) {
            const key = `${node.id}::${video.youtubeVideoId}`;
            const title = video.youtubeTitle || video.videoTitle || node.title || video.youtubeVideoId;
            try {
                const noteImages = buildNoteImages(node);
                const base = (video.thumbnailSettings as Partial<ThumbSettings> | undefined) || {};
                const own = defaultThumbSettings({ title: video.youtubeTitle || node.title || 'StudyBoard', subtitleText: node._notebookTitle || '', imageCount: noteImages.length });
                const merged = { ...own, ...base, ...copiedStyle.style, v: 1 } as ThumbSettings;
                const full = await renderThumbnailFromSettings(merged, { noteImages, notebookId: node._notebookId, fallbackTitle: video.youtubeTitle || node.title || 'StudyBoard' });
                const preview = await downscaleToJpeg(full);
                const draft: ThumbDraft = { id: `d${Date.now().toString(36)}${items.length}`, name: `Style: ${copiedStyle.fromTitle.slice(0, 18)}`, settings: merged, preview, createdAt: Date.now() };
                await patchVideoById(video.youtubeVideoId, v => ({ thumbnailDrafts: [...((v.thumbnailDrafts || []) as unknown as ThumbDraft[]), draft].slice(-MAX_THUMB_DRAFTS) as unknown as YouTubeVideoEntry['thumbnailDrafts'] }));
                items.push({ key, videoId: video.youtubeVideoId, title, preview, settings: merged, status: 'draft', selected: true });
            } catch (e: any) {
                items.push({ key, videoId: video.youtubeVideoId, title, preview: '', settings: null, status: 'error', error: e?.message || String(e), selected: false });
            }
            setStyleReview({ phase: 'generating', done: items.length, total: targets.length, items: [...items] });
        }
        setStyleReview({ phase: 'review', done: items.length, total: targets.length, items });
    };
    const pushStyleReview = async () => {
        if (!styleReview) return;
        const picked = styleReview.items.filter(i => i.selected && i.status === 'draft' && i.settings);
        if (picked.length === 0) return;
        let items = styleReview.items;
        const setItems = (next: StyleItem[], message?: string, phase: 'review' | 'pushing' = 'pushing') => { items = next; setStyleReview(prev => prev ? { ...prev, phase, items: next, message } : prev); };
        setItems(items, undefined, 'pushing');
        for (const it of picked) {
            setItems(items.map(x => x.key === it.key ? { ...x, status: 'pushing' } : x));
            try {
                const entry = flatVideos.find(x => x.video.youtubeVideoId === it.videoId);
                if (!entry) throw new Error(vn ? 'Không tìm thấy video' : 'Video not found');
                const full = await renderThumbnailFromSettings(it.settings!, { noteImages: buildNoteImages(entry.node), notebookId: entry.node._notebookId, fallbackTitle: entry.video.youtubeTitle || entry.node.title || 'StudyBoard' });
                const [, mime, b64] = full.match(/^data:([^;]+);base64,(.+)$/) ?? [];
                await updateYouTubeVideo(it.videoId, { thumbnailBase64: b64, thumbnailMime: mime });
                await patchVideoById(it.videoId, () => ({ thumbnailSettings: it.settings as unknown as Record<string, unknown>, thumbnailPushedAt: Date.now() }));
                setItems(items.map(x => x.key === it.key ? { ...x, status: 'pushed', selected: false } : x));
            } catch (e: any) {
                const msg = e?.message === 'not_connected' ? (vn ? 'YouTube chưa kết nối hoặc hết phiên' : 'YouTube not connected or session expired') : (e?.message || String(e));
                setItems(items.map(x => x.key === it.key ? { ...x, status: 'error', error: msg } : x));
                // Every remaining push would fail for the same reason (quota / session) — stop here.
                if (/quota|not_connected|expired|hết phiên/i.test(String(e?.message) + msg)) {
                    setItems(items, vn ? 'Đã dừng: hết quota hoặc phiên YouTube hết hạn. Các mục còn lại vẫn là bản nháp, thử lại sau.' : 'Stopped: quota exhausted or YouTube session expired. The rest stay as drafts — try again later.', 'review');
                    return;
                }
            }
        }
        setItems(items, undefined, 'review');
    };

    const handleSaveDetails = async () => {
        if (!selectedNode || !selectedVideo || !ytConnected) return;
        if (scheduleDate && videoAudience === 'unset') { setSaveMsg(vn ? 'Hãy khai báo "Video này có dành cho trẻ em không?" trước khi lên lịch đăng' : 'Declare "Is this video made for kids?" before scheduling'); return; }
        setIsSaving(true); setSaveMsg('');
        try {
            const uid = auth.currentUser?.uid;
            const opts: Parameters<typeof updateYouTubeVideo>[1] = { title: editTitle, description: editDesc };
            if (scheduleDate) opts.publishAt = new Date(scheduleDate).toISOString();
            await updateYouTubeVideo(selectedVideo.youtubeVideoId, opts);
            const { doc: fsDoc, updateDoc } = await import('firebase/firestore');
            const nodeRef = fsDoc(db, `users/${uid}/notebooks/${selectedNode._notebookId}/nodes/${selectedNode.id}`);
            const fields: Partial<YouTubeVideoEntry> = { youtubeTitle: editTitle, youtubeDescription: editDesc, youtubeEditedAt: Date.now() };
            if (scheduleDate) { fields.youtubeStatus = 'scheduled'; fields.youtubeScheduledAt = new Date(scheduleDate).getTime(); }
            const patch = buildVideoUpdatePatch(selectedNode, selectedVideo.youtubeVideoId, fields);
            await updateDoc(nodeRef, patch);
            setAllNodes(prev => prev.map(n => n.id === selectedNode.id ? { ...n, ...patch } : n));
            setSaveMsg(vn ? '✓ Đã lưu' : '✓ Saved');
        } catch (e: any) {
            setSaveMsg(describeSaveError(e, vn ? 'Lỗi khi lưu' : 'Save failed', /[<>]/.test(editDesc) || /[<>]/.test(editTitle)));
        } finally { setIsSaving(false); }
    };

    const handlePublish = async () => {
        if (!selectedNode || !selectedVideo || !ytConnected) return;
        if (videoAudience === 'unset') { setSaveMsg(vn ? 'Hãy khai báo "Video này có dành cho trẻ em không?" trước khi publish' : 'Declare "Is this video made for kids?" before publishing'); return; }
        setIsSaving(true); setSaveMsg('');
        try {
            await updateYouTubeVideo(selectedVideo.youtubeVideoId, { privacyStatus: 'public' });
            const uid = auth.currentUser?.uid;
            const { doc: fsDoc, updateDoc } = await import('firebase/firestore');
            const patch = buildVideoUpdatePatch(selectedNode, selectedVideo.youtubeVideoId, { youtubeStatus: 'public' });
            await updateDoc(fsDoc(db, `users/${uid}/notebooks/${selectedNode._notebookId}/nodes/${selectedNode.id}`), patch);
            setAllNodes(prev => prev.map(n => n.id === selectedNode.id ? { ...n, ...patch } : n));
            setSaveMsg(vn ? '✓ Đã publish!' : '✓ Published!');
        } catch (e: any) { setSaveMsg(describeSaveError(e, vn ? 'Lỗi publish' : 'Publish failed', false)); }
        finally { setIsSaving(false); }
    };

    // Opens the reorder panel for the currently checked videos — selection order is not trusted as
    // final sequence (easy to pick out of order and have to redo everything), so this seeds an
    // explicit, separately-reorderable list instead.
    const lastCheckedKeyRef = useRef<string | null>(null);
    const combinableChecked = Array.from(checkedVideoIds).filter(key => {
        const [nodeId, videoId] = key.split('::');
        return !!flatVideos.find(x => x.node.id === nodeId && x.video.youtubeVideoId === videoId)?.video.videoStoragePath;
    }).length;
    const openCombinePanel = () => {
        const items = Array.from(checkedVideoIds).map(key => {
            const [nodeId, videoId] = key.split('::');
            const node = allNodes.find(n => n.id === nodeId)!;
            const video = getVideosForNode(node).find(v => v.youtubeVideoId === videoId)!;
            return { key, node, video };
        // Ticks are shared with other bulk actions (e.g. new thumbnails); only videos with a backup copy can be combined.
        }).filter(item => item.node && item.video && item.video.videoStoragePath);
        openCombinePanelWith(items);
    };

    // Shared by the Video tab (checkboxes) and the Map tab (picked squares, already in reading order).
    const openCombinePanelWith = (items: { key: string; node: EnhancedNode; video: YouTubeVideoEntry }[]) => {
        // A combine already running in the background: show its progress instead of starting over
        // (its listener is still live and would otherwise overwrite the new panel's state).
        if (combinePhase === 'pending' || combinePhase === 'processing') { setShowCombinePanel(true); return; }
        setCombineOrder(items);
        setCombineTargetNodeId(items[0]?.node.id ?? null);
        setCombinePhase('idle');
        setCombineError('');
        setShowCombinePanel(true);
    };

    const moveCombineItem = (idx: number, dir: -1 | 1) => {
        setCombineOrder(prev => {
            const next = [...prev];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return prev;
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });
    };

    // Trim = the same export pipeline as combining, with a single chunk that covers only the chosen
    // range of one existing video (the server resolves the video's backup file from its ids, as for
    // a combine). The result is handed to the upload window exactly like a combined or freshly
    // recorded video: nothing is uploaded to YouTube until the user does it there. If that window
    // is closed, the finished job stays listed on the note as an export waiting to be used.
    const handleStartTrim = async (startMs: number, endMs: number) => {
        if (!trimTarget) return;
        const { node, video } = trimTarget;
        setTrimPhase('pending');
        setTrimProgress(null);
        setTrimError('');
        try {
            const requestExport = httpsCallable(functions, 'requestVideoExport');
            const res: any = await requestExport({
                chunks: [{
                    startMs, endMs, speed: 1, kind: 'speaking' as const,
                    sourceClip: { notebookId: node._notebookId, nodeId: node.id, videoId: video.youtubeVideoId },
                }],
                notebookId: node._notebookId,
                nodeId: node.id,
                ...(autopilotOn ? { autopilot: true, language } : {}),
            });
            const jobId = res.data.jobId;
            const jobRef = doc(db, `users/${auth.currentUser?.uid}/videoExportJobs/${jobId}`);
            trimUnsubRef.current = onSnapshot(jobRef, async (snap) => {
                const data = snap.data();
                if (!data) return;
                if (data.status === 'processing' || data.status === 'pending') {
                    setTrimPhase(data.status);
                    setTrimProgress(typeof (data as any).progress === 'number' ? (data as any).progress : null);
                } else if (data.status === 'done') {
                    // Autopilot: the server uploads it; only a skipped upload falls back to the window.
                    if (data.autopilot === true && data.autoUpload?.state !== 'skipped') {
                        if (data.autoUpload?.state === 'done' || data.autoUpload?.state === 'error') {
                            trimUnsubRef.current?.();
                            setTrimPhase('idle');
                            setTrimTarget(null);
                            if (data.autoUpload.state === 'error') alert((vn ? 'Server không upload được video đã cắt: ' : 'The server could not upload the trimmed video: ') + (data.autoUpload.error || ''));
                        } else setTrimPhase('processing');
                        return;
                    }
                    trimUnsubRef.current?.();
                    setTrimPhase('fetching');
                    try {
                        const url = await getDownloadURL(storageRef(storage, data.storagePathFinal));
                        const blob = await (await fetch(url)).blob();
                        const file = new File([blob], `studyboard-trim-${Date.now()}.mp4`, { type: 'video/mp4' });
                        setTrimPhase('idle');
                        setTrimTarget(null);
                        onOpenUpload(node.id, node._notebookId, file, jobId);
                    } catch (e: any) {
                        setTrimPhase('error');
                        setTrimError(e?.message || 'Failed to fetch the trimmed video');
                    }
                } else if (data.status === 'error') {
                    trimUnsubRef.current?.();
                    setTrimPhase('error');
                    setTrimError(data.error || 'Trim failed');
                }
            });
        } catch (e: any) {
            setTrimPhase('error');
            setTrimError(e?.message || 'Trim failed');
        }
    };
    useEffect(() => () => trimUnsubRef.current?.(), []);

    // Kicks off the generalized multi-source export: one whole-clip chunk per selected video, in
    // the order confirmed in the panel — no storagePath on this job (pure combine, no live
    // recording of its own), so every chunk names its own sourceClip. The server resolves each
    // sourceClip to that video's canonical, currently-published backup file — never a client-
    // supplied storage path — see requestVideoExport. A generous 4h sentinel endMs stands in for
    // "the whole clip"; ffmpeg's trim filters clamp to the source's actual duration on their own.
    const handleStartCombine = async () => {
        setCombinePhase('pending');
        setCombineProgress(null);
        setCombineError('');
        try {
            const chunks = combineOrder.map(item => ({
                startMs: 0,
                endMs: 4 * 60 * 60 * 1000,
                speed: 1,
                kind: 'speaking' as const,
                sourceClip: { notebookId: item.node._notebookId, nodeId: item.node.id, videoId: item.video.youtubeVideoId },
            }));
            const targetNodeForJob = allNodes.find(n => n.id === combineTargetNodeId) || combineOrder[0]?.node;
            const requestExport = httpsCallable(functions, 'requestVideoExport');
            const res: any = await requestExport({
                chunks,
                notebookId: targetNodeForJob?._notebookId,
                nodeId: targetNodeForJob?.id,
                ...(autopilotOn ? { autopilot: true, language } : {}),
            });
            setCombineAuto(null);
            const jobId = res.data.jobId;
            const uidNow = auth.currentUser?.uid;
            const jobRef = doc(db, `users/${uidNow}/videoExportJobs/${jobId}`);
            combineUnsubRef.current = onSnapshot(jobRef, async (snap) => {
                const data = snap.data();
                if (!data) return;
                if (data.status === 'processing' || data.status === 'pending') {
                    setCombinePhase(data.status);
                    setCombineProgress(typeof (data as any).progress === 'number' ? (data as any).progress : null);
                } else if (data.status === 'done') {
                    // Autopilot: the server uploads the combined video and runs the same overnight
                    // processing as a fresh recording. Only a skipped upload (YouTube not
                    // connected for the server) falls back to the manual upload window.
                    if (data.autopilot === true && data.autoUpload?.state !== 'skipped') {
                        const st = data.autoUpload?.state;
                        if (st === 'done' || st === 'error') {
                            combineUnsubRef.current?.();
                            setCheckedVideoIds(new Set());
                            setCombineAuto(st === 'done' ? { state: 'done' } : { state: 'error', error: data.autoUpload?.error });
                            setCombinePhase('done');
                        } else {
                            setCombineAuto({ state: 'uploading' });
                            setCombinePhase('processing');
                        }
                        return;
                    }
                    combineUnsubRef.current?.();
                    try {
                        const url = await getDownloadURL(storageRef(storage, data.storagePathFinal));
                        const fileRes = await fetch(url);
                        const blob = await fileRes.blob();
                        const combinedFile = new File([blob], `studyboard-combined-${Date.now()}.mp4`, { type: 'video/mp4' });
                        setCombinePhase('done');
                        setShowCombinePanel(false);
                        setCheckedVideoIds(new Set());
                        if (targetNodeForJob) onOpenUpload(targetNodeForJob.id, targetNodeForJob._notebookId, combinedFile, jobId);
                    } catch (e: any) {
                        setCombinePhase('error');
                        setCombineError(e?.message || 'Failed to fetch combined video');
                    }
                } else if (data.status === 'error') {
                    combineUnsubRef.current?.();
                    setCombinePhase('error');
                    setCombineError(data.error || 'Combine failed');
                }
            });
        } catch (e: any) {
            setCombinePhase('error');
            setCombineError(e?.message || 'Failed to start combine');
        }
    };

    // Shared apply step for analyzeVideoIdeas' result: update the note's own note-level fields
    // locally, and merge the returned ideas into local videoIdeas state — mirroring the server's
    // own reconciliation (drop this note's old "suggested" ideas, add the fresh ones) so the "Ý
    // tưởng video" tab doesn't need a full reload after every Check, only the notes actually
    // re-analyzed change.
    const applyVideoIdeasResult = useCallback((nodeId: string, data: { overallScore: number; summary: string; videoIdeas: VideoIdea[] }) => {
        setAllNodes(prev => prev.map(n => n.id === nodeId
            ? { ...n, videoWorthinessScore: data.overallScore, videoWorthinessSummary: data.summary, videoAnalysisCheckedAt: Date.now() }
            : n
        ));
        setVideoIdeas(prev => [
            ...prev.filter(v => !(v.nodeId === nodeId && v.status === 'suggested')),
            ...data.videoIdeas,
        ]);
    }, []);

    const handleCheckQuality = async () => {
        if (!selectedNode) return;
        setIsChecking(true);
        try {
            const fn = httpsCallable(functions, 'analyzeVideoIdeas');
            const customCriteria = scoringCriteria.trim() || undefined;
            const res = await fn({ notebookId: selectedNode._notebookId, nodeId: selectedNode.id, notebookTitle: selectedNode._notebookTitle, title: selectedNode.title, content: selectedNode.content, language, customCriteria }) as any;
            applyVideoIdeasResult(selectedNode.id, res.data);
        } catch { } finally { setIsChecking(false); }
    };


    // mode 'skip-scored' (default) only analyzes notes that have never been checked — e.g.
    // scoring a whole chapter after only some of its sub-sections were added later. mode
    // 'rescore-all' ignores that and re-analyzes every note in the branch(es), useful after the
    // prompt/rubric itself changed and old results should be refreshed.
    // Single code path for every "score more than one note at once" trigger — whether the notes
    // came from checking branch checkboxes, checking individual note checkboxes, or just viewing
    // one branch with none checked. Having handleBatchScore and handleScoreSelected as two
    // separate functions was the actual bug behind "checking branches skips the mode dialog":
    // branch checkboxes populate checkedNodeIds too (see their onChange below), which routed
    // through handleScoreSelected — a path that never got the dialog wired into it. One function,
    // called from one dialog, can't drift out of sync like that again.
    const runBatchScore = async (nodeIds: string[], mode: 'skip-scored' | 'rescore-all') => {
        if (isBatchScoring || nodeIds.length === 0) return;
        const idSet = new Set(nodeIds);
        const notesToScore = allNodes.filter(n => idSet.has(n.id) && (mode === 'rescore-all' || !n.videoAnalysisCheckedAt));
        if (notesToScore.length === 0) return;
        batchAbortRef.current = false;
        setIsBatchScoring(true);
        setBatchProgress({ done: 0, total: notesToScore.length });
        setScoreQuotaError('');
        const fn = httpsCallable(functions, 'analyzeVideoIdeas');
        const customCriteria = scoringCriteria.trim() || undefined;
        for (let i = 0; i < notesToScore.length; i++) {
            if (batchAbortRef.current) break;
            const node = notesToScore[i];
            try {
                const res = await fn({
                    notebookId: node._notebookId, nodeId: node.id, notebookTitle: node._notebookTitle,
                    title: node.title, content: node.content, language, customCriteria,
                }) as any;
                applyVideoIdeasResult(node.id, res.data);
            } catch (e: any) {
                if (e?.code === 'functions/resource-exhausted') {
                    setScoreQuotaError(e.message || (vn ? 'Hết lượt AI miễn phí hôm nay' : 'Out of free AI uses for today'));
                    break;
                }
                /* other errors: skip this note, keep going */
            }
            setBatchProgress({ done: i + 1, total: notesToScore.length });
        }
        setIsBatchScoring(false);
        setBatchProgress(null);
        // Keep the just-scored branches in view (as a plain "viewing" selection) so the column
        // doesn't collapse to "Chọn một nhánh" the instant the checkmarks clear below.
        const scoredBranchIds = new Set(
            allNodes.filter(n => idSet.has(n.id) && n._branchId).map(n => n._branchId as string)
        );
        if (scoredBranchIds.size > 0) setSelectedBranchIds(scoredBranchIds);
        setCheckedBranchIds(new Set());
        setCheckedNodeIds(new Set());
    };

    const handleScoreOne = async (node: EnhancedNode) => {
        if (isBatchScoring || scoringNodeIds.has(node.id)) return;
        setScoringNodeIds(prev => new Set([...prev, node.id]));
        try {
            const fn = httpsCallable(functions, 'analyzeVideoIdeas');
            const customCriteria = scoringCriteria.trim() || undefined;
            const res = await fn({ notebookId: node._notebookId, nodeId: node.id, notebookTitle: node._notebookTitle, title: node.title, content: node.content, language, customCriteria }) as any;
            applyVideoIdeasResult(node.id, res.data);
        } catch (e: any) {
            if (e?.code === 'functions/resource-exhausted') {
                setScoreQuotaError(e.message || (vn ? 'Hết lượt AI miễn phí hôm nay' : 'Out of free AI uses for today'));
            }
        }
        setScoringNodeIds(prev => { const s = new Set(prev); s.delete(node.id); return s; });
    };

    const handleSuggestCombined = async () => {
        if (isSuggestingCombined || checkedNodeIds.size < 2) return;
        const selectedNotes = allNodes.filter(n => checkedNodeIds.has(n.id));
        setIsSuggestingCombined(true);
        setCombinedSuggestError('');
        setCombinedSuggestion(null);
        try {
            const result = await suggestCombinedVideo(
                selectedNotes.map(n => ({ title: n.title || '', content: n.content || '' })),
                language
            );
            setCombinedSuggestion(result);
        } catch (e: any) {
            setCombinedSuggestError(
                e?.code === 'functions/resource-exhausted'
                    ? (e.message || (vn ? 'Hết lượt AI miễn phí hôm nay' : 'Out of free AI uses for today'))
                    : (vn ? 'Không tạo được gợi ý' : 'Failed to generate suggestion')
            );
        } finally {
            setIsSuggestingCombined(false);
        }
    };

    // Persists an AI Check verdict onto a given video entry so it survives closing/reopening the
    // note instead of evaporating with local state — best-effort, mirrors the pattern already used
    // for title/description/thumbnail saves elsewhere in this file. Takes an explicit node/video
    // (not just the currently-selected one) so the batch checker below can persist results for
    // videos the user isn't even looking at.
    const persistTranscriptCheckFor = async (targetNode: EnhancedNode, targetVideo: YouTubeVideoEntry, result: { score: number; recommendation: 'publish' | 'publish_with_correction' | 're_record'; strengths: string[]; findings: { severity: string; note: string }[]; checkSummary: string; passes?: AiCheckPass[] }) => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        try {
            const patch = buildVideoUpdatePatch(targetNode, targetVideo.youtubeVideoId, {
                postCheckScore: result.score,
                postCheckRecommendation: result.recommendation,
                postCheckStrengths: result.strengths,
                postCheckFindings: result.findings as any,
                postCheckSummary: result.checkSummary,
                postCheckPasses: result.passes,
                postCheckCheckedAt: Date.now(),
            });
            await updateDoc(doc(db, `users/${uid}/notebooks/${targetNode._notebookId}/nodes/${targetNode.id}`), patch);
            setAllNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, ...patch } : n));
        } catch { /* best-effort — the check result still shows for this session even if the save fails */ }
    };
    const persistTranscriptCheck = async (result: { score: number; recommendation: 'publish' | 'publish_with_correction' | 're_record'; strengths: string[]; findings: { severity: string; note: string }[]; checkSummary: string; passes?: AiCheckPass[] }) => {
        if (!selectedNode || !selectedVideo) return;
        await persistTranscriptCheckFor(selectedNode, selectedVideo, result);
    };

    // Caches the fetched caption text on the video itself so re-checking later (e.g. after an AI
    // Check quality upgrade, or arguing a verdict) never has to call the YouTube Data API again —
    // that API's daily quota is shared and easily exhausted by re-fetching the same captions
    // repeatedly, which is exactly what batch re-checking old videos used to do.
    const persistTranscriptFor = async (targetNode: EnhancedNode, targetVideo: YouTubeVideoEntry, text: string, timed?: string) => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        try {
            const patch = buildVideoUpdatePatch(targetNode, targetVideo.youtubeVideoId, { youtubeTranscript: text, ...(timed ? { youtubeTimedTranscript: timed } : {}) });
            await updateDoc(doc(db, `users/${uid}/notebooks/${targetNode._notebookId}/nodes/${targetNode.id}`), patch);
            setAllNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, ...patch } : n));
        } catch { /* best-effort — worst case it's simply re-fetched next time */ }
    };

    // Batch quality check — checks every private, not-yet-checked video in the active notebook so
    // the user can scan scores in the list instead of opening each video one by one. Bounded
    // concurrency (a few videos at a time) rather than firing every request at once; a video with
    // no captions yet is skipped rather than failing the whole batch.
    const [batchCheckState, setBatchCheckState] = useState<{ done: number; total: number } | null>(null);
    // Surfaced once a batch run finishes. "noCaption" = genuinely no captions yet (benign, YouTube
    // still processing); "failed" = a real API error (auth, network, quota, ...) distinct from that.
    const [batchCheckResult, setBatchCheckResult] = useState<{ ok: number; noCaption: number; failed: number; quotaExceeded: boolean; authExpired: boolean } | null>(null);
    // Never auto-trigger the YouTube OAuth flow — always surface an explicit prompt first and let
    // the user click through themselves, per standing preference.
    const [showBatchConnectPrompt, setShowBatchConnectPrompt] = useState(false);
    const handleBatchCheck = async () => {
        if (!ytConnected) { setShowBatchConnectPrompt(true); return; }
        const targets = flatVideos.filter(x => needsCheck(x.video));
        if (targets.length === 0) return;
        setBatchCheckResult(null);
        setBatchCheckState({ done: 0, total: targets.length });
        let doneCount = 0, okCount = 0, noCaptionCount = 0, failedCount = 0;
        let quotaExceeded = false;
        let authExpired = false;
        let cursor = 0;
        const CONCURRENCY = 3;
        const worker = async () => {
            while (cursor < targets.length) {
                // The YouTube Data API's daily quota is shared across the whole batch — once one
                // call comes back quota-exceeded, every remaining call will too, so stop immediately
                // instead of burning through the rest of the list on calls guaranteed to fail.
                if (quotaExceeded || authExpired) break;
                const { node: targetNode, video: targetVideo } = targets[cursor++];
                try {
                    // Reuse a previously-fetched transcript if we have one cached — this is the
                    // common case when re-checking a video that was already checked under the old
                    // single-pass system, and skipping the API call entirely is what actually makes
                    // "re-check everything" affordable within the daily quota.
                    let text = targetVideo.youtubeTranscript;
                    const noCaptionKey = 'sb_yt_no_caption_v1';
                    const noCaptionAt: Record<string, number> = (() => { try { return JSON.parse(localStorage.getItem(noCaptionKey) || '{}'); } catch { return {}; } })();
                    const recentlyNoCaptions = !text && Date.now() - (noCaptionAt[targetVideo.youtubeVideoId] || 0) < 6 * 60 * 60 * 1000;
                    if (recentlyNoCaptions) {
                        // Already asked YouTube within the last 6 hours and it had no captions — every ask costs quota.
                        noCaptionCount++;
                    } else if (!text) {
                        const transcriptResult = await fetchYouTubeTranscript(targetVideo.youtubeVideoId);
                        if ('text' in transcriptResult) {
                            text = transcriptResult.text;
                            await persistTranscriptFor(targetNode, targetVideo, text, transcriptResult.timed);
                        } else if (transcriptResult.error === 'no_captions') {
                            noCaptionCount++;
                            try { localStorage.setItem(noCaptionKey, JSON.stringify({ ...noCaptionAt, [targetVideo.youtubeVideoId]: Date.now() })); } catch { /* ignore */ }
                        } else if (transcriptResult.error === 'auth_expired') {
                            authExpired = true;
                            failedCount++;
                        } else {
                            if (/quota/i.test(transcriptResult.error)) quotaExceeded = true;
                            failedCount++;
                        }
                    }
                    if (text) {
                        const checkResult = await checkTranscriptQuality(text, targetNode.title || '', targetNode.content || '', language, targetVideo.videoClipFocus || undefined);
                        await persistTranscriptCheckFor(targetNode, targetVideo, checkResult);
                        okCount++;
                    }
                } catch { failedCount++; }
                doneCount++;
                setBatchCheckState({ done: doneCount, total: targets.length });
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker));
        setBatchCheckState(null);
        setBatchCheckResult({ ok: okCount, noCaption: noCaptionCount, failed: failedCount, quotaExceeded, authExpired });
    };

    // One-off fix for the 2026-09-29 transcript-truncation bug: every already-checked video whose
    // cached transcript is longer than the old 8000-char server-side cap was graded on a cut-off
    // transcript (checkTranscriptQuality itself is now fixed to use the full transcript). This
    // re-runs the check on every such video, persists the corrected verdict, swaps the corrected
    // "📊 AI Check" block into the saved description, and pushes that description straight to
    // YouTube — since only the browser holds a live YouTube OAuth token, this can't be done from a
    // server script, only from here.
    const [recheckPushState, setRecheckPushState] = useState<{ done: number; total: number } | null>(null);
    const [recheckPushResult, setRecheckPushResult] = useState<{ ok: number; failed: number; escalated: string[] } | null>(null);
    const TRUNCATION_FIX_CAP = 8000;
    const handleBatchRecheckAndPushYoutube = async () => {
        if (!ytConnected) { setShowBatchConnectPrompt(true); return; }
        const targets = flatVideos.filter(x => (x.video.youtubeTranscript?.length || 0) > TRUNCATION_FIX_CAP && x.video.postCheckRecommendation);
        if (targets.length === 0) return;
        setRecheckPushResult(null);
        setRecheckPushState({ done: 0, total: targets.length });
        let doneCount = 0, okCount = 0, failedCount = 0;
        const escalated: string[] = [];
        let cursor = 0;
        const CONCURRENCY = 2; // gentle on both Gemini and the YouTube Data API
        const worker = async () => {
            while (cursor < targets.length) {
                const { video, node } = targets[cursor++];
                try {
                    const text = video.youtubeTranscript!;
                    const result = await checkTranscriptQuality(text, node.title || '', node.content || '', language, video.videoClipFocus || undefined);
                    const oldRec = video.postCheckRecommendation;
                    await persistTranscriptCheckFor(node, video, result);
                    const existingDesc = video.youtubeDescription || '';
                    const newBlock = formatAiCheckForDescription(result, vn);
                    const merged = replaceCheckBlock(existingDesc, newBlock) ?? (existingDesc.trim() ? existingDesc.trimEnd() + '\n\n' + newBlock : newBlock);
                    await updateYouTubeVideo(video.youtubeVideoId, { description: merged });
                    const uid = auth.currentUser?.uid;
                    const patch = buildVideoUpdatePatch(node, video.youtubeVideoId, { youtubeDescription: merged, youtubeEditedAt: Date.now() });
                    await updateDoc(doc(db, `users/${uid}/notebooks/${node._notebookId}/nodes/${node.id}`), patch);
                    setAllNodes(prev => prev.map(n => n.id === node.id ? { ...n, ...patch } : n));
                    okCount++;
                    if (result.recommendation === 're_record' && oldRec !== 're_record') escalated.push(node.title || video.youtubeVideoId);
                } catch { failedCount++; }
                doneCount++;
                setRecheckPushState({ done: doneCount, total: targets.length });
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker));
        setRecheckPushState(null);
        setRecheckPushResult({ ok: okCount, failed: failedCount, escalated });
    };

    // Accepts an explicit transcript text (used right after a fresh fetch, since setTranscript's
    // state update isn't visible yet in this same closure) or falls back to whatever's in state
    // (the "Chạy lại" re-check button, run against an already-loaded transcript).
    const handleCheckTranscript = async (transcriptTextArg?: string) => {
        const text = transcriptTextArg ?? transcript;
        if (!text || !selectedNode) return;
        setIsCheckingTranscript(true);
        setTranscriptCheck(null);
        try {
            const result = await checkTranscriptQuality(text, selectedNode.title || '', selectedNode.content || '', language, clipFocusInput.trim() || undefined);
            setTranscriptCheck(result);
            await persistTranscriptCheck(result);
        } catch { /* silent */ }
        finally { setIsCheckingTranscript(false); }
    };

    // "Argue with AI Check" — sends the running chat history + the current verdict + the new
    // message to discussAiCheck. The model may hold its verdict (explaining why) or genuinely
    // revise it; a revision is shown as its own card in the thread and only takes effect on the
    // saved video entry once the user explicitly clicks "Cập nhật kết quả" (persistTranscriptCheck).
    const handleSendDiscuss = async () => {
        const message = discussInput.trim();
        if (!message || !transcript || !transcriptCheck || !selectedNode || discussLoading) return;
        setDiscussInput('');
        const nextMessages = [...discussMessages, { role: 'user' as const, text: message }];
        setDiscussMessages(nextMessages);
        setDiscussLoading(true);
        try {
            const historyForApi = discussMessages.map(m => ({ role: m.role, text: m.text }));
            const result = await discussAiCheck(
                transcript, selectedNode.title || '', selectedNode.content || '', language,
                clipFocusInput.trim() || undefined, transcriptCheck as any, historyForApi, message,
            );
            setDiscussMessages([...nextMessages, { role: 'ai', text: result.reply, revisedVerdict: result.revisedVerdict }]);
        } catch (e: any) {
            setDiscussMessages([...nextMessages, { role: 'ai', text: vn ? 'Xin lỗi, có lỗi xảy ra, thử lại nhé.' : 'Sorry, something went wrong — try again.' }]);
        } finally {
            setDiscussLoading(false);
        }
    };

    const handleAcceptRevisedVerdict = async (verdict: NonNullable<typeof transcriptCheck>) => {
        setTranscriptCheck(verdict);
        await persistTranscriptCheck(verdict);
    };

    // Single button, one click: fetch YouTube's transcript, then immediately run the AI check on
    // it — this used to be two separate clicks ("Get transcript" then a second "AI Check" button
    // that only appeared afterward), which read as needlessly complicated for what's really one
    // step from the user's point of view. "AI tóm tắt" (description) still does its own independent
    // fetch+check+summarize for the fully-automatic path; this is the manual "review, then decide"
    // path — see the "Đưa vào mô tả" button below for the explicit last step.
    // `force` bypasses the cached transcript (e.g. the video's captions were edited on YouTube
    // after the last fetch) — the default path reuses whatever's cached on the video so re-running
    // the AI check never has to re-spend YouTube Data API quota on a transcript we already have.
    // ── Note from video ──────────────────────────────────────────────────────
    // The reverse of the usual order: explain on video first, then turn that explanation into the
    // note's written version. Always previewed before anything is saved, and never replaces text
    // the note already has.
    const [noteDraft, setNoteDraft] = useState<{ text: string; source: 'recording' | 'youtube'; nodeId: string; videoId: string } | null>(null);
    const [isGenNote, setIsGenNote] = useState(false);
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [noteGenMsg, setNoteGenMsg] = useState('');
    const handleGenNoteFromVideo = async () => {
        if (!selectedVideo || !selectedNode || isGenNote) return;
        setIsGenNote(true); setNoteGenMsg('');
        try {
            // What was transcribed while recording is available at once and usually cleaner than
            // YouTube's automatic captions, so it is preferred when it covers the video.
            const recorded = (selectedVideo.videoSegments || []).map(seg => (seg.transcript || '').trim()).filter(Boolean).join('\n\n');
            let source: 'recording' | 'youtube' = 'recording';
            let text = recorded.length >= 200 ? recorded : '';
            if (!text) {
                source = 'youtube';
                text = selectedVideo.youtubeTranscript || '';
                if (!text) {
                    const result = await fetchYouTubeTranscript(selectedVideo.youtubeVideoId);
                    if ('text' in result) {
                        text = result.text;
                        await persistTranscriptFor(selectedNode, selectedVideo, text, result.timed);
                    } else {
                        setNoteGenMsg(result.error === 'no_captions'
                            ? (vn ? 'YouTube chưa có transcript cho video này — thử lại sau ít phút.' : 'YouTube has no transcript for this video yet — try again in a few minutes.')
                            : result.error === 'auth_expired'
                                ? (vn ? 'Phiên kết nối YouTube đã hết hạn — kết nối lại rồi thử lại.' : 'YouTube session expired — reconnect and try again.')
                                : result.error);
                        return;
                    }
                }
            }
            const note = await generateNoteFromTranscript(text, selectedNode.title || '', selectedNode._notebookTitle || '', language);
            if (!note.trim()) { setNoteGenMsg(vn ? 'AI không trả về nội dung.' : 'The AI returned nothing.'); return; }
            setNoteDraft({ text: note, source, nodeId: selectedNode.id, videoId: selectedVideo.youtubeVideoId });
        } catch (e: any) {
            setNoteGenMsg(e?.message || (vn ? 'Không tạo được ghi chú.' : 'Could not write the note.'));
        } finally { setIsGenNote(false); }
    };
    const handleSaveNoteFromVideo = async () => {
        if (!noteDraft || !noteDraft.text.trim()) return;
        const uid = auth.currentUser?.uid;
        const targetNode = allNodes.find(n => n.id === noteDraft.nodeId);
        if (!uid || !targetNode) return;
        setIsSavingNote(true);
        try {
            const nodeRef = doc(db, `users/${uid}/notebooks/${targetNode._notebookId}/nodes/${targetNode.id}`);
            // Append to the note as it is on the server right now, not to this list's older copy.
            const snap = await getDoc(nodeRef);
            const current = String((snap.exists() ? (snap.data() as MindMapNode).content : targetNode.content) || '');
            const label = vn ? '🎥 Ghi chú tạo từ video' : '🎥 Note written from the video';
            const block = `> ${label}: https://www.youtube.com/watch?v=${noteDraft.videoId}\n\n${noteDraft.text.trim()}`;
            const content = current.trim() ? `${current.replace(/\s+$/, '')}\n\n---\n\n${block}` : block;
            const patch: Partial<MindMapNode> = { content, updatedAt: Date.now(), noteFromVideoAt: Date.now(), noteFromVideoId: noteDraft.videoId };
            await updateDoc(nodeRef, patch as Record<string, unknown>);
            setAllNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, ...patch } : n));
            onNodePatched?.(targetNode._notebookId, targetNode.id, patch);
            setNoteDraft(null);
            setNoteGenMsg(vn ? '✓ Đã thêm vào note' : '✓ Added to the note');
        } catch (e: any) {
            setNoteGenMsg(e?.message || (vn ? 'Không lưu được vào note.' : 'Could not save to the note.'));
        } finally { setIsSavingNote(false); }
    };

    // Loaded once the notes are known. A video that is linked to a note again (the delete was
    // undone, or it was attached elsewhere) no longer belongs in the list and is dropped from it.
    const orphansLoadedFor = useRef(-1);
    useEffect(() => {
        const uidNow = auth.currentUser?.uid;
        if (!uidNow || allNodes.length === 0 || orphansLoadedFor.current === allNodes.length) return;
        orphansLoadedFor.current = allNodes.length;
        listOrphanVideos(uidNow).then(list => {
            const linked = new Set(allNodes.flatMap(n => getVideosForNode(n).map(v => v.youtubeVideoId)));
            const still = list.filter(o => !linked.has(o.video.youtubeVideoId));
            list.filter(o => linked.has(o.video.youtubeVideoId)).forEach(o => { removeOrphanVideo(uidNow, o.video.youtubeVideoId).catch(() => {}); });
            setOrphans(still);
        }).catch(() => {});
    }, [allNodes]);

    const handleAttachOrphan = async (orphan: OrphanVideo, target: EnhancedNode) => {
        const uidNow = auth.currentUser?.uid;
        if (!uidNow) return;
        const videoId = orphan.video.youtubeVideoId;
        setOrphanBusy(videoId); setOrphanMsg('');
        try {
            const nodeRef = doc(db, `users/${uidNow}/notebooks/${target._notebookId}/nodes/${target.id}`);
            const snap = await getDoc(nodeRef);
            if (!snap.exists()) throw new Error(vn ? 'Note đích không còn tồn tại' : 'The target note no longer exists');
            const fresh = snap.data() as MindMapNode;
            const { id: _id, ...fields } = orphan.video;

            // The description's "Full notes" link still points at the deleted note: aim it at the
            // new one, when this notebook's published address and the note's chapter are known.
            let description = fields.youtubeDescription || '';
            let linkNote = '';
            const chapter = branches.find(b => b.id === target._branchId)?.title;
            const newLink = notebookPublishUrl && chapter ? resolveNoteLink(notebookPublishUrl, chapter, target.id) : null;
            const oldLink = (description.match(new RegExp(`https?://\\S+#node-${orphan.fromNodeId}(?![\\w-])`)) || [])[0];
            if (newLink && oldLink && oldLink !== newLink) {
                description = description.split(oldLink).join(newLink);
                if (ytConnected) {
                    try { await updateYouTubeVideo(videoId, { description }); linkNote = vn ? ' Link trong mô tả YouTube đã trỏ sang note mới.' : ' The link in the YouTube description now points to the new note.'; }
                    catch { description = fields.youtubeDescription || ''; linkNote = vn ? ' Chưa sửa được link trên YouTube — mở video và bấm "Cập nhật link".' : ' The YouTube link could not be updated — open the video and press "Update link".'; }
                } else {
                    description = fields.youtubeDescription || '';
                    linkNote = vn ? ' YouTube chưa kết nối nên link trong mô tả chưa được sửa.' : ' YouTube is not connected, so the description link was not updated.';
                }
            } else if (oldLink && !newLink) {
                linkNote = vn ? ' Link trong mô tả vẫn trỏ tới note cũ — mở video để cập nhật.' : ' The description link still points to the old note — open the video to update it.';
            }

            const patch = buildVideoAppendPatch(fresh, JSON.parse(JSON.stringify({ ...fields, youtubeDescription: description })));
            await updateDoc(nodeRef, patch);
            setAllNodes(prev => prev.map(n => n.id === target.id ? { ...n, ...patch } : n));
            onNodePatched?.(target._notebookId, target.id, patch as Partial<MindMapNode>);
            await removeOrphanVideo(uidNow, videoId);
            setOrphans(prev => prev.filter(o => o.video.youtubeVideoId !== videoId));
            setOrphanMsg(`✓ ${vn ? 'Đã gắn vào' : 'Attached to'} "${target.title || ''}".${linkNote}`);
        } catch (e: any) {
            setOrphanMsg(`${vn ? 'Không gắn được' : 'Could not attach'}: ${e?.message || e}`);
        } finally { setOrphanBusy(null); }
    };
    const handleDismissOrphan = async (orphan: OrphanVideo) => {
        const uidNow = auth.currentUser?.uid;
        if (!uidNow) return;
        if (!window.confirm(vn
            ? 'Bỏ video này khỏi danh sách? Video vẫn còn trên YouTube, nhưng StudyBoard sẽ không còn ghi nhớ nó.'
            : 'Remove this video from the list? It stays on YouTube, but StudyBoard will no longer keep a record of it.')) return;
        try { await removeOrphanVideo(uidNow, orphan.video.youtubeVideoId); setOrphans(prev => prev.filter(o => o.video.youtubeVideoId !== orphan.video.youtubeVideoId)); }
        catch (e: any) { setOrphanMsg(e?.message || String(e)); }
    };

    const handleFetchTranscript = async (force = false) => {
        if (!selectedVideo || !selectedNode) return;
        setIsFetchingTranscript(true);
        setTranscriptError('');
        setTranscript(null);
        setTranscriptCheck(null);
        try {
            let text = !force ? selectedVideo.youtubeTranscript : undefined;
            if (!text) {
                const result = await fetchYouTubeTranscript(selectedVideo.youtubeVideoId);
                if ('text' in result) {
                    text = result.text;
                    await persistTranscriptFor(selectedNode, selectedVideo, text, result.timed);
                } else if (result.error === 'no_captions') {
                    setTranscriptError(vn ? 'Chưa có transcript — YouTube có thể chưa xử lý xong, thử lại sau.' : 'No transcript yet — YouTube may still be processing. Try again later.');
                    return;
                } else if (result.error === 'auth_expired') {
                    setTranscriptError(vn ? 'Phiên kết nối YouTube đã hết hạn — bấm nút "Kết nối YouTube" ở góc trên bên phải rồi thử lại.' : 'YouTube session expired — click "Connect YouTube" at the top right and try again.');
                    return;
                } else {
                    setTranscriptError(result.error);
                    return;
                }
            }
            setTranscript(text);
            setShowTranscript(true);
            setIsFetchingTranscript(false);
            await handleCheckTranscript(text);
            return;
        } catch (e: any) {
            setTranscriptError(e.message || 'Failed to fetch transcript.');
        } finally {
            setIsFetchingTranscript(false);
        }
    };

    // Pulls the CURRENT live title/description straight from YouTube and overwrites both the
    // editor fields and Firestore with it — the manual escape hatch for whenever this app's local
    // copy has drifted from what's actually live on YouTube (e.g. a push that reached YouTube but,
    // for whatever reason, never got persisted back here — youtubeEditedAt staying unset on a video
    // that's visibly been edited on YouTube is the tell). Deliberately manual, not automatic on
    // every video open, since it spends a YouTube Data API read each time.
    const [isSyncingFromYT, setIsSyncingFromYT] = useState(false);
    const handleSyncFromYouTube = async () => {
        if (!selectedNode || !selectedVideo || !ytConnected) return;
        if (!window.confirm(vn
            ? 'Lấy title/mô tả hiện tại từ YouTube và ghi đè lên bản ở đây (kể cả nếu bạn đang gõ dở)?'
            : "Pull the current title/description from YouTube and overwrite what's here (even if you're mid-edit)?")) return;
        setIsSyncingFromYT(true);
        setSaveMsg('');
        try {
            const sn = await fetchYouTubeVideoSnippet(selectedVideo.youtubeVideoId);
            if (!sn) { setSaveMsg(vn ? 'Không lấy được dữ liệu từ YouTube' : 'Could not fetch from YouTube'); return; }
            const uid = auth.currentUser?.uid;
            const patch = buildVideoUpdatePatch(selectedNode, selectedVideo.youtubeVideoId, { youtubeTitle: sn.title, youtubeDescription: sn.description, youtubeEditedAt: Date.now() });
            const { doc: fsDoc, updateDoc } = await import('firebase/firestore');
            await updateDoc(fsDoc(db, `users/${uid}/notebooks/${selectedNode._notebookId}/nodes/${selectedNode.id}`), patch);
            setAllNodes(prev => prev.map(n => n.id === selectedNode.id ? { ...n, ...patch } : n));
            setEditTitle(sn.title);
            setEditDesc(sn.description);
            setSaveMsg(vn ? '✓ Đã đồng bộ từ YouTube' : '✓ Synced from YouTube');
        } catch (e: any) {
            setSaveMsg(describeSaveError(e, vn ? 'Lỗi khi đồng bộ' : 'Sync failed', false));
        } finally {
            setIsSyncingFromYT(false);
        }
    };

    const handleRefreshStatus = async () => {
        if (!selectedNode || !selectedVideo || !ytConnected) return;
        const status = await getYouTubeVideoStatus(selectedVideo.youtubeVideoId);
        if (status) {
            const uid = auth.currentUser?.uid;
            const { doc: fsDoc, updateDoc } = await import('firebase/firestore');
            const patch = buildVideoUpdatePatch(selectedNode, selectedVideo.youtubeVideoId, { youtubeStatus: status });
            await updateDoc(fsDoc(db, `users/${uid}/notebooks/${selectedNode._notebookId}/nodes/${selectedNode.id}`), patch);
            setAllNodes(prev => prev.map(n => n.id === selectedNode.id ? { ...n, ...patch } : n));
        }
    };

    // One click does both: summarize the note AND fold in the transcript-based AI Check findings
    // (fetching the transcript / running the check inline if the user hasn't already, via the
    // Transcript panel) — reuses whatever's already in state instead of redoing work.
    const handleGenDesc = async () => {
        if (!selectedNode || !selectedVideo) return;
        setIsGenDesc(true);
        try {
            let transcriptText = transcript;
            let checkResult = transcriptCheck;
            // Chapters need the captions' timings. Transcripts cached before chapters existed hold
            // text only, so those are fetched once more (and cached with timings from then on).
            let timedTranscript = selectedVideo.youtubeTimedTranscript || '';
            if (!transcriptText || !timedTranscript) {
                try {
                    const result = await fetchYouTubeTranscript(selectedVideo.youtubeVideoId);
                    if ('text' in result) {
                        timedTranscript = result.timed;
                        if (!transcriptText) { transcriptText = result.text; setTranscript(result.text); }
                        await persistTranscriptFor(selectedNode, selectedVideo, transcriptText, result.timed);
                    }
                } catch { /* no captions yet — falls back to a plain content summary below */ }
            }
            if (transcriptText && !checkResult) {
                try {
                    checkResult = await checkTranscriptQuality(transcriptText, selectedNode.title || '', selectedNode.content || '', language, clipFocusInput.trim() || undefined);
                    setTranscriptCheck(checkResult);
                    await persistTranscriptCheck(checkResult);
                } catch { /* AI check failed — falls back to a plain content summary below */ }
            }

            const aiSummary = await generateYouTubeDescription(selectedNode.title || '', selectedNode.content || '', language,
                { title: editTitle || selectedVideo.youtubeTitle || selectedVideo.videoTitle, transcript: transcriptText || selectedVideo.youtubeTranscript || '' });
            const chapterTitle = branchesRef.current.find(b => b.id === selectedNode._branchId)?.title;
            const noteLink = resolveNoteLink(notebookPublishUrlRef.current, chapterTitle, selectedNode.id);
            const checkNote = checkResult ? `\n\n${formatAiCheckForDescription(checkResult, vn)}` : '';
            // Timestamp list (YouTube turns it into chapters). Best-effort: a short video, missing
            // captions or a failed call just leaves it out — the rest of the description is unaffected.
            let chapterNote = '';
            if (timedTranscript) {
                try {
                    const chapters = await generateVideoChapters(timedTranscript, selectedNode.title || '', language);
                    if (chapters.length) chapterNote = `\n\n${formatChaptersForDescription(chapters, vn)}`;
                } catch { /* leave chapters out */ }
            }
            const footer = [
                noteLink ? `📝 Notebook notes: ${noteLink}` : null,
                '🎓 Recorded with StudyBoard — studyboard.app',
            ].filter(Boolean).join('\n');
            // Regenerating the description used to silently wipe out a hashtag line added earlier
            // (this fully replaces the box, "Hashtag" only appends) — preserve one if it's there,
            // so doing the checklist steps out of order never loses work.
            setEditDesc(d => {
                const trimmed = d.trim();
                const lastLine = trimmed.split('\n').pop() || '';
                const existingHashtagLine = /^(#[^\s#]+\s*)+$/.test(lastLine) ? lastLine : '';
                // Links to other videos (spin-off clips, hand-picked related videos) are not something
                // the AI can regenerate — carry them over.
                const linkBlocks = extractLinkBlocks(d);
                return aiSummary + checkNote + chapterNote + '\n\n' + footer + (linkBlocks ? '\n\n' + linkBlocks : '') + (existingHashtagLine ? '\n\n' + existingHashtagLine : '');
            });
            setGenDescDone(true);
        } catch { } finally { setIsGenDesc(false); }
    };

    // Appends a hashtag line to whatever's currently in the description box — a fixed
    // "#LearningInPublic" tag, one derived from the book/course (notebook) name, and 2-4 AI-suggested
    // tags for the specific concept this note covers. The first two are deterministic (no AI call);
    // only the topical part needs the model to actually read the note.
    const handleAddHashtags = async () => {
        if (!selectedNode) return;
        setIsGenHashtags(true);
        try {
            const topicTags = await generateYouTubeTopicHashtags(selectedNode.title || '', selectedNode.content || '', language);
            const bookTag = toHashtag(selectedNode._notebookTitle);
            const seen = new Set<string>();
            const tags = ['LearningInPublic', bookTag, ...topicTags]
                .filter(Boolean)
                .filter(t => { const k = t.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
                .map(t => `#${t}`);
            setEditDesc(d => (d.trim() ? d.trimEnd() + '\n\n' : '') + tags.join(' '));
            setGenHashtagDone(true);
        } catch { } finally { setIsGenHashtags(false); }
    };

    // AI title can be based on the whole note or only on what was actually said in this video — a
    // video often covers just one piece of a long note, so the note text alone can be too broad.
    const [showTitleMenu, setShowTitleMenu] = useState(false);
    const [titleGenMsg, setTitleGenMsg] = useState('');
    useEffect(() => { setShowTitleMenu(false); setTitleGenMsg(''); }, [selectedVideoId]);
    const handleGenTitle = async (source: 'note' | 'transcript') => {
        if (!selectedNode || !selectedVideo) return;
        setShowTitleMenu(false); setTitleGenMsg(''); setIsGenTitle(true);
        try {
            let aiTitle = '';
            if (source === 'transcript') {
                let text = selectedVideo.youtubeTranscript || transcript || '';
                if (!text) {
                    // Only reached because the user explicitly chose the transcript option; cached afterwards.
                    const r = await fetchYouTubeTranscript(selectedVideo.youtubeVideoId);
                    if ('text' in r) {
                        text = r.text;
                        setTranscript(r.text);
                        await persistTranscriptFor(selectedNode, selectedVideo, r.text, r.timed);
                    } else {
                        setTitleGenMsg(r.error === 'no_captions'
                            ? (vn ? 'Video chưa có transcript (YouTube có thể chưa xử lý xong) — thử lại sau hoặc chọn "Từ nội dung note".' : 'No transcript yet (YouTube may still be processing) — try later or use "From the note".')
                            : r.error === 'auth_expired'
                                ? (vn ? 'Phiên YouTube hết hạn — bấm "Kết nối YouTube" ở góc trên phải rồi thử lại.' : 'YouTube session expired — click "Connect YouTube" at the top right and retry.')
                                : r.error);
                        return;
                    }
                }
                aiTitle = await generateTitleForNode(text.slice(0, 30000), null, null, language, 'thumbnail', 'transcript');
            } else {
                aiTitle = await generateTitleForNode(selectedNode.content || '', selectedNode.imageBase64 || null, selectedNode.imageMimeType || null, language, 'thumbnail');
            }
            if (aiTitle) { setEditTitle(aiTitle); setGenTitleDone(true); }
            else setTitleGenMsg(vn ? 'AI không tạo được tiêu đề — thử nguồn khác.' : 'AI could not produce a title — try the other source.');
        } catch (e: any) {
            setTitleGenMsg(`${vn ? 'Lỗi' : 'Error'}: ${e?.message || e}`);
        } finally { setIsGenTitle(false); }
    };

    // "Chạy tất cả" — the single entry point for the checklist below. Runs the 4 prep steps in the
    // one order that actually works (check needs a transcript; description folds in the check
    // result; hashtags go last) instead of leaving that order for the user to remember. Each step
    // is skipped if already done, so re-running after a partial run only fills in what's missing.
    // ── Unattended pipeline for the ticked videos ──
    // Everything "Chạy tất cả" does for one video, plus the save to YouTube, for each ticked video
    // in turn: transcript → AI check → title → description (summary, check, chapters, links) →
    // hashtags → push title and description to YouTube → record it on the note. Works on its own
    // arguments, never on the editor fields, so it does not matter which video is open meanwhile.
    type PipeItem = { key: string; title: string; status: 'waiting' | 'running' | 'done' | 'skipped' | 'error'; step?: string; note?: string };
    const [pipe, setPipe] = useState<{ items: PipeItem[]; running: boolean } | null>(null);
    const pipeStop = useRef(false);
    const runPipelineFor = async (node: EnhancedNode, video: YouTubeVideoEntry, onStep: (step: string) => void): Promise<void> => {
        const videoId = video.youtubeVideoId;
        let text = video.youtubeTranscript || '';
        let timed = video.youtubeTimedTranscript || '';
        if (!text || !timed) {
            onStep(vn ? 'lấy transcript' : 'transcript');
            const r = await fetchYouTubeTranscript(videoId);
            if ('text' in r) { text = r.text; timed = r.timed; await persistTranscriptFor(node, video, text, timed); }
            else if (!text) {
                const e: any = new Error(r.error === 'no_captions' ? (vn ? 'YouTube chưa có transcript' : 'no transcript on YouTube yet') : r.error === 'auth_expired' ? (vn ? 'phiên YouTube hết hạn' : 'YouTube session expired') : r.error);
                e.skip = r.error === 'no_captions'; e.fatal = r.error === 'auth_expired';
                throw e;
            }
        }
        onStep('AI check');
        const check = await checkTranscriptQuality(text, node.title || '', node.content || '', language, video.videoClipFocus || undefined);
        await persistTranscriptCheckFor(node, video, check);

        onStep(vn ? 'tiêu đề' : 'title');
        let title = '';
        try { title = await generateTitleForNode(text.slice(0, 30000), null, null, language, 'thumbnail', 'transcript'); } catch { /* keep the current title */ }
        if (!title) title = video.youtubeTitle || video.videoTitle || node.title || 'StudyBoard';
        title = title.replace(/[<>]/g, '').slice(0, 100);

        onStep(vn ? 'mô tả' : 'description');
        const summary = await generateYouTubeDescription(node.title || '', node.content || '', language, { title, transcript: text });
        let chapterNote = '';
        if (timed) {
            try {
                const chapters = await generateVideoChapters(timed, node.title || '', language);
                if (chapters.length) chapterNote = `\n\n${formatChaptersForDescription(chapters, vn)}`;
            } catch { /* leave chapters out */ }
        }
        const chapterTitle = branchesRef.current.find(b => b.id === node._branchId)?.title;
        const noteLink = resolveNoteLink(notebookPublishUrlRef.current, chapterTitle, node.id);
        const footer = [noteLink ? `📝 Notebook notes: ${noteLink}` : null, '🎓 Recorded with StudyBoard — studyboard.app'].filter(Boolean).join('\n');
        const linkBlocks = extractLinkBlocks(video.youtubeDescription || '');

        onStep('hashtag');
        let tagLine = '';
        try {
            const topicTags = await generateYouTubeTopicHashtags(node.title || '', node.content || '', language);
            const seen = new Set<string>();
            tagLine = ['LearningInPublic', toHashtag(node._notebookTitle), ...topicTags]
                .filter(Boolean)
                .filter(t => { const k = t.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
                .map(t => `#${t}`).join(' ');
        } catch { /* a description without hashtags is still fine */ }

        const description = (summary + `\n\n${formatAiCheckForDescription(check, vn)}` + chapterNote + '\n\n' + footer
            + (linkBlocks ? '\n\n' + linkBlocks : '') + (tagLine ? '\n\n' + tagLine : '')).replace(/[<>]/g, '').slice(0, 5000);

        onStep(vn ? 'lưu lên YouTube' : 'saving to YouTube');
        await updateYouTubeVideo(videoId, { title, description });
        const uidNow = auth.currentUser?.uid;
        const patch = buildVideoUpdatePatch(node, videoId, { youtubeTitle: title, videoTitle: title, youtubeDescription: description, youtubeEditedAt: Date.now() });
        await updateDoc(doc(db, `users/${uidNow}/notebooks/${node._notebookId}/nodes/${node.id}`), patch);
        setAllNodes(prev => prev.map(n => n.id === node.id ? { ...n, ...buildVideoUpdatePatch(n, videoId, { youtubeTitle: title, videoTitle: title, youtubeDescription: description, youtubeEditedAt: Date.now() }) } : n));
        // If this video is the one open in the editor, show what was just saved instead of stale text.
        if (selectedVideoIdRef.current === videoId) { setEditTitle(title); setEditDesc(description); setGenTitleDone(true); setGenDescDone(true); setGenHashtagDone(true); }
    };
    const handleRunPipelineBatch = async () => {
        if (!ytConnected) { setShowBatchConnectPrompt(true); return; }
        if (pipe?.running) return;
        const targets = Array.from(checkedVideoIds).map(key => {
            const [nodeId, videoId] = key.split('::');
            const node = allNodes.find(n => n.id === nodeId);
            const video = node ? getVideosForNode(node).find(v => v.youtubeVideoId === videoId) : undefined;
            return node && video ? { key, node, video } : null;
        }).filter((x): x is { key: string; node: EnhancedNode; video: YouTubeVideoEntry } => !!x);
        if (targets.length === 0) return;
        pipeStop.current = false;
        let items: PipeItem[] = targets.map(t => ({ key: t.key, title: t.video.youtubeTitle || t.video.videoTitle || t.node.title || t.video.youtubeVideoId, status: 'waiting' }));
        const update = (key: string, patch: Partial<PipeItem>) => { items = items.map(it => (it.key === key ? { ...it, ...patch } : it)); setPipe({ items, running: true }); };
        setPipe({ items, running: true });
        // Keep the screen (and so the tab) awake while it runs, where the browser allows it.
        let wake: any = null;
        try { wake = await (navigator as any).wakeLock?.request?.('screen'); } catch { /* not supported or not allowed */ }
        for (const t of targets) {
            if (pipeStop.current) break;
            update(t.key, { status: 'running', step: '' });
            try {
                await runPipelineFor(t.node, t.video, step => update(t.key, { step }));
                update(t.key, { status: 'done', step: '' });
            } catch (e: any) {
                const msg = e?.message === 'not_connected' ? (vn ? 'YouTube chưa kết nối' : 'YouTube not connected') : (e?.message || String(e));
                update(t.key, { status: e?.skip ? 'skipped' : 'error', note: msg, step: '' });
                // Out of quota or signed out: every later video would fail the same way.
                if (e?.fatal || e?.message === 'not_connected' || /quota/i.test(msg)) {
                    items = items.map(it => (it.status === 'waiting' ? { ...it, status: 'skipped', note: vn ? 'chưa chạy — dừng do lỗi ở trên' : 'not run — stopped by the error above' } : it));
                    break;
                }
            }
        }
        try { await wake?.release?.(); } catch { /* ignore */ }
        setPipe({ items, running: false });
    };

    const handleRunAllSteps = async () => {
        if (!selectedNode || !selectedVideo || isRunningAllSteps) return;
        setIsRunningAllSteps(true);
        try {
            if (!transcriptCheck) await handleFetchTranscript();
            if (!genTitleDone) await handleGenTitle('note');
            await handleGenDesc();
            await handleAddHashtags();
        } finally {
            setIsRunningAllSteps(false);
        }
    };

    // Editor settings behind the most recently SAVED (not merely previewed) thumbnail — see
    // ThumbnailEditorModal's handleSave, the only place onSettingsChange now fires from for an
    // already-published video. null = it's an uploaded image, or nothing saved yet.
    // thumbnailPushedAt is only bumped when `pushed` is true — i.e. this really went to YouTube
    // (via "Cập nhật YouTube" or the detail-panel push button below), not just saved/applied in-app.
    // Keeping the two separate means the "Push lúc" label stays honest instead of claiming a push
    // that never happened.
    const latestThumbSettingsRef = useRef<ThumbSettings | null>(null);
    const persistThumbSettings = async (settings: ThumbSettings | null, opts?: { pushed?: boolean }) => {
        if (!selectedNode || !selectedVideo) return;
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const targetNode = selectedNode;
        const videoId = selectedVideo.youtubeVideoId;
        const nodeRef = doc(db, `users/${uid}/notebooks/${targetNode._notebookId}/nodes/${targetNode.id}`);
        // The push time and the editor settings are written separately: the list relies on the
        // push time to reload YouTube's cached image, and it must not be lost when the (larger,
        // free-form) settings object is rejected.
        if (opts?.pushed) {
            const pushedAt = Date.now();
            // Local first — the thumbnail is already live on YouTube whatever the save does.
            setAllNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, ...buildVideoUpdatePatch(n, videoId, { thumbnailPushedAt: pushedAt }) } : n));
            try { await updateDoc(nodeRef, buildVideoUpdatePatch(targetNode, videoId, { thumbnailPushedAt: pushedAt })); }
            catch (e) { console.error('[thumbnail] saving push time failed', e); }
        }
        if (!settings) return;
        try {
            // Firestore rejects `undefined` anywhere in a value; the optional editor fields
            // (focal point, zoom, pen strokes…) are undefined until used. A JSON round trip drops them.
            const clean = JSON.parse(JSON.stringify(settings)) as Record<string, unknown>;
            await updateDoc(nodeRef, buildVideoUpdatePatch(targetNode, videoId, { thumbnailSettings: clean }));
            setAllNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, ...buildVideoUpdatePatch(n, videoId, { thumbnailSettings: clean }) } : n));
        } catch (e) { console.error('[thumbnail] saving editor settings failed', e); }
    };

    const handleUploadThumbnail = async (dataUrl: string) => {
        if (!selectedVideo || !ytConnected || !dataUrl) return;
        setIsUploadingThumb(true); setThumbMsg('');
        try {
            const [, mime, b64] = dataUrl.match(/^data:([^;]+);base64,(.+)$/) ?? [];
            await updateYouTubeVideo(selectedVideo.youtubeVideoId, { thumbnailBase64: b64, thumbnailMime: mime });
            await persistThumbSettings(latestThumbSettingsRef.current, { pushed: true });
            setThumbMsg(vn ? '✓ Thumbnail đã cập nhật!' : '✓ Thumbnail updated!');
        } catch (e: any) {
            setThumbMsg(`${vn ? 'Lỗi cập nhật thumbnail' : 'Failed to update thumbnail'}: ${e?.message === 'not_connected' ? (vn ? 'YouTube chưa kết nối hoặc hết phiên' : 'YouTube not connected or session expired') : (e?.message || e)}`);
        } finally { setIsUploadingThumb(false); }
    };

    const handleDownloadThumb = (dataUrl: string) => {
        if (!dataUrl || !selectedNode) return;
        const a = document.createElement('a');
        const slug = (selectedNode.title || 'thumbnail').replace(/[^a-z0-9]+/gi, '_').toLowerCase().slice(0, 50);
        a.href = dataUrl;
        a.download = `thumb_${slug}.png`;
        a.click();
    };

    // Double-click a column divider to auto-fit its width to the longest visible title
    const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const measureTextWidth = (text: string, font: string): number => {
        if (!measureCanvasRef.current) measureCanvasRef.current = document.createElement('canvas');
        const ctx = measureCanvasRef.current.getContext('2d');
        if (!ctx) return 0;
        ctx.font = font;
        return ctx.measureText(text).width;
    };
    const ROW_FONT = '600 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const handleAutoFitBranchCol = () => {
        const untitled = vn ? '(Không có tiêu đề)' : '(Untitled)';
        const maxW = branches.reduce((m, b) => Math.max(m, measureTextWidth(b.title || untitled, ROW_FONT)), 0);
        setBranchColWidth(Math.max(150, Math.min(380, Math.ceil(maxW) + 70)));
    };
    const handleAutoFitNoteCol = () => {
        const untitled = vn ? '(Không có tiêu đề)' : '(Untitled)';
        const maxW = branchNotes.reduce((m, n) => Math.max(m, measureTextWidth(n.title || untitled, ROW_FONT)), 0);
        setNoteColWidth(Math.max(200, Math.min(500, Math.ceil(maxW) + 110)));
    };

    const startColResize = (col: 'branch' | 'note') => (e: React.MouseEvent) => {
        e.preventDefault();
        dragStateRef.current = { col, startX: e.clientX, startW: col === 'branch' ? branchColWidth : noteColWidth };
        const onMove = (ev: MouseEvent) => {
            if (!dragStateRef.current) return;
            const dx = ev.clientX - dragStateRef.current.startX;
            const next = dragStateRef.current.startW + dx;
            if (dragStateRef.current.col === 'branch') setBranchColWidth(Math.max(150, Math.min(380, next)));
            else setNoteColWidth(Math.max(200, Math.min(500, next)));
        };
        const onUp = () => { dragStateRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    return (
        <>
        {/* Floating pill when minimized */}
        {minimized && (
            <div className="fixed bottom-5 right-5 z-[300] flex items-center gap-2.5 px-4 py-2.5 bg-gray-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl cursor-pointer select-none"
                onClick={() => setMinimized(false)}>
                {isBatchScoring ? (
                    <Loader2 size={13} className="animate-spin text-red-400 flex-shrink-0" />
                ) : (
                    <Youtube size={13} className="text-red-400 flex-shrink-0" />
                )}
                <span className="text-xs font-semibold">
                    {isBatchScoring
                        ? `${vn ? 'Đang chấm' : 'Scoring'} ${batchProgress?.done ?? 0}/${batchProgress?.total ?? 0}`
                        : 'YouTube Manager'}
                </span>
                {isBatchScoring && (
                    <button
                        onClick={e => { e.stopPropagation(); batchAbortRef.current = true; }}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                        title={vn ? 'Dừng' : 'Stop'}
                    >
                        <X size={12} />
                    </button>
                )}
                <Maximize2 size={11} className="text-gray-400 ml-1" />
            </div>
        )}

        <div className={`fixed inset-0 z-[200] bg-white dark:bg-slate-900 flex flex-col ${minimized ? 'hidden' : ''}`} style={{ paddingRight: showChat && !minimized ? 380 : 0 }}>

            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl">
                    <Youtube size={16} className="text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                    <h2 className="font-bold text-gray-900 dark:text-white text-sm">YouTube Manager</h2>
                    <p className="text-xs text-gray-400">{globalCounts.total} videos · {globalCounts.published} published</p>
                </div>
                <AutopilotButton language={language} />
                <button onClick={() => setShowThumbBatch(true)} title={vn ? 'Thumbnail mới hàng loạt: xem trước, duyệt, đặt lên YouTube' : 'New thumbnails in bulk: preview, review, push'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20">
                    <Sparkles size={12} /> Thumbnail
                </button>
                <button
                    onClick={() => setShowChat(v => !v)}
                    title={vn ? 'Trợ lý: hỏi đáp và nhờ sửa hàng loạt' : 'Assistant: ask questions and request bulk edits'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${showChat ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'}`}
                >
                    <MessageSquare size={12} /> {vn ? 'Trợ lý' : 'Assistant'}
                </button>
                <button
                    onClick={() => connectYouTube()}
                    title={ytConnected ? (vn ? 'Bấm để kết nối lại / đổi tài khoản' : 'Click to reconnect / switch account') : (vn ? 'Kết nối YouTube' : 'Connect YouTube')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${ytConnected
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/60 hover:bg-green-100 dark:hover:bg-green-900/30'
                        : 'bg-red-500 hover:bg-red-600 text-white'}`}
                >
                    <span className={`w-1.5 h-1.5 rounded-full ${ytConnected ? 'bg-green-500' : 'bg-white/80'}`} />
                    {ytConnected
                        ? (vn ? 'Đã kết nối YouTube' : 'YouTube connected')
                        : (vn ? 'Chưa kết nối — Kết nối YouTube' : 'Not connected — Connect YouTube')}
                </button>
                <button
                    onClick={() => setMinimized(true)}
                    title={vn ? 'Thu nhỏ — tiếp tục chấm ở nền' : 'Minimize — keep scoring in background'}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl relative"
                >
                    <Minimize2 size={15} className="text-gray-400" />
                    {isBatchScoring && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    )}
                </button>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl">
                    <X size={15} className="text-gray-400" />
                </button>
            </div>

            {/* View switcher — "Ghi chú" is the existing branch/note tree; "Video" is a flat,
                cross-note list of every video for browsing/publishing/flagging one by one; "Ý tưởng
                video" is a flat, cross-NOTEBOOK backlog of every scored note, for picking what to
                record next. */}
            <div className="flex gap-1 px-5 py-2 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                {(['notes', 'videos', 'ideas', 'map'] as const).map(m => (
                    <button
                        key={m}
                        onClick={() => setViewMode(m)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${viewMode === m ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    >
                        {m === 'notes' ? (vn ? 'Ghi chú' : 'Notes') : m === 'videos' ? (vn ? 'Video' : 'Videos') : m === 'ideas' ? (vn ? 'Ý tưởng video' : 'Video Ideas') : (vn ? 'Bản đồ' : 'Map')}
                    </button>
                ))}
                {orphans.length > 0 && (
                    <button onClick={() => { setOrphanMsg(''); setShowOrphans(true); }}
                        title={vn ? 'Video của những note đã bị xóa — gắn chúng vào note khác' : 'Videos of notes that were deleted — attach them to another note'}
                        className="ml-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50">
                        <AlertTriangle size={12} /> {vn ? `${orphans.length} video không còn note` : `${orphans.length} video${orphans.length === 1 ? '' : 's'} without a note`}
                    </button>
                )}
            </div>

            {scoreQuotaError && (
                <div className="flex items-center gap-3 px-5 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/40 flex-shrink-0">
                    <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-300 flex-1">{scoreQuotaError}</p>
                    <button onClick={() => setScoreQuotaError('')} className="text-amber-500 hover:text-amber-700 flex-shrink-0">
                        <X size={13} />
                    </button>
                </div>
            )}

            <div className="flex flex-1 overflow-hidden">
                {viewMode === 'notes' && (
                <>
                {/* Column 1 — Branch sidebar */}
                <div className="border-r border-gray-100 dark:border-slate-800 flex flex-col flex-shrink-0 bg-gray-50 dark:bg-slate-900/50" style={{ width: branchColWidth }}>

                    {/* Notebook selector */}
                    {allNotebooksMeta.length > 1 && (
                        <div className="px-3 py-2.5 border-b border-gray-100 dark:border-slate-800 flex items-center gap-1.5">
                            <select
                                value={activeNotebookMeta.id}
                                onChange={e => {
                                    const meta = allNotebooksMeta.find(m => m.id === e.target.value);
                                    if (meta) setActiveNotebookMeta(meta);
                                }}
                                className="flex-1 min-w-0 text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300"
                            >
                                {allNotebooksMeta.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                            </select>
                            <button
                                onClick={() => loadNodes(activeNotebookMeta)}
                                disabled={loadingNodes}
                                title={vn ? 'Tải lại danh sách' : 'Reload list'}
                                className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-40"
                            >
                                <RefreshCw size={13} className={loadingNodes ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    )}

                    <div className="px-3 py-2 border-b border-gray-100 dark:border-slate-800 flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={branches.length > 0 && checkedBranchIds.size === branches.length}
                            ref={el => { if (el) el.indeterminate = checkedBranchIds.size > 0 && checkedBranchIds.size < branches.length; }}
                            onChange={() => {
                                const allChecked = checkedBranchIds.size === branches.length;
                                setCheckedBranchIds(allChecked ? new Set() : new Set(branches.map(b => b.id)));
                                setCheckedNodeIds(allChecked ? new Set() : new Set(allNodes.map(n => n.id)));
                            }}
                            onClick={e => e.stopPropagation()}
                            className="w-3.5 h-3.5 rounded accent-red-500 cursor-pointer flex-shrink-0"
                        />
                        <button
                            onClick={() => {
                                const allSelected = branches.length > 0 && selectedBranchIds.size === branches.length;
                                setSelectedBranchIds(allSelected ? new Set() : new Set(branches.map(b => b.id)));
                                setSelectedNodeId(null);
                                setStatusFilter('all');
                            }}
                            title={vn ? 'Chọn/bỏ chọn tất cả nhánh (xem note)' : 'Select/deselect all branches (for viewing)'}
                            className={`text-[10px] font-semibold uppercase tracking-wider flex-1 text-left transition-colors ${branches.length > 0 && selectedBranchIds.size === branches.length ? 'text-red-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            {vn ? 'CHƯƠNG / NHÁNH' : 'CHAPTER / BRANCH'}
                            {selectedBranchIds.size > 1 && ` (${selectedBranchIds.size})`}
                        </button>
                        <button
                            onClick={() => setBranchSortMode(m => m === 'default' ? 'alpha' : 'default')}
                            title={vn
                                ? 'Mặc định: theo thứ tự sắp xếp trong canvas. Bấm để chuyển sang sắp theo tên (A-Z, hiểu số — 1.2 trước 1.10).'
                                : 'Default: canvas arrangement order. Click to sort by name instead (A-Z, numeric-aware — 1.2 before 1.10).'}
                            className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${branchSortMode === 'alpha' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'text-gray-300 hover:text-gray-500 dark:hover:text-gray-400'}`}
                        >
                            A-Z
                        </button>
                        {checkedBranchIds.size > 0 && !isBatchScoring && (
                            <button
                                onClick={() => setPendingScoreNodeIds([...checkedNodeIds])}
                                className="text-[10px] font-semibold text-red-500 hover:text-red-700 px-2 py-0.5 bg-red-50 dark:bg-red-900/20 rounded-lg whitespace-nowrap"
                            >
                                Score {checkedBranchIds.size}
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto hide-scrollbar">
                        {loadingNodes ? (
                            <div className="flex items-center justify-center h-24">
                                <Loader2 size={16} className="animate-spin text-gray-300" />
                            </div>
                        ) : branches.length === 0 ? (
                            <div className="flex items-center justify-center h-24 text-xs text-gray-400 px-3 text-center">
                                {vn ? 'Không có nhánh nào' : 'No branches found'}
                            </div>
                        ) : (branchSortMode === 'alpha'
                            ? [...branches].sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' }))
                            : branches
                        ).map(branch => {
                            const stats = branchStats(branch.id);
                            const isPlainSelected = selectedBranchIds.has(branch.id);
                            const isCheckedBranch = checkedBranchIds.has(branch.id);
                            // What actually drives column 2 right now (see viewBranchIds above) —
                            // used for the highlight so the sidebar never shows a branch as "active"
                            // while column 2 is actually showing something else.
                            const isViewed = viewBranchIds.has(branch.id);
                            return (
                                <div
                                    key={branch.id}
                                    onClick={(e) => {
                                        if (e.metaKey || e.ctrlKey || e.shiftKey) {
                                            setSelectedBranchIds(prev => {
                                                const s = new Set(prev);
                                                isPlainSelected ? s.delete(branch.id) : s.add(branch.id);
                                                return s;
                                            });
                                        } else {
                                            setSelectedBranchIds(new Set([branch.id]));
                                        }
                                        setSelectedNodeId(null);
                                        setStatusFilter('all');
                                    }}
                                    title={vn ? 'Cmd/Ctrl/Shift+Click để chọn nhiều nhánh' : 'Cmd/Ctrl/Shift+Click to select multiple branches'}
                                    className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-slate-800 transition-colors flex items-start gap-2 cursor-pointer ${isViewed ? 'bg-red-50 dark:bg-red-900/10 border-l-2 border-l-red-400' : isCheckedBranch ? 'bg-amber-50/60 dark:bg-amber-900/10' : 'hover:bg-white dark:hover:bg-slate-800/60'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isCheckedBranch}
                                        onChange={() => {
                                            const adding = !checkedBranchIds.has(branch.id);
                                            setCheckedBranchIds(prev => {
                                                const s = new Set(prev);
                                                adding ? s.add(branch.id) : s.delete(branch.id);
                                                return s;
                                            });
                                            const branchNoteIds = allNodes.filter(n => n._branchId === branch.id).map(n => n.id);
                                            setCheckedNodeIds(prev => {
                                                const s = new Set(prev);
                                                if (adding) branchNoteIds.forEach(id => s.add(id));
                                                else branchNoteIds.forEach(id => s.delete(id));
                                                return s;
                                            });
                                        }}
                                        onClick={e => e.stopPropagation()}
                                        className="w-3.5 h-3.5 rounded accent-red-500 cursor-pointer flex-shrink-0 mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-semibold leading-snug line-clamp-2 ${isViewed ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                            {branch.title || (vn ? '(Không có tiêu đề)' : '(Untitled)')}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[10px] text-gray-400">{stats.total} {vn ? 'ghi chú' : 'notes'}</span>
                                            {stats.withVideo > 0 && (
                                                <span className="text-[10px] text-red-400 font-medium flex items-center gap-0.5">
                                                    <Youtube size={9} /> {stats.withVideo}
                                                </span>
                                            )}
                                            {stats.total > 0 && (
                                                <span
                                                    title={vn ? `${stats.scored}/${stats.total} ghi chú đã chấm điểm video` : `${stats.scored}/${stats.total} notes video-scored`}
                                                    className={`text-[9px] font-bold px-1 rounded flex-shrink-0 ${stats.scoredPct === 100 ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : stats.scoredPct > 0 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-400'}`}
                                                >
                                                    {stats.scoredPct}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Drag handle 1 */}
                <div onMouseDown={startColResize('branch')} onDoubleClick={handleAutoFitBranchCol}
                    className="w-1 flex-shrink-0 cursor-col-resize hover:bg-red-400 active:bg-red-500 transition-colors bg-transparent group relative"
                    title={vn ? 'Kéo để chỉnh độ rộng · Double-click để vừa khít chữ' : 'Drag to resize · Double-click to auto-fit'}>
                    <div className="absolute inset-y-0 -left-1 -right-1" />
                </div>

                {/* Column 2 — Note list */}
                <div className="border-r border-gray-100 dark:border-slate-800 flex flex-col flex-shrink-0 bg-gray-50/50 dark:bg-slate-900/20" style={{ width: noteColWidth }}>

                    {/* Batch score bar */}
                    {viewBranchIds.size > 0 && (
                        <div className="flex-shrink-0 border-b border-gray-100 dark:border-slate-800">
                            <div className="flex items-center gap-2 px-3 py-2">
                                {/* Select-all checkbox */}
                                <input
                                    type="checkbox"
                                    checked={branchNotes.length > 0 && branchNotes.every(n => checkedNodeIds.has(n.id))}
                                    ref={el => { if (el) { const some = branchNotes.some(n => checkedNodeIds.has(n.id)); const all = branchNotes.every(n => checkedNodeIds.has(n.id)); el.indeterminate = some && !all; } }}
                                    onChange={() => {
                                        const allChecked = branchNotes.every(n => checkedNodeIds.has(n.id));
                                        setCheckedNodeIds(prev => {
                                            const s = new Set(prev);
                                            if (allChecked) branchNotes.forEach(n => s.delete(n.id));
                                            else branchNotes.forEach(n => s.add(n.id));
                                            return s;
                                        });
                                        if (viewBranchIds.size > 0) {
                                            setCheckedBranchIds(prev => {
                                                const s = new Set(prev);
                                                viewBranchIds.forEach(id => allChecked ? s.delete(id) : s.add(id));
                                                return s;
                                            });
                                        }
                                    }}
                                    onClick={e => e.stopPropagation()}
                                    className="w-3.5 h-3.5 rounded accent-red-500 cursor-pointer flex-shrink-0"
                                />
                                {isBatchScoring ? (
                                    <>
                                        <Loader2 size={11} className="animate-spin text-red-400 flex-shrink-0" />
                                        <span className="text-[11px] text-gray-500 flex-1">
                                            {vn ? 'Đang chấm' : 'Scoring'} {batchProgress?.done}/{batchProgress?.total}
                                        </span>
                                        <button onClick={() => { batchAbortRef.current = true; }} className="text-[10px] text-gray-400 hover:text-red-500">
                                            {vn ? 'Dừng' : 'Stop'}
                                        </button>
                                    </>
                                ) : checkedNodeIds.size > 0 ? (
                                    <>
                                        <span className="text-[11px] text-gray-500 flex-1">
                                            {checkedNodeIds.size} {vn ? 'đã chọn' : 'selected'}
                                        </span>
                                        {checkedNodeIds.size >= 2 && (
                                            <button onClick={handleSuggestCombined} disabled={isSuggestingCombined}
                                                className="flex items-center gap-1 text-[10px] font-semibold text-violet-500 hover:text-violet-700 px-2 py-0.5 bg-violet-50 dark:bg-violet-900/20 rounded-lg disabled:opacity-50">
                                                {isSuggestingCombined ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                                                {vn ? 'Gợi ý video tổng hợp' : 'Suggest combined video'}
                                            </button>
                                        )}
                                        <button onClick={() => setPendingScoreNodeIds([...checkedNodeIds])}
                                            className="text-[10px] font-semibold text-red-500 hover:text-red-700 px-2 py-0.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                            Score {checkedNodeIds.size}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-[11px] text-gray-400 flex-1">
                                            {branchAllNotes.filter(n => !n.videoAnalysisCheckedAt).length} {vn ? 'chưa chấm' : 'unscored'}
                                        </span>
                                        {branchAllNotes.length > 0 && (
                                            <button onClick={() => setPendingScoreNodeIds(allNodes.filter(n => n._branchId && viewBranchIds.has(n._branchId)).map(n => n.id))}
                                                className="text-[10px] font-semibold text-red-500 hover:text-red-700 px-2 py-0.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                                {vn ? 'Score branch' : 'Score branch'}
                                            </button>
                                        )}
                                    </>
                                )}
                                <button
                                    onClick={() => setShowScoringSettings(p => !p)}
                                    title={vn ? 'Tiêu chí chấm điểm' : 'Scoring criteria'}
                                    className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg transition-colors ${showScoringSettings ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                >
                                    <Settings size={11} />
                                </button>
                            </div>
                            {/* Scoring criteria settings panel */}
                            {showScoringSettings && (
                                <div className="px-3 pb-2.5 space-y-2 border-t border-gray-50 dark:border-slate-800 pt-2">
                                    <p className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                                        {vn ? 'Tiêu chí chấm điểm tùy chỉnh' : 'Custom scoring criteria'}
                                    </p>
                                    <textarea
                                        value={scoringCriteria}
                                        onChange={e => setScoringCriteria(e.target.value)}
                                        placeholder={vn
                                            ? 'Để trống để dùng tiêu chí mặc định (5 tiêu chí về visual, insight, scope…)\n\nHoặc nhập tiêu chí riêng, ví dụ:\n- Phù hợp với kênh toán cao cấp\n- Có ví dụ thực tế'
                                            : 'Leave empty to use the default 5 criteria (visual, insight, scope…)\n\nOr enter your own, e.g.:\n- Suitable for advanced math channel\n- Has real-world examples'}
                                        rows={4}
                                        className="w-full text-[11px] border border-gray-200 dark:border-slate-600 rounded-lg px-2.5 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-300 resize-none placeholder-gray-300 dark:placeholder-slate-600"
                                    />
                                    <div className="flex items-center justify-between">
                                        {scoringCriteria.trim() && (
                                            <span className="text-[10px] text-amber-500 font-medium">
                                                {vn ? '⚠ Đang dùng tiêu chí tùy chỉnh' : '⚠ Using custom criteria'}
                                            </span>
                                        )}
                                        <div className={`flex gap-2 ${scoringCriteria.trim() ? '' : 'ml-auto'}`}>
                                            {scoringCriteria.trim() && (
                                                <button
                                                    onClick={() => setScoringCriteria('')}
                                                    className="text-[10px] text-gray-400 hover:text-gray-600"
                                                >
                                                    {vn ? 'Xóa' : 'Clear'}
                                                </button>
                                            )}
                                            <button
                                                onClick={handleSaveScoringCriteria}
                                                disabled={isSavingCriteria}
                                                className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg disabled:opacity-40"
                                            >
                                                {isSavingCriteria ? <Loader2 size={9} className="animate-spin" /> : <Save size={9} />}
                                                {vn ? 'Lưu' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Orphan video scanner */}
                    {ytConnected && (
                        <div className="px-3 py-2 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                            <button
                                onClick={() => { setShowOrphanScanner(s => !s); if (!showOrphanScanner) { setOrphanScanDone(false); setOrphanMatches([]); } }}
                                className="flex items-center gap-1.5 text-[10px] font-semibold text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300"
                            >
                                <RefreshCw size={10} className={isScanningOrphans ? 'animate-spin' : ''} />
                                {vn ? 'Quét video chưa được link' : 'Scan unlinked videos'}
                            </button>
                            {showOrphanScanner && (
                                <div className="mt-2 flex flex-col gap-2">
                                    {!orphanScanDone && !isScanningOrphans && (
                                        <button
                                            onClick={handleScanOrphans}
                                            className="self-start text-[10px] font-semibold px-2 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
                                        >
                                            {vn ? 'Bắt đầu quét' : 'Start scan'}
                                        </button>
                                    )}
                                    {isScanningOrphans && (
                                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                                            <Loader2 size={10} className="animate-spin" />
                                            {vn ? 'Đang quét YouTube...' : 'Scanning YouTube...'}
                                        </div>
                                    )}
                                    {orphanScanDone && orphanMatches.length === 0 && (
                                        <p className="text-[10px] text-gray-400">{vn ? 'Không tìm thấy video chưa link nào.' : 'No unlinked videos found.'}</p>
                                    )}
                                    {orphanMatches.map(m => (
                                        <div key={m.video.id} className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-2">
                                            {m.video.thumbnail && (
                                                <img src={m.video.thumbnail} alt="" className="w-14 h-9 object-cover rounded flex-shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[10px] font-semibold text-gray-800 dark:text-white truncate">{m.video.title}</p>
                                                <p className="text-[9px] text-indigo-600 dark:text-indigo-400 truncate">→ {m.nodeTitle}</p>
                                            </div>
                                            <button
                                                onClick={() => handleConfirmOrphanLink(m)}
                                                disabled={linkingOrphan === m.video.id}
                                                className="flex-shrink-0 flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg disabled:opacity-40"
                                            >
                                                {linkingOrphan === m.video.id ? <Loader2 size={9} className="animate-spin" /> : <LinkIcon size={9} />}
                                                {vn ? 'Link' : 'Link'}
                                            </button>
                                        </div>
                                    ))}
                                    {orphanScanDone && (
                                        <button onClick={handleScanOrphans} className="self-start text-[10px] text-gray-400 hover:text-gray-600">
                                            {vn ? 'Quét lại' : 'Rescan'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Status filter tabs */}
                    <div className="flex gap-1 px-3 py-2 border-b border-gray-100 dark:border-slate-800 overflow-x-auto hide-scrollbar flex-shrink-0">
                        {(['all', 'none', 'private', 'scheduled', 'public', 'mixed'] as const).map(f => (
                            <button
                                key={f}
                                onClick={() => setStatusFilter(f)}
                                className={`text-[10px] font-semibold px-2 py-1 rounded-lg whitespace-nowrap flex-shrink-0 transition-colors ${statusFilter === f ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            >
                                {f === 'all' ? `${vn ? 'Tất cả' : 'All'} (${counts.all})` :
                                 f === 'none' ? `${vn ? 'Chưa có' : 'No video'} (${counts.none})` :
                                 f === 'private' ? `Private (${counts.private})` :
                                 f === 'scheduled' ? `Scheduled (${counts.scheduled})` :
                                 f === 'public' ? `Published (${counts.public})` :
                                 `${vn ? 'Hỗn hợp' : 'Mixed'} (${counts.mixed})`}
                            </button>
                        ))}
                    </div>

                    {/* Sort control */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide">{vn ? 'Sắp xếp' : 'Sort'}</span>
                        <select
                            value={sortMode}
                            onChange={e => setSortMode(e.target.value as SortMode)}
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 outline-none cursor-pointer"
                        >
                            <option value="default">{vn ? 'Mặc định' : 'Default'}</option>
                            <option value="quality-desc">{vn ? 'Video-Worthy: Cao → Thấp' : 'Video-Worthy: High → Low'}</option>
                            <option value="quality-asc">{vn ? 'Video-Worthy: Thấp → Cao' : 'Video-Worthy: Low → High'}</option>
                            <option value="ai-desc">{vn ? 'AI Check: Cao → Thấp' : 'AI Check: High → Low'}</option>
                            <option value="ai-asc">{vn ? 'AI Check: Thấp → Cao' : 'AI Check: Low → High'}</option>
                        </select>
                    </div>

                    {/* Note list */}
                    <div className="flex-1 overflow-y-auto hide-scrollbar">
                        {loadingNodes ? (
                            <div className="flex items-center justify-center h-32">
                                <Loader2 size={20} className="animate-spin text-gray-300" />
                            </div>
                        ) : viewBranchIds.size === 0 ? (
                            <div className="flex items-center justify-center h-32 text-xs text-gray-400">
                                {vn ? 'Chọn một nhánh' : 'Select a branch'}
                            </div>
                        ) : branchNotes.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-xs text-gray-400">
                                {vn ? 'Không có note nào' : 'No notes found'}
                            </div>
                        ) : sortedBranchNotes.map(node => {
                            const st = getNoteVideoStatus(node);
                            const cfg = STATUS_CONFIG[st];
                            const videoCount = getVideosForNode(node).length;
                            const isSelected = node.id === selectedNodeId;
                            const isChecked = checkedNodeIds.has(node.id);
                            const isScoring = scoringNodeIds.has(node.id);
                            return (
                                <div
                                    key={node.id}
                                    onClick={() => setSelectedNodeId(node.id)}
                                    className={`w-full text-left px-3 py-2.5 border-b border-gray-50 dark:border-slate-800 transition-colors flex items-start gap-2 cursor-pointer group ${isSelected ? 'bg-red-50 dark:bg-red-900/10' : isChecked ? 'bg-amber-50/60 dark:bg-amber-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'}`}
                                >
                                    {/* Row checkbox */}
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => {
                                            const adding = !checkedNodeIds.has(node.id);
                                            setCheckedNodeIds(prev => {
                                                const s = new Set(prev);
                                                adding ? s.add(node.id) : s.delete(node.id);
                                                return s;
                                            });
                                            if (node._branchId) {
                                                const branchNodeIds = allNodes.filter(n => n._branchId === node._branchId).map(n => n.id);
                                                const checkedAfter = new Set(checkedNodeIds);
                                                adding ? checkedAfter.add(node.id) : checkedAfter.delete(node.id);
                                                const allBranchChecked = branchNodeIds.every(id => checkedAfter.has(id));
                                                setCheckedBranchIds(prev => {
                                                    const s = new Set(prev);
                                                    allBranchChecked ? s.add(node._branchId!) : s.delete(node._branchId!);
                                                    return s;
                                                });
                                            }
                                        }}
                                        onClick={e => e.stopPropagation()}
                                        className="w-3.5 h-3.5 rounded accent-red-500 cursor-pointer flex-shrink-0 mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-semibold truncate ${isSelected ? 'text-red-700 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'}`}>
                                            {node.title || (vn ? '(Không có tiêu đề)' : '(Untitled)')}
                                        </p>
                                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                            <span className={`flex items-center gap-1 text-[10px] font-medium ${cfg.color}`}>
                                                {cfg.icon}
                                                {vn ? cfg.labelVn : cfg.label}
                                                {videoCount > 1 && ` ×${videoCount}`}
                                            </span>
                                            {node.videoWorthinessScore != null && (
                                                <span className={`text-[10px] font-bold px-1 rounded ${qualityColor(node.videoWorthinessScore)}`} title={vn ? 'Video-Worthy Score' : 'Video-Worthy Score'}>
                                                    {node.videoWorthinessScore}
                                                </span>
                                            )}
                                            {node.analysis?.score != null && (
                                                <span className={`text-[10px] font-bold px-1 rounded ${qualityColor(node.analysis.score)}`} title={vn ? 'AI Check Score' : 'AI Check Score'}>
                                                    AI·{node.analysis.score}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Per-row score button */}
                                    <button
                                        onClick={e => { e.stopPropagation(); handleScoreOne(node); }}
                                        disabled={isScoring || isBatchScoring}
                                        title={vn ? 'Score note này' : 'Score this note'}
                                        className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg transition-colors ${isScoring ? 'text-red-400' : 'opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
                                    >
                                        {isScoring
                                            ? <Loader2 size={11} className="animate-spin" />
                                            : <RefreshCw size={11} />}
                                    </button>
                                    {node.imageBase64 && (
                                        <img
                                            src={node.imageBase64.startsWith('http') ? node.imageBase64 : `data:${node.imageMimeType || 'image/jpeg'};base64,${node.imageBase64}`}
                                            className="w-12 h-8 object-cover rounded flex-shrink-0 border border-gray-100 dark:border-slate-700"
                                            alt=""
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Drag handle 2 */}
                <div onMouseDown={startColResize('note')} onDoubleClick={handleAutoFitNoteCol}
                    className="w-1 flex-shrink-0 cursor-col-resize hover:bg-red-400 active:bg-red-500 transition-colors bg-transparent relative"
                    title={vn ? 'Kéo để chỉnh độ rộng · Double-click để vừa khít chữ' : 'Drag to resize · Double-click to auto-fit'}>
                    <div className="absolute inset-y-0 -left-1 -right-1" />
                </div>
                </>
                )}

                {viewMode === 'videos' && (
                <div className="border-r border-gray-100 dark:border-slate-800 flex flex-col flex-shrink-0 bg-gray-50 dark:bg-slate-900/50 w-[420px]">
                    {allNotebooksMeta.length > 1 && (
                        <div className="px-3 py-2.5 border-b border-gray-100 dark:border-slate-800 flex items-center gap-1.5">
                            <select
                                value={activeNotebookMeta.id}
                                onChange={e => {
                                    const meta = allNotebooksMeta.find(m => m.id === e.target.value);
                                    if (meta) setActiveNotebookMeta(meta);
                                }}
                                className="flex-1 min-w-0 text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300"
                            >
                                {allNotebooksMeta.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                            </select>
                            <button
                                onClick={() => loadNodes(activeNotebookMeta)}
                                disabled={loadingNodes}
                                title={vn ? 'Tải lại danh sách' : 'Reload list'}
                                className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-40"
                            >
                                <RefreshCw size={13} className={loadingNodes ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    )}
                    <div className="flex gap-1 px-3 py-2 border-b border-gray-100 dark:border-slate-800 overflow-x-auto hide-scrollbar flex-shrink-0">
                        {(['all', 'private', 'scheduled', 'public', 'unchecked'] as const).map(f => (
                            <button
                                key={f}
                                onClick={() => setVideoTabFilter(f)}
                                className={`text-[10px] font-semibold px-2 py-1 rounded-lg whitespace-nowrap flex-shrink-0 transition-colors ${videoTabFilter === f ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            >
                                {f === 'all' ? `${vn ? 'Tất cả' : 'All'} (${videoTabCounts.all})` :
                                 f === 'private' ? `${vn ? 'Riêng tư' : 'Private'} (${videoTabCounts.private})` :
                                 f === 'scheduled' ? `${vn ? 'Đã lên lịch' : 'Scheduled'} (${videoTabCounts.scheduled})` :
                                 f === 'public' ? `${vn ? 'Đã đăng' : 'Published'} (${videoTabCounts.public})` :
                                 `${vn ? 'Chưa kiểm tra' : 'Not checked'} (${videoTabCounts.unchecked})`}
                            </button>
                        ))}
                    </div>

                    {/* Sort control */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide">{vn ? 'Sắp xếp' : 'Sort'}</span>
                        <select
                            value={videoSortMode}
                            onChange={e => setVideoSortMode(e.target.value as VideoSortMode)}
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 outline-none cursor-pointer"
                        >
                            <option value="time-desc">{vn ? 'Mới nhất trước' : 'Newest first'}</option>
                            <option value="time-asc">{vn ? 'Cũ nhất trước' : 'Oldest first'}</option>
                            <option value="default">{vn ? 'Mặc định' : 'Default'}</option>
                        </select>
                    </div>
                    {/* Bulk actions: one compact row of quiet chips; progress shows inside the chip */}
                    {flatVideos.length > 0 && (
                        <div className="px-3 py-1.5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                            <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-[10px] text-gray-400 uppercase tracking-wide mr-0.5">{vn ? 'Hàng loạt' : 'Bulk'}</span>
                                <button
                                    onClick={() => { if (!ytConnected) { setShowBatchConnectPrompt(true); return; } setShowBatchChapters(true); }}
                                    title={vn ? 'Mốc thời gian (chương YouTube) cho nhiều video: xem trước rồi mới áp dụng' : 'Chapters for many videos: preview, then apply'}
                                    className={BULK_CHIP}
                                >
                                    <Clock size={11} /> {vn ? 'Mốc thời gian' : 'Chapters'}
                                </button>
                                {videoTabCounts.unchecked > 0 && (
                                    <button onClick={handleBatchCheck} disabled={!!batchCheckState}
                                        title={vn ? 'AI kiểm tra các video chưa kiểm tra' : 'AI-check the videos not checked yet'}
                                        className={BULK_CHIP}>
                                        {batchCheckState
                                            ? <><Loader2 size={11} className="animate-spin" /> {batchCheckState.done}/{batchCheckState.total}</>
                                            : <><Sparkles size={11} /> {vn ? 'Kiểm tra' : 'Check'} <span className="text-gray-400 dark:text-gray-500 font-semibold">{videoTabCounts.unchecked}</span></>}
                                    </button>
                                )}
                                {flatVideos.some(x => (x.video.youtubeTranscript?.length || 0) > TRUNCATION_FIX_CAP && x.video.postCheckRecommendation) && (
                                    <button onClick={handleBatchRecheckAndPushYoutube} disabled={!!recheckPushState}
                                        title={vn ? 'Chấm lại bằng transcript đầy đủ (sửa lỗi bị cắt ở 8000 ký tự) rồi cập nhật mô tả thẳng lên YouTube' : 'Re-check with the full transcript (fixes the old 8000-char truncation bug) and push the description straight to YouTube'}
                                        className={BULK_CHIP}>
                                        {recheckPushState
                                            ? <><Loader2 size={11} className="animate-spin" /> {recheckPushState.done}/{recheckPushState.total}</>
                                            : <><RefreshCw size={11} /> {vn ? 'Chấm lại' : 'Recheck'}</>}
                                    </button>
                                )}
                            </div>
                            {batchCheckResult && (
                                <div className="mt-1.5 flex items-start justify-between gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                                    <span>
                                        {vn ? `Xong: ${batchCheckResult.ok} thành công` : `Done: ${batchCheckResult.ok} succeeded`}
                                        {batchCheckResult.noCaption > 0 && (vn ? `, ${batchCheckResult.noCaption} chưa có transcript (YouTube chưa xử lý xong, thử lại sau)` : `, ${batchCheckResult.noCaption} no transcript yet (YouTube still processing, retry later)`)}
                                        {batchCheckResult.failed > 0 && !batchCheckResult.quotaExceeded && !batchCheckResult.authExpired && (vn ? `, ${batchCheckResult.failed} lỗi` : `, ${batchCheckResult.failed} failed`)}
                                        {batchCheckResult.authExpired && (
                                            <span className="text-amber-500 dark:text-amber-400">
                                                {vn ? ' — Đã dừng: phiên YouTube hết hạn. Bấm "Kết nối YouTube" ở góc trên phải rồi chạy lại.' : ' — Stopped: YouTube session expired. Click "Connect YouTube" at the top right and re-run.'}
                                            </span>
                                        )}
                                        {batchCheckResult.quotaExceeded && (
                                            <span className="text-amber-500 dark:text-amber-400">
                                                {vn
                                                    ? ` — Đã dừng: hết quota YouTube API hôm nay (${batchCheckResult.failed} video chưa kiểm tra được). Quota được cấp lại theo ngày (khoảng nửa đêm giờ Mỹ, ~14h-15h giờ VN) — thử lại sau.`
                                                    : ` — Stopped: YouTube API daily quota exceeded (${batchCheckResult.failed} videos not checked). Quota resets daily — retry later.`}
                                            </span>
                                        )}
                                    </span>
                                    <button onClick={() => setBatchCheckResult(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"><X size={11} /></button>
                                </div>
                            )}
                            {recheckPushResult && (
                                <div className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                                    <div className="flex items-start justify-between gap-2">
                                        <span>
                                            {vn ? `Xong: ${recheckPushResult.ok} đã cập nhật lên YouTube` : `Done: ${recheckPushResult.ok} updated on YouTube`}
                                            {recheckPushResult.failed > 0 && (vn ? `, ${recheckPushResult.failed} lỗi` : `, ${recheckPushResult.failed} failed`)}
                                        </span>
                                        <button onClick={() => setRecheckPushResult(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"><X size={11} /></button>
                                    </div>
                                    {recheckPushResult.escalated.length > 0 && (
                                        <div className="mt-1 text-red-500 dark:text-red-400 font-semibold">
                                            ⛔ {vn ? 'Mới phát hiện cần quay lại:' : 'Newly flagged as needing a re-record:'} {recheckPushResult.escalated.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="px-3 py-1.5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0 flex items-center gap-2 text-[10px] text-gray-400">
                        <button onClick={handleSyncPlaylists} disabled={isSyncingPlaylists || !ytConnected}
                            title={vn ? 'Đọc mọi playlist trên kênh một lần để hiện tên playlist của từng video (tốn khoảng vài chục đơn vị quota)' : 'Read every playlist on the channel once to show each video\'s playlists (costs a few dozen quota units)'}
                            className="flex items-center gap-1 font-semibold text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 disabled:opacity-50">
                            {isSyncingPlaylists ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                            {vn ? 'Đồng bộ playlist' : 'Sync playlists'}
                        </button>
                        <span className="truncate">{playlistCache ? `${vn ? 'lúc' : 'at'} ${formatShortTime(playlistCache.syncedAt)}` : (vn ? 'chưa đồng bộ' : 'not synced yet')}</span>
                        {playlistSyncError && <span className="text-red-500 truncate" title={playlistSyncError}>{playlistSyncError}</span>}
                        {filteredFlatVideos.length > 0 && (
                            <button onClick={() => {
                                const keys = filteredFlatVideos.map(({ video, node }) => `${node.id}::${video.youtubeVideoId}`);
                                const all = keys.every(k => checkedVideoIds.has(k));
                                setCheckedVideoIds(all ? new Set() : new Set(keys));
                            }} className="ml-auto flex-shrink-0 font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
                                {filteredFlatVideos.every(({ video, node }) => checkedVideoIds.has(`${node.id}::${video.youtubeVideoId}`))
                                    ? (vn ? 'Bỏ chọn tất cả' : 'Unselect all')
                                    : (vn ? `Chọn tất cả (${filteredFlatVideos.length})` : `Select all (${filteredFlatVideos.length})`)}
                            </button>
                        )}
                    </div>
                    {checkedVideoIds.size > 0 && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/70 dark:bg-indigo-950/30 flex-shrink-0 overflow-x-auto hide-scrollbar">
                            <span className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300 whitespace-nowrap">{checkedVideoIds.size} {vn ? 'đã chọn' : 'selected'}</span>
                            <button onClick={() => setCheckedVideoIds(new Set())} title={vn ? 'Bỏ chọn' : 'Clear selection'} className="text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200 flex-shrink-0"><X size={12} /></button>
                            <span className="flex-1" />
                            <button
                                onClick={async () => {
                                    const items = Array.from(checkedVideoIds).map(key => {
                                        const [nodeId, videoId] = key.split('::');
                                        const e = flatVideos.find(x => x.node.id === nodeId && x.video.youtubeVideoId === videoId);
                                        return e ? { videoId, nodeId, notebookId: e.node._notebookId, title: e.video.youtubeTitle || e.video.videoTitle || e.node.title || '' } : null;
                                    }).filter(Boolean);
                                    setThumbBatchMsg('');
                                    try {
                                        await httpsCallable(functions, 'thumbnailQueueAdd')({ items, push: false, language });
                                        setShowThumbBatch(true);
                                    } catch (e: any) { setThumbBatchMsg(e?.message || String(e)); }
                                }}
                                title={vn ? 'Server viết chữ và dựng thumbnail mới cho các video đã tích; anh xem trước rồi mới đặt lên YouTube' : 'The server writes and draws new thumbnails for the ticked videos; review them before they go to YouTube'}
                                className={BULK_CHIP}
                            >
                                <ImageIcon size={11} /> {vn ? 'Thumbnail' : 'Thumbnails'}
                            </button>
                            {thumbBatchMsg && <span className="text-[10px] text-red-500 max-w-[200px] truncate" title={thumbBatchMsg}>{thumbBatchMsg}</span>}
                            <button
                                onClick={handleRunPipelineBatch}
                                disabled={!!pipe?.running}
                                title={vn ? 'Với từng video đã tích: lấy transcript, AI check, tạo tiêu đề, mô tả, hashtag rồi lưu lên YouTube — không cần bấm thêm gì' : 'For each ticked video: transcript, AI check, title, description, hashtags, then save to YouTube — nothing more to press'}
                                className="flex items-center gap-1 whitespace-nowrap flex-shrink-0 text-[11px] font-bold px-2 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-md transition-colors"
                            >
                                <Sparkles size={11} /> {vn ? 'Chạy hết → YouTube' : 'Run all → YouTube'}
                            </button>
                            <button
                                onClick={openCombinePanel}
                                disabled={combinableChecked < 2}
                                title={combinableChecked < checkedVideoIds.size ? (vn ? 'Chỉ ghép được video có bản sao lưu' : 'Only videos with a backup copy can be combined') : undefined}
                                className={BULK_CHIP}
                            >
                                <ListVideo size={11} /> {vn ? 'Ghép' : 'Combine'}
                            </button>
                        </div>
                    )}
                    <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
                        {loadingNodes ? (
                            <div className="flex items-center justify-center py-12"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
                        ) : filteredFlatVideos.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-12">{vn ? 'Không có video nào' : 'No videos here'}</p>
                        ) : filteredFlatVideos.map(({ video, node }) => {
                            const cfg = STATUS_CONFIG[video.youtubeStatus || 'private'];
                            const isSelected = selectedNodeId === node.id && selectedVideoId === video.youtubeVideoId;
                            const key = `${node.id}::${video.youtubeVideoId}`;
                            const isChecked = checkedVideoIds.has(key);
                            const canCombine = !!video.videoStoragePath;
                            return (
                                <div
                                    key={key}
                                    onClick={() => openVideoFromChat(video.youtubeVideoId)}
                                    onContextMenu={e => { e.preventDefault(); setRowMenu({ x: e.clientX, y: e.clientY, videoId: video.youtubeVideoId }); }}
                                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left cursor-pointer transition-colors ${isSelected ? 'bg-red-50 dark:bg-red-900/20 ring-1 ring-red-200 dark:ring-red-800' : 'hover:bg-gray-100 dark:hover:bg-slate-800'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        title={canCombine ? undefined : (vn ? 'Video này chưa có bản sao lưu nên không ghép được (các việc hàng loạt khác vẫn được)' : 'No backup copy, so it cannot be combined (other bulk actions still work)')}
                                        onChange={() => { /* handled in onClick, which knows whether Shift is held */ }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            // Shift-click ticks (or unticks) every row between the last clicked one and this one.
                                            const keys = filteredFlatVideos.map(x => `${x.node.id}::${x.video.youtubeVideoId}`);
                                            const at = keys.indexOf(key), from = lastCheckedKeyRef.current ? keys.indexOf(lastCheckedKeyRef.current) : -1;
                                            const range = e.shiftKey && from !== -1 ? keys.slice(Math.min(from, at), Math.max(from, at) + 1) : [key];
                                            setCheckedVideoIds(prev => {
                                                const s = new Set(prev);
                                                range.forEach(k => (isChecked ? s.delete(k) : s.add(k)));
                                                return s;
                                            });
                                            lastCheckedKeyRef.current = key;
                                        }}
                                        className="w-3.5 h-3.5 rounded accent-red-500 cursor-pointer flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                                    />
                                    <YtThumb video={video} size="mqdefault" className="w-16 h-9 rounded-md object-cover bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{video.youtubeTitle || video.videoTitle || video.youtubeVideoId}</p>
                                        <p className="text-[10px] text-gray-400 truncate">{node.title}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className={`flex items-center gap-1 text-[10px] font-medium ${cfg.color}`}>
                                                {cfg.icon} {vn ? cfg.labelVn : cfg.label}
                                            </span>
                                            {video.postCheckRecommendation != null && (
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                                    video.postCheckRecommendation === 're_record' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                                                    video.postCheckRecommendation === 'publish_with_correction' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                                                    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                }`} title={video.postCheckSummary || ''}>
                                                    {video.postCheckScore ?? '?'}
                                                </span>
                                            )}
                                            {isVideoOnGithub(node, video) && (
                                                <span className="flex items-center gap-0.5 text-[10px] font-semibold text-green-600 dark:text-green-400"
                                                    title={vn ? 'Link video này hiện trên trang GitHub của note' : "This video's link shows on the note's GitHub page"}>
                                                    <CheckCircle2 size={11} /> GitHub
                                                </span>
                                            )}
                                        </div>
                                        {playlistCache && (() => {
                                            const names = playlistCache.map[video.youtubeVideoId] ?? [];
                                            return names.length > 0
                                                ? <p className="text-[10px] truncate mt-0.5 text-gray-400" title={names.join(', ')}>📋 {names.join(', ')}</p>
                                                : <p className="text-[10px] truncate mt-0.5 text-amber-500 dark:text-amber-400">📋 {vn ? 'Chưa có playlist' : 'No playlist'}</p>;
                                        })()}
                                        {video.youtubeUploadedAt && (
                                            <p className="text-[10px] truncate mt-0.5 text-gray-400">🕒 {vn ? 'Tạo lúc' : 'Created'} {formatShortTime(video.youtubeUploadedAt)}</p>
                                        )}
                                        {video.youtubeEditedAt && (
                                            <p className="text-[10px] truncate mt-0.5 text-gray-400">✏️ {vn ? 'Sửa lúc' : 'Edited'} {formatShortTime(video.youtubeEditedAt)}</p>
                                        )}
                                    </div>
                                    <span
                                        onMouseEnter={e => { cancelHideDescPreview(); const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setDescPreview({ videoId: video.youtubeVideoId, top: r.top, left: r.right + 10 }); }}
                                        onMouseLeave={scheduleHideDescPreview}
                                        className="flex-shrink-0 p-1 rounded text-gray-300 dark:text-slate-600 hover:text-indigo-500 cursor-help"
                                    >
                                        <Eye size={14} />
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                )}

                {/* Column 3 — Detail panel. Shared by the Notes and Videos tabs (both key off
                    selectedNode) — explicitly excluded from the Ideas tab, which has its own
                    two-column list+detail layout using selectedIdeaId instead; without this guard
                    it rendered unconditionally (a pre-existing gap that only surfaced once a third
                    viewMode existed) and showed as a stray empty "Chọn một note" column. */}
                {viewMode !== 'ideas' && viewMode !== 'map' && (!selectedNode ? (
                    <div className="flex-1 flex items-center justify-center text-gray-300 dark:text-slate-600">
                        <div className="text-center">
                            <Youtube size={40} className="mx-auto mb-2 opacity-30" />
                            <p className="text-sm">{vn ? 'Chọn một note' : 'Select a note'}</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto hide-scrollbar p-5 flex flex-col gap-4">

                        {/* Note header */}
                        <div>
                            <div className="flex items-start gap-2">
                                <h3 className="font-bold text-gray-900 dark:text-white text-base leading-snug flex-1">{selectedNode.title}</h3>
                                {onOpenNote && (
                                    <button
                                        onClick={() => onOpenNote(selectedNode._notebookId, selectedNode.id)}
                                        title={vn ? 'Mở note đầy đủ' : 'Open full note'}
                                        className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800 transition-colors"
                                    >
                                        <Edit3 size={11} /> {vn ? 'Mở note' : 'Open note'}
                                    </button>
                                )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">{selectedNode._notebookTitle}</p>
                            {(() => {
                                const branchTitle = branches.find(b => b.id === selectedNode._branchId)?.title;
                                const ghLink = notebookPublishUrl ? resolveNoteLink(notebookPublishUrl, branchTitle, selectedNode.id) : null;
                                return ghLink ? (
                                    <a href={ghLink} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-1 font-mono break-all">
                                        <ExternalLink size={10} className="flex-shrink-0" />
                                        <span className="truncate max-w-[360px]">{ghLink}</span>
                                    </a>
                                ) : null;
                            })()}
                        </div>

                        {/* Video-worthiness score + AI Check — about the note itself, shown once regardless of video count */}
                        <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800 rounded-xl space-y-3">
                            {/* Video Idea Discovery — a note's overall video-worthiness plus every
                                distinct video idea found inside it (0..N, see analyzeVideoIdeas). */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                                        <Zap size={12} className="text-amber-500" />
                                        {vn ? 'Tiềm năng video' : 'Video potential'}
                                    </p>
                                    <button
                                        onClick={handleCheckQuality}
                                        disabled={isChecking}
                                        className="text-[11px] text-indigo-500 hover:text-indigo-700 font-medium flex items-center gap-1 disabled:opacity-40"
                                    >
                                        {isChecking ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                        {vn ? 'Kiểm tra' : 'Check'}
                                    </button>
                                </div>
                                {selectedNode.videoWorthinessScore != null ? (() => {
                                    const noteIdeas = videoIdeas.filter(v => v.nodeId === selectedNode.id && v.status !== 'dismissed');
                                    return (
                                        <div className="space-y-2">
                                            <div className="flex items-start gap-3">
                                                <span className={`text-lg font-black px-2.5 py-1 rounded-lg flex-shrink-0 ${qualityColor(selectedNode.videoWorthinessScore)}`}>
                                                    {selectedNode.videoWorthinessScore}
                                                </span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 flex-1 leading-relaxed">{selectedNode.videoWorthinessSummary}</p>
                                            </div>
                                            {noteIdeas.length === 0 ? (
                                                <p className="text-xs text-gray-400 italic px-1">
                                                    {vn ? 'Không tìm thấy ý tưởng video độc lập nào trong note này.' : 'No independently recordable video idea found in this note.'}
                                                </p>
                                            ) : (
                                                <div className="space-y-1.5">
                                                    {noteIdeas.map(idea => {
                                                        const statusInfo = IDEA_STATUS_INFO[idea.status];
                                                        return (
                                                            <div key={idea.id} className="px-3 py-2 bg-white dark:bg-slate-700/50 rounded-lg space-y-1">
                                                                <div className="flex items-start gap-2">
                                                                    <span className={`text-xs font-black px-1.5 py-0.5 rounded flex-shrink-0 ${qualityColor(idea.score)}`}>{idea.score}</span>
                                                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex-1">{idea.title}</p>
                                                                    <span className={`text-[9px] font-bold px-1 rounded flex-shrink-0 ${statusInfo.color}`}>{vn ? statusInfo.vi : statusInfo.en}</span>
                                                                </div>
                                                                {idea.angle && <p className="text-[11px] text-red-600 dark:text-red-300 italic pl-1">{idea.angle}</p>}
                                                                <div className="flex items-center gap-1.5 pl-1">
                                                                    {idea.status === 'suggested' && (
                                                                        <button onClick={() => handleUpdateIdeaStatus(idea.id, 'planned')}
                                                                            className="text-[10px] font-semibold text-blue-500 hover:text-blue-700 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 rounded">
                                                                            {vn ? 'Lên kế hoạch' : 'Plan'}
                                                                        </button>
                                                                    )}
                                                                    {idea.status !== 'dismissed' && (
                                                                        <button onClick={() => handleUpdateIdeaStatus(idea.id, 'dismissed')}
                                                                            className="text-[10px] font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-1.5 py-0.5">
                                                                            {vn ? 'Bỏ qua' : 'Dismiss'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })() : (
                                    <p className="text-xs text-gray-400">{vn ? 'Chưa kiểm tra' : 'Not checked yet'}</p>
                                )}
                            </div>

                            {/* AI Check Score (from Analyze Note) */}
                            {selectedNode.analysis != null && (
                                <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                        <Sparkles size={10} className="text-violet-400" />
                                        {vn ? 'AI Check' : 'AI Check'}
                                    </p>
                                    <div className="flex items-start gap-3">
                                        <span className={`text-base font-black px-2 py-0.5 rounded-lg flex-shrink-0 ${qualityColor(selectedNode.analysis.score)}`}>
                                            {selectedNode.analysis.score}
                                        </span>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 flex-1 leading-relaxed">
                                            {selectedNode.analysis.feedback}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Stuck/unfinished export job for this note, if any */}
                        {/* A job rendering normally right now — shown so it never looks like it vanished
                            (the attention banner only covers stuck/finished/failed jobs). */}
                        {runningJob && (() => {
                            const phases: Record<string, [string, string]> = {
                                downloading: ['đang tải video gốc', 'downloading'], normalizing: ['chuẩn hoá', 'normalizing'],
                                cutting: ['cắt đoạn', 'cutting'], merging: ['ghép', 'merging'], uploading: ['lưu kết quả', 'saving'],
                            };
                            const pct = runningJob.status === 'processing' && typeof runningJob.progress === 'number' ? runningJob.progress : null;
                            const label = runningJob.status === 'pending'
                                ? (vn ? 'Đang khởi động máy xử lý…' : 'Starting the worker…')
                                : pct != null
                                    ? `${pct}%${runningJob.phase && phases[runningJob.phase] ? ` · ${phases[runningJob.phase][vn ? 0 : 1]}` : ''}`
                                    : (vn ? 'Đang xử lý…' : 'Processing…');
                            return (
                                <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-900/10 px-4 py-3">
                                    <div className="flex items-center gap-2 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                                        <Loader2 size={12} className="animate-spin" />
                                        <span>{vn ? 'Video đang được xử lý' : 'Video is being processed'}</span>
                                        <span className="ml-auto tabular-nums font-bold">{label}</span>
                                    </div>
                                    {pct != null && (
                                        <div className="mt-2 h-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 overflow-hidden">
                                            <div className="h-full bg-indigo-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                                        </div>
                                    )}
                                    <p className="mt-1.5 text-[11px] text-indigo-500/80 dark:text-indigo-400/70">
                                        {vn ? 'Xong sẽ hiện nút "Tiếp tục upload" ở đây. Có thể đóng cửa sổ này, việc xử lý vẫn chạy trên server.' : 'A "Continue upload" button appears here when done. You can close this — processing keeps running on the server.'}
                                    </p>
                                </div>
                            );
                        })()}
                        {attentionJob && (
                            <div className={`flex flex-col gap-2 px-4 py-3 rounded-xl border ${attentionJob.status === 'done' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
                                <div className="flex items-center gap-2">
                                    {attentionJob.status === 'done'
                                        ? <CheckCircle2 size={14} className="text-blue-500 flex-shrink-0" />
                                        : <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />}
                                    <p className={`text-xs flex-1 font-medium ${attentionJob.status === 'done' ? 'text-blue-700 dark:text-blue-300' : 'text-amber-700 dark:text-amber-300'}`}>
                                        {attentionJob.status === 'done'
                                            ? (vn ? 'Video đã xử lý xong nhưng chưa upload lên YouTube' : 'Video finished processing but was never uploaded to YouTube')
                                            : (vn ? 'Video đang xử lý bị gián đoạn' : 'Video export was interrupted')}
                                    </p>
                                </div>
                                {jobActionError && <p className="text-[11px] text-red-500">{jobActionError}</p>}
                                <div className="flex items-center gap-2">
                                    {attentionJob.status === 'done' ? (
                                        <button onClick={() => handleContinueUpload(attentionJob)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold rounded-lg">
                                            <Upload size={11} /> {vn ? 'Tiếp tục upload' : 'Continue upload'}
                                        </button>
                                    ) : (
                                        <button onClick={() => handleResumeJob(attentionJob)} disabled={resumingJobId === attentionJob.id}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:cursor-default text-white text-xs font-bold rounded-lg tabular-nums">
                                            {resumingJobId === attentionJob.id ? (() => {
                                                const rp = resumeProgress;
                                                const phases: Record<string, [string, string]> = {
                                                    downloading: ['đang tải', 'downloading'], normalizing: ['chuẩn hoá', 'normalizing'],
                                                    cutting: ['cắt đoạn', 'cutting'], merging: ['ghép', 'merging'], uploading: ['lưu kết quả', 'saving'],
                                                };
                                                const label = !rp || !rp.status || rp.status === 'pending'
                                                    ? (vn ? 'Đang khởi động máy xử lý…' : 'Starting the worker…')
                                                    : rp.pct != null
                                                        ? `${vn ? 'Đang xử lý' : 'Processing'} ${rp.pct}%${rp.phase && phases[rp.phase] ? ` · ${phases[rp.phase][vn ? 0 : 1]}` : ''}`
                                                        : (vn ? 'Đang xử lý…' : 'Processing…');
                                                return <><Loader2 size={11} className="animate-spin" />{label}</>;
                                            })() : <><RefreshCw size={11} />{vn ? 'Tiếp tục xử lý' : 'Resume processing'}</>}
                                        </button>
                                    )}
                                    <button onClick={() => handleDismissJob(attentionJob)}
                                        className="px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xs font-semibold">
                                        {vn ? 'Bỏ qua' : 'Dismiss'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Video status + actions */}
                        {selectedNodeVideos.length === 0 ? (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-slate-800 rounded-xl">
                                    <Upload size={15} className="text-gray-400 flex-shrink-0" />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">{vn ? 'Note này chưa có video' : 'No video for this note yet'}</p>
                                    <button
                                        onClick={() => { setShowLinkInput(v => !v); setLinkError(''); }}
                                        className="px-3 py-1.5 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-lg flex items-center gap-1"
                                    >
                                        <LinkIcon size={11} /> {vn ? 'Link video' : 'Link video'}
                                    </button>
                                    <button
                                        onClick={() => onOpenUpload(selectedNode.id, selectedNode._notebookId)}
                                        className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg"
                                    >
                                        {vn ? 'Upload video' : 'Upload video'}
                                    </button>
                                </div>
                                {showLinkInput && (
                                    <div className="flex flex-col gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                        <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">{vn ? 'Paste URL hoặc video ID từ YouTube:' : 'Paste a YouTube URL or video ID:'}</p>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={linkInputValue}
                                                onChange={e => { setLinkInputValue(e.target.value); setLinkError(''); }}
                                                placeholder="https://youtu.be/... hoặc dXwYMc..."
                                                className="flex-1 text-xs px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-400"
                                                onKeyDown={e => e.key === 'Enter' && handleLinkVideo()}
                                                autoFocus
                                            />
                                            <button
                                                onClick={handleLinkVideo}
                                                disabled={isLinking || !linkInputValue.trim()}
                                                className="px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-xs font-bold rounded-lg flex items-center gap-1"
                                            >
                                                {isLinking ? <Loader2 size={12} className="animate-spin" /> : <LinkIcon size={12} />}
                                                {vn ? 'Xác nhận' : 'Link'}
                                            </button>
                                        </div>
                                        {linkError && <p className="text-xs text-red-500">{linkError}</p>}
                                    </div>
                                )}
                            </div>
                        ) : !selectedVideo ? (
                            <div className="flex flex-col gap-2">
                                {selectedNodeVideos.map(v => {
                                    const cfg = STATUS_CONFIG[v.youtubeStatus || 'private'];
                                    return (
                                        <button
                                            key={v.youtubeVideoId}
                                            onClick={() => setSelectedVideoId(v.youtubeVideoId)}
                                            className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl text-left transition-colors"
                                        >
                                            <div className="w-7 h-7 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                                <Youtube size={13} className="text-red-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{v.youtubeTitle || v.videoTitle || v.youtubeVideoId}</p>
                                                <span className={`flex items-center gap-1 text-[10px] font-medium ${cfg.color}`}>
                                                    {cfg.icon} {vn ? cfg.labelVn : cfg.label}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={() => onOpenUpload(selectedNode.id, selectedNode._notebookId)}
                                    className="flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors"
                                >
                                    <Plus size={12} /> {vn ? 'Thêm video khác' : 'Add another video'}
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                            {selectedNodeVideos.length > 1 && (
                                <button onClick={() => setSelectedVideoId(null)} className="self-start text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 font-medium mb-1">
                                    ← {vn ? 'Quay lại danh sách video' : 'Back to videos'}
                                </button>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                                {(() => {
                                    const st: VideoStatus = selectedVideo.youtubeStatus || 'private';
                                    const cfg = STATUS_CONFIG[st];
                                    return (
                                        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                                            st === 'public' ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' :
                                            st === 'scheduled' ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' :
                                            'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                                        }`}>
                                            {cfg.icon} {vn ? cfg.labelVn : cfg.label}
                                        </span>
                                    );
                                })()}
                                <a
                                    href={`https://www.youtube.com/watch?v=${selectedVideo.youtubeVideoId}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium"
                                >
                                    <ExternalLink size={11} /> {vn ? 'Xem trên YouTube' : 'View on YouTube'}
                                </a>
                                <button onClick={handleRefreshStatus} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                                    <RefreshCw size={10} /> Refresh
                                </button>
                                {/* Trim a part into a new video — needs the video's backup (YouTube cannot be downloaded from) */}
                                <button
                                    onClick={() => { setTrimPhase('idle'); setTrimError(''); setTrimProgress(null); setTrimTarget({ node: selectedNode, video: selectedVideo }); }}
                                    disabled={!selectedVideo.videoStoragePath}
                                    title={selectedVideo.videoStoragePath
                                        ? (vn ? 'Lấy một phần của video này làm video mới' : 'Make a new video from a part of this one')
                                        : (vn ? 'Video này không có bản sao lưu trong StudyBoard nên không cắt được' : 'This video has no backup in StudyBoard, so it cannot be trimmed')}
                                    className="flex items-center gap-1 text-xs font-medium text-indigo-500 hover:text-indigo-700 disabled:text-gray-300 dark:disabled:text-slate-600 disabled:cursor-not-allowed"
                                >
                                    <Scissors size={11} /> {vn ? 'Cắt đoạn' : 'Trim'}
                                </button>
                                {/* Ask the AI whether a part of this video could be its own video; opens the trim panel on its suggestions */}
                                <button
                                    onClick={() => { setTrimPhase('idle'); setTrimError(''); setTrimProgress(null); setTrimTarget({ node: selectedNode, video: selectedVideo, autoSuggest: true }); }}
                                    disabled={!selectedVideo.videoStoragePath || !selectedVideo.youtubeTimedTranscript}
                                    title={!selectedVideo.videoStoragePath
                                        ? (vn ? 'Video này không có bản sao lưu trong StudyBoard nên không cắt được' : 'This video has no backup in StudyBoard, so it cannot be trimmed')
                                        : !selectedVideo.youtubeTimedTranscript
                                            ? (vn ? 'Cần transcript: chạy "Transcript & AI Check" trước' : 'Needs the transcript: run "Transcript & AI Check" first')
                                            : (vn ? 'AI tìm đoạn có thể tách thành video riêng; bạn xem trước rồi mới cắt' : 'AI looks for parts that could be their own video; you preview before cutting')}
                                    className="flex items-center gap-1 text-xs font-medium text-indigo-500 hover:text-indigo-700 disabled:text-gray-300 dark:disabled:text-slate-600 disabled:cursor-not-allowed"
                                >
                                    <Sparkles size={11} /> {vn ? 'Gợi ý đoạn tách riêng' : 'Suggest a spin-off'}
                                </button>
                            </div>

                            {/* Checklist chuẩn bị video — gom Check/Tiêu đề/Mô tả/Hashtag vào 1 chỗ, theo đúng
                                thứ tự, thay vì rải rác 4 nút ở 4 nơi khác nhau mà người dùng phải tự nhớ thứ tự
                                (mô tả từng ghi đè hashtag nếu làm sai thứ tự — nay đã fix ở handleGenDesc). */}
                            <div className="mt-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/40 overflow-hidden flex-shrink-0">
                                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-slate-700">
                                    <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{vn ? '✅ Chuẩn bị video' : '✅ Prepare video'}</span>
                                    <button
                                        onClick={handleRunAllSteps}
                                        disabled={isRunningAllSteps}
                                        className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors"
                                    >
                                        {isRunningAllSteps ? <Loader2 size={11} className="animate-spin" /> : <span>▶</span>}
                                        {vn ? 'Chạy tất cả' : 'Run all'}
                                    </button>
                                </div>
                                <div className="divide-y divide-gray-200 dark:divide-slate-700">
                                    {/* 1. Transcript & AI Check */}
                                    <div className="flex items-center justify-between px-3 py-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="flex-shrink-0">{transcriptCheck ? '✅' : '⬜'}</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                                {vn ? 'Transcript & AI Check' : 'Transcript & AI Check'}
                                                {transcriptCheck && <span className="ml-1.5 text-[10px] font-bold text-gray-400">{transcriptCheck.score}/100</span>}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {selectedVideo.youtubeTranscript && (
                                                <button onClick={() => handleFetchTranscript(true)} disabled={isFetchingTranscript || isCheckingTranscript} title={vn ? 'Tải lại transcript từ YouTube (nếu phụ đề đã thay đổi)' : 'Re-fetch transcript from YouTube (if captions changed)'} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
                                                    <RefreshCw size={11} />
                                                </button>
                                            )}
                                            <button onClick={() => handleFetchTranscript()} disabled={isFetchingTranscript || isCheckingTranscript} className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-blue-500 hover:text-blue-700 disabled:opacity-50">
                                                {(isFetchingTranscript || isCheckingTranscript) ? <Loader2 size={10} className="animate-spin" /> : null}
                                                {transcriptCheck ? (vn ? 'Chạy lại' : 'Re-run') : (vn ? 'Chạy' : 'Run')}
                                            </button>
                                        </div>
                                    </div>
                                    {/* Note from video */}
                                    <div className="flex items-center justify-between px-3 py-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="flex-shrink-0">{selectedNode.noteFromVideoId === selectedVideo.youtubeVideoId ? '✅' : '⬜'}</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate" title={vn ? 'AI viết lại lời giảng trong video thành ghi chú, giữ nguyên cách bạn giải thích. Xem trước rồi mới lưu.' : 'Turns what you explained in the video into the written note, keeping your own explanation. Previewed before saving.'}>
                                                {vn ? 'Ghi chú từ video' : 'Note from video'}
                                                {noteGenMsg && <span className="ml-1.5 text-[10px] text-gray-400">{noteGenMsg}</span>}
                                            </span>
                                        </div>
                                        <button onClick={handleGenNoteFromVideo} disabled={isGenNote} className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-blue-500 hover:text-blue-700 disabled:opacity-40 flex-shrink-0">
                                            {isGenNote ? <Loader2 size={10} className="animate-spin" /> : null}
                                            {selectedNode.noteFromVideoId === selectedVideo.youtubeVideoId ? (vn ? 'Tạo lại' : 'Regenerate') : (vn ? 'Tạo' : 'Write')}
                                        </button>
                                    </div>
                                    {/* 2. Tiêu đề */}
                                    <div className="flex items-center justify-between px-3 py-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="flex-shrink-0">{genTitleDone ? '✅' : '⬜'}</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{vn ? 'Tiêu đề' : 'Title'}</span>
                                        </div>
                                        <div className="relative flex-shrink-0">
                                            <button onClick={() => setShowTitleMenu(v => !v)} disabled={isGenTitle} className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-blue-500 hover:text-blue-700 disabled:opacity-50">
                                                {isGenTitle ? <Loader2 size={10} className="animate-spin" /> : null}
                                                {genTitleDone ? (vn ? 'Tạo lại' : 'Regenerate') : (vn ? 'Tạo' : 'Generate')}
                                                <ChevronDown size={9} />
                                            </button>
                                            {showTitleMenu && (
                                                <>
                                                    <div className="fixed inset-0 z-[40]" onClick={() => setShowTitleMenu(false)} />
                                                    <div className="absolute right-0 top-full mt-1 z-[41] w-72 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl overflow-hidden">
                                                        <button onClick={() => handleGenTitle('note')} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">{vn ? 'Từ nội dung note' : 'From the note content'}</p>
                                                            <p className="text-[10px] text-gray-400 mt-0.5">{vn ? 'Dùng toàn bộ note — hợp khi video nói hết note' : 'Uses the whole note — best when the video covers all of it'}</p>
                                                        </button>
                                                        <button
                                                            onClick={() => handleGenTitle('transcript')}
                                                            disabled={!selectedVideo.youtubeTranscript && !transcript && !ytConnected}
                                                            className="w-full text-left px-3 py-2.5 border-t border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-40"
                                                        >
                                                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">{vn ? 'Từ transcript (những gì đã nói)' : 'From the transcript (what was said)'}</p>
                                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                                {selectedVideo.youtubeTranscript || transcript
                                                                    ? (vn ? 'Đã có transcript — không tốn quota' : 'Transcript already saved — no quota used')
                                                                    : (vn ? 'Chưa có transcript: sẽ tải từ YouTube một lần (~250 đơn vị quota)' : 'No transcript yet: fetched from YouTube once (~250 quota units)')}
                                                            </p>
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {titleGenMsg && <p className="px-3 pb-2 text-[11px] text-red-500">{titleGenMsg}</p>}
                                    {/* 3. Mô tả */}
                                    <div className="flex items-center justify-between px-3 py-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="flex-shrink-0">{genDescDone ? '✅' : '⬜'}</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{vn ? 'Mô tả' : 'Description'}</span>
                                        </div>
                                        <button onClick={handleGenDesc} disabled={isGenDesc} className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-blue-500 hover:text-blue-700 disabled:opacity-50 flex-shrink-0">
                                            {isGenDesc ? <Loader2 size={10} className="animate-spin" /> : null}
                                            {genDescDone ? (vn ? 'Tạo lại' : 'Regenerate') : (vn ? 'Tạo' : 'Generate')}
                                        </button>
                                    </div>
                                    {/* 4. Hashtag */}
                                    <div className="flex items-center justify-between px-3 py-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="flex-shrink-0">{genHashtagDone ? '✅' : '⬜'}</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{vn ? 'Hashtag' : 'Hashtags'}</span>
                                        </div>
                                        <button onClick={handleAddHashtags} disabled={isGenHashtags} className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-blue-500 hover:text-blue-700 disabled:opacity-50 flex-shrink-0">
                                            {isGenHashtags ? <Loader2 size={10} className="animate-spin" /> : null}
                                            {genHashtagDone ? (vn ? 'Thêm lại' : 'Re-add') : (vn ? 'Thêm' : 'Add')}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Transcript + AI Check result section — also shown for a video that already has a
                                persisted check result but no cached transcript text yet (checked before transcript
                                caching existed), so its "3 góc nhìn" breakdown isn't hidden. */}
                            {(transcript || transcriptError || transcriptCheck) && (
                                <div className="mt-2 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 overflow-hidden flex-shrink-0">
                                    <div className="flex items-center justify-between px-3 py-2 border-b border-blue-100 dark:border-blue-800/50">
                                        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">📝 {vn ? 'Transcript YouTube' : 'YouTube Transcript'}</span>
                                        <div className="flex items-center gap-2">
                                            {transcript && !transcriptCheck && (
                                                <button onClick={() => handleCheckTranscript()} disabled={isCheckingTranscript} className="flex items-center gap-1 px-2 py-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-xs font-bold rounded-lg">
                                                    {isCheckingTranscript ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                                                    {vn ? 'AI Check' : 'AI Check'}
                                                </button>
                                            )}
                                            <button onClick={() => setShowTranscript(v => !v)} className="text-xs text-blue-500 hover:text-blue-700">
                                                {showTranscript ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                    {transcript && (
                                        <div className="px-3 pt-2 flex items-center gap-1.5">
                                            <input
                                                type="text"
                                                value={clipFocusInput}
                                                onChange={e => setClipFocusInput(e.target.value)}
                                                onBlur={async () => {
                                                    if (!selectedNode || !selectedVideo) return;
                                                    if (clipFocusInput === (selectedVideo.videoClipFocus || '')) return;
                                                    setIsSavingClipFocus(true);
                                                    const uid = auth.currentUser?.uid;
                                                    const patch = buildVideoUpdatePatch(selectedNode, selectedVideo.youtubeVideoId, { videoClipFocus: clipFocusInput.trim() || undefined });
                                                    await updateDoc(doc(db, `users/${uid}/notebooks/${selectedNode._notebookId}/nodes/${selectedNode.id}`), patch);
                                                    setAllNodes(prev => prev.map(n => n.id === selectedNode.id ? { ...n, ...patch } : n));
                                                    setIsSavingClipFocus(false);
                                                }}
                                                placeholder={vn ? 'Clip này giải thích gì? (tùy chọn — thu hẹp phạm vi AI check)' : 'What does this clip explain? (optional — narrows what AI check grades)'}
                                                className="flex-1 text-[11px] px-2 py-1 rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-blue-400"
                                            />
                                            {isSavingClipFocus && <Loader2 size={11} className="animate-spin text-gray-300 flex-shrink-0" />}
                                        </div>
                                    )}
                                    {transcriptError && <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400">{transcriptError}</p>}
                                    {transcript && showTranscript && (
                                        <p className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 leading-relaxed max-h-40 overflow-y-auto whitespace-pre-wrap">{transcript}</p>
                                    )}
                                    {transcriptCheck && (
                                        <div className="px-3 pb-3 pt-2 space-y-2 border-t border-blue-100 dark:border-blue-800/50">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                                    transcriptCheck.recommendation === 're_record' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                                                    transcriptCheck.recommendation === 'publish_with_correction' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                                                    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                }`}>
                                                    {transcriptCheck.recommendation === 're_record' ? (vn ? '⛔ Cần quay lại' : '⛔ Re-record')
                                                        : transcriptCheck.recommendation === 'publish_with_correction' ? (vn ? '✓ Publish (kèm correction)' : '✓ Publish with correction')
                                                        : (vn ? '✓ Publish' : '✓ Publish')}
                                                </span>
                                                <span className="text-[10px] text-gray-400">{transcriptCheck.score}/100</span>
                                            </div>
                                            {transcriptCheck.checkSummary && (
                                                <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed italic">"{transcriptCheck.checkSummary}"</p>
                                            )}
                                            {transcriptCheck.passes && transcriptCheck.passes.length > 0 && (
                                                <div className="space-y-1 rounded-lg bg-gray-50 dark:bg-slate-800/60 p-2">
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{vn ? '3 góc nhìn' : '3 angles'}</p>
                                                    {transcriptCheck.passes.map((p, i) => (
                                                        <div key={i} className="flex items-center justify-between gap-2">
                                                            <span className={`text-[11px] ${p.hasMajorError ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`} title={p.checkSummary}>
                                                                {p.hasMajorError ? '⛔ ' : ''}{p.label}
                                                            </span>
                                                            <span className={`text-[11px] font-bold flex-shrink-0 ${p.hasMajorError ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{p.score}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {transcriptCheck.strengths.length > 0 && (
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider">{vn ? 'Điểm đúng' : "What's right"}</p>
                                                    {transcriptCheck.strengths.map((s, i) => (
                                                        <p key={i} className="text-xs leading-relaxed text-green-700 dark:text-green-400">✓ {s}</p>
                                                    ))}
                                                </div>
                                            )}
                                            {(() => {
                                                const mistakes = transcriptCheck.findings.filter(f => f.severity === 'major_error' || f.severity === 'minor_correction');
                                                return (
                                                    <div className="space-y-1">
                                                        <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">{vn ? 'Ý sai' : 'Incorrect points'}</p>
                                                        {mistakes.length > 0
                                                            ? mistakes.map((f, i) => {
                                                                const style = f.severity === 'major_error' ? { icon: '⛔', color: 'text-red-700 dark:text-red-400' } : { icon: '🟡', color: 'text-amber-700 dark:text-amber-400' };
                                                                return <p key={i} className={`text-xs leading-relaxed ${style.color}`}>{style.icon} {f.note}</p>;
                                                            })
                                                            : <p className="text-xs leading-relaxed text-green-700 dark:text-green-400">✓ {vn ? 'Không có — không phát hiện lỗi lớn hay lỗi nhỏ nào.' : 'None — no major or minor errors found.'}</p>}
                                                    </div>
                                                );
                                            })()}
                                            {transcriptCheck.findings.filter(f => f.severity === 'clarification' || f.severity === 'optional_improvement').length > 0 && (
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">{vn ? 'Ý đúng, có thể giải thích rõ hơn' : 'Right idea, could be clearer'}</p>
                                                    {transcriptCheck.findings.filter(f => f.severity === 'clarification' || f.severity === 'optional_improvement').map((f, i) => (
                                                        <p key={i} className="text-xs leading-relaxed text-blue-700 dark:text-blue-400">💡 {f.note}</p>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Re-check / Add-to-description now live in the "Chuẩn bị video" checklist above —
                                                Argue with AI stays here since it's a follow-up on these specific results, not a prep step. */}
                                            {transcript && (
                                                <button
                                                    onClick={() => setShowDiscussPanel(true)}
                                                    className="flex items-center gap-1 text-[10px] text-indigo-500 hover:text-indigo-700 font-semibold"
                                                >
                                                    <MessageSquare size={10} /> {vn ? 'Tranh luận với AI' : 'Argue with AI'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Live "AI Checkpoint" recording checks — the real-time per-segment check that ran
                                DURING screen recording (not Auto-Pace, and distinct from the transcript-based
                                AI Check above). Currently only persisted for a note's first/slot-0 video —
                                see docs note in YouTubeUploadModal.tsx's saveSegmentsNow. */}
                            {!!selectedVideo?.videoSegments?.length && (
                                <div className="mt-2 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/10 overflow-hidden flex-shrink-0">
                                    <button
                                        onClick={() => setShowRecordingChecks(v => !v)}
                                        className="w-full flex items-center justify-between px-3 py-2 border-b border-indigo-100 dark:border-indigo-800/50"
                                    >
                                        <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                                            🎙️ {vn ? 'Check lúc quay' : 'Live recording checks'} · {selectedVideo.videoSegments!.length} {vn ? 'đoạn' : 'segments'}
                                        </span>
                                        {showRecordingChecks ? <ChevronUp size={14} className="text-indigo-500" /> : <ChevronDown size={14} className="text-indigo-500" />}
                                    </button>
                                    {showRecordingChecks && (
                                        <div className="px-3 py-2 space-y-2 max-h-64 overflow-y-auto">
                                            {selectedVideo.videoSegments!.map((seg) => {
                                                const fmt = (s?: number) => s == null ? '' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
                                                const style = seg.status === 'needs_redo' ? { icon: '⛔', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' }
                                                    : seg.status === 'warning' ? { icon: '🟡', color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30' }
                                                    : seg.status === 'good' ? { icon: '✓', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30' }
                                                    : { icon: '○', color: 'text-gray-400', bg: 'bg-gray-100 dark:bg-slate-800' };
                                                return (
                                                    <div key={seg.index} className="rounded-lg bg-white dark:bg-slate-800/60 p-2 space-y-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${style.bg} ${style.color}`}>{style.icon} {fmt(seg.startSecs)}–{fmt(seg.endSecs)}</span>
                                                            {seg.score != null && <span className="text-[10px] text-gray-400">{seg.score}/100</span>}
                                                            {seg.overridden && <span className="text-[10px] text-gray-400 italic">{vn ? '(đã bỏ qua)' : '(overridden)'}</span>}
                                                        </div>
                                                        {seg.summary && <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{seg.summary}</p>}
                                                        {seg.mainIssue && <p className={`text-xs leading-relaxed ${style.color}`}>{style.icon} {seg.mainIssue}</p>}
                                                        {seg.suggestedCorrection && <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed italic">→ {seg.suggestedCorrection}</p>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        )}

                        {/* Playlist manager — only when a video is selected */}
                        {selectedVideo && (
                            <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden flex-shrink-0">
                                <button
                                    className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                                    onClick={async () => {
                                        if (!playlistsLoaded && playlistCache?.playlists) {
                                            // Already fetched by the last sync — no YouTube call needed.
                                            setPlaylists(playlistCache.playlists);
                                            setPlaylistsLoaded(true);
                                        } else if (!playlistsLoaded) {
                                            setIsLoadingPlaylists(true);
                                            setPlaylistMsg('');
                                            try {
                                                const list = await fetchYouTubePlaylists();
                                                setPlaylists(list);
                                                setPlaylistsLoaded(true);
                                            } catch (e: any) {
                                                // Not marked as loaded, so the next open retries instead of showing a false "no playlists".
                                                setPlaylistMsg(`${vn ? 'Không tải được danh sách playlist' : 'Could not load playlists'}: ${playlistErrText(e)}`);
                                            } finally { setIsLoadingPlaylists(false); }
                                        }
                                        setShowCreatePlaylist(v => !v);
                                    }}
                                >
                                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                                        <ListVideo size={13} className="text-red-500" />
                                        {vn ? 'Thêm vào playlist' : 'Add to playlist'}
                                        {memberIds !== undefined && (
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${memberIds.length > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400'}`}>
                                                {memberIds.length > 0 ? (vn ? `Đang trong ${memberIds.length} playlist` : `In ${memberIds.length} playlist${memberIds.length > 1 ? 's' : ''}`) : (vn ? 'Chưa thuộc playlist nào' : 'Not in any playlist')}
                                            </span>
                                        )}
                                    </span>
                                    {isLoadingPlaylists
                                        ? <Loader2 size={12} className="animate-spin text-gray-400" />
                                        : <ChevronDown size={12} className={`text-gray-400 transition-transform ${showCreatePlaylist ? 'rotate-180' : ''}`} />
                                    }
                                </button>
                                {showCreatePlaylist && (
                                    <div className="px-3 py-2.5 space-y-2 border-t border-gray-100 dark:border-slate-700">
                                        {playlistMsg && (
                                            <p className={`text-[11px] font-medium ${playlistMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{playlistMsg}</p>
                                        )}
                                        {memberIds === undefined && (
                                            <p className="text-[11px] text-gray-400">{vn ? 'Chưa biết video này đang nằm trong playlist nào — bấm "Đồng bộ playlist" ở cột trái (một lần cho mọi video).' : 'Which playlists this video is in is unknown — press "Sync playlists" in the left column (once, for all videos).'}</p>
                                        )}
                                        {/* AI suggestion — the user still adds each playlist themselves */}
                                        {playlists.length > 0 && (
                                            <div className="rounded-lg border border-indigo-100 dark:border-indigo-900/50 bg-indigo-50/50 dark:bg-indigo-900/10 px-2.5 py-2 space-y-1.5">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-300">{vn ? 'Gợi ý playlist bằng AI' : 'AI playlist suggestion'}</span>
                                                    <button onClick={handleSuggestPlaylists} disabled={isSuggestingPl} className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-300 hover:underline disabled:opacity-40">
                                                        {isSuggestingPl && <Loader2 size={10} className="animate-spin" />}
                                                        {plSuggest?.videoId === selectedVideo.youtubeVideoId ? (vn ? 'Gợi ý lại' : 'Suggest again') : (vn ? 'Gợi ý' : 'Suggest')}
                                                    </button>
                                                </div>
                                                {plSuggest?.videoId === selectedVideo.youtubeVideoId && (
                                                    <>
                                                        {plSuggest.suggestions.length === 0 && (
                                                            <p className="text-[11px] text-gray-500">{vn ? 'Không có playlist hiện có nào thật sự hợp với video này.' : 'None of the existing playlists really fits this video.'}</p>
                                                        )}
                                                        {plSuggest.suggestions.map(sg => {
                                                            const pl = playlists.find(p => p.id === sg.id);
                                                            if (!pl) return null;
                                                            const isMember = !!memberIds?.includes(pl.id);
                                                            return (
                                                                <div key={sg.id} className="flex items-start gap-2">
                                                                    {isMember ? (
                                                                        <span className="flex-shrink-0 w-14 text-center text-[11px] py-0.5 text-green-600 dark:text-green-400 font-semibold">{vn ? 'Đã có' : 'In list'}</span>
                                                                    ) : (
                                                                        <button onClick={() => handleAddToPlaylist(pl)} disabled={addingToPlaylistId === pl.id || !ytConnected}
                                                                            className="flex-shrink-0 w-14 flex justify-center text-[11px] py-0.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded font-semibold disabled:opacity-40">
                                                                            {addingToPlaylistId === pl.id ? <Loader2 size={9} className="animate-spin" /> : (vn ? 'Thêm' : 'Add')}
                                                                        </button>
                                                                    )}
                                                                    <div className="min-w-0 flex-1">
                                                                        <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-200 truncate">
                                                                            {pl.title}
                                                                            <span className="ml-1.5 text-[10px] font-normal text-gray-400">{sg.role === 'main' ? (vn ? 'chính' : 'main') : (vn ? 'phụ' : 'also')}</span>
                                                                        </p>
                                                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">{sg.reason}</p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {plSuggest.missingTopic && (
                                                            <p className="text-[11px] text-amber-600 dark:text-amber-400">{vn ? 'Chưa có playlist cho chủ đề' : 'No playlist yet for'}: {plSuggest.missingTopic}</p>
                                                        )}
                                                        <p className="text-[10px] text-gray-400">{plSuggest.source === 'transcript' ? (vn ? 'Dựa trên transcript của video.' : "Based on the video's transcript.") : (vn ? 'Dựa trên ghi chú (video chưa có transcript lưu sẵn — chạy "Transcript & AI Check" để gợi ý chính xác hơn).' : 'Based on the note (no transcript cached — run "Transcript & AI Check" for a sharper suggestion).')}</p>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {/* Existing playlists */}
                                        {playlists.length > 0 ? (
                                            <div ref={playlistListRef} className="space-y-1 max-h-48 overflow-y-auto">
                                                {[...playlists].sort((a, b) => Number(!!memberIds?.includes(b.id)) - Number(!!memberIds?.includes(a.id))).map(pl => {
                                                    const isMember = !!memberIds?.includes(pl.id);
                                                    return (
                                                    <div key={pl.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 ${isMember ? 'bg-green-50/70 dark:bg-green-900/10' : ''}`}>
                                                        {isMember ? (
                                                            <span className="flex-shrink-0 w-14 text-center text-[11px] py-0.5 text-green-600 dark:text-green-400 font-semibold">{vn ? 'Đã có' : 'In list'}</span>
                                                        ) : (
                                                        <button
                                                            onClick={() => handleAddToPlaylist(pl)}
                                                            disabled={addingToPlaylistId === pl.id || !ytConnected}
                                                            className="flex-shrink-0 w-14 flex justify-center text-[11px] py-0.5 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 rounded font-semibold text-gray-600 dark:text-gray-300 disabled:opacity-40 transition-colors"
                                                        >
                                                            {addingToPlaylistId === pl.id ? <Loader2 size={9} className="animate-spin" /> : (vn ? 'Thêm' : 'Add')}
                                                        </button>
                                                        )}
                                                        {isMember ? <CheckCircle2 size={11} className="text-green-500 flex-shrink-0" /> : <ListVideo size={11} className="text-gray-400 flex-shrink-0" />}
                                                        <span className="text-[11px] text-gray-700 dark:text-gray-300 flex-1 truncate">{pl.title}</span>
                                                        <span className="text-[10px] text-gray-400 flex-shrink-0">{pl.itemCount}</span>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            playlistsLoaded && <p className="text-[11px] text-gray-400">{vn ? 'Chưa có playlist nào' : 'No playlists yet'}</p>
                                        )}
                                        {/* Create new playlist */}
                                        <div className="border-t border-gray-100 dark:border-slate-700 pt-2">
                                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Tạo playlist mới' : 'New playlist'}</p>
                                            <div className="flex gap-1.5">
                                                <input
                                                    type="text"
                                                    value={newPlaylistTitle}
                                                    onChange={e => setNewPlaylistTitle(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter' && newPlaylistTitle.trim()) { e.preventDefault(); handleCreatePlaylist(); } }}
                                                    placeholder={vn ? 'Tên playlist...' : 'Playlist name...'}
                                                    className="flex-1 text-[11px] border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-red-300 placeholder:text-gray-400"
                                                />
                                                <button
                                                    onClick={handleCreatePlaylist}
                                                    disabled={!newPlaylistTitle.trim() || isCreatingPlaylist || !ytConnected}
                                                    className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-lg disabled:opacity-40 transition-colors"
                                                >
                                                    {isCreatingPlaylist ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
                                                    {vn ? 'Tạo' : 'Create'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}


                        {/* GitHub page link — which of this note's videos get a link on its published page */}
                        {selectedVideo && selectedNode && (
                            <label className="flex items-center gap-2.5 rounded-xl border border-gray-100 dark:border-slate-700 px-3 py-2.5 cursor-pointer">
                                <input type="checkbox" className="w-3.5 h-3.5 accent-red-500 cursor-pointer flex-shrink-0"
                                    checked={isVideoOnGithub(selectedNode, selectedVideo)}
                                    onChange={e => handleToggleGithub(e.target.checked)} />
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex-1">{vn ? 'Hiện link video này trên trang GitHub của note' : "Show this video's link on the note's GitHub page"}</span>
                                {githubToggleMsg && <span className={`text-[11px] font-medium ${githubToggleMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{githubToggleMsg}</span>}
                            </label>
                        )}

                        {/* Audience declaration — mirrors the required "made for kids" question in YouTube Studio */}
                        {selectedVideo && ytConnected && videoAudience !== null && (
                            <div className={`rounded-xl border px-3 py-2.5 flex flex-col gap-2 ${videoAudience === 'unset' ? 'border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/10' : 'border-gray-100 dark:border-slate-700'}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{vn ? 'Đối tượng xem — video này có dành cho trẻ em không?' : 'Audience — is this video made for kids?'}</p>
                                    {videoAudience === 'unset' && <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex-shrink-0">{vn ? 'Chưa khai báo (bắt buộc trước khi công khai)' : 'Not declared (required before going public)'}</span>}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {([['notkids', vn ? 'Không, không dành cho trẻ em' : "No, it's not made for kids"], ['kids', vn ? 'Có, dành cho trẻ em' : "Yes, it's made for kids"]] as const).map(([val, label]) => (
                                        <button key={val} onClick={() => handleSetAudience(val === 'kids')} disabled={audienceSaving}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-50 ${videoAudience === val ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'}`}>
                                            <span className={`w-3 h-3 rounded-full border-2 ${videoAudience === val ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-slate-500'}`} />
                                            {label}
                                        </button>
                                    ))}
                                    {audienceSaving && <Loader2 size={12} className="animate-spin text-gray-400" />}
                                    {audienceMsg && <span className={`text-[11px] font-medium ${audienceMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{audienceMsg}</span>}
                                </div>
                            </div>
                        )}

                        {/* Thumbnail — compact horizontal card, per-video so only show inside a video's detail view */}
                        {selectedVideo && (
                        <div className="rounded-xl border border-gray-100 dark:border-slate-700 p-3 flex gap-3">
                            <div
                                onClick={() => setShowThumbEditor(true)}
                                className="relative flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-600 cursor-pointer group bg-gray-100 dark:bg-slate-800"
                                style={{ width: 240, aspectRatio: '16/9' }}
                            >
                                {thumbDataUrl
                                    ? <img src={thumbDataUrl} alt="Thumbnail" className="w-full h-full object-cover" />
                                    : <YtThumb video={selectedVideo} size="hqdefault" alt="Current thumbnail" className="w-full h-full object-cover" />
                                }
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                    <Edit3 size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                                </div>
                            </div>
                            <div className="flex flex-col justify-between flex-1 min-w-0">
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Thumbnail</p>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                        {vn ? 'Nhấn ảnh để chỉnh sửa' : 'Click image to edit'}
                                    </p>
                                    {thumbMsg && <p className={`text-[11px] mt-1 font-medium ${thumbMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{thumbMsg}</p>}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <button
                                        onClick={openThumbWindow}
                                        className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-[11px] font-semibold transition-colors"
                                    >
                                        <Sparkles size={10} /> Thumbnail
                                    </button>
                                    <button onClick={() => handleUploadThumbnail(thumbDataUrl)} disabled={isUploadingThumb || !ytConnected || !thumbDataUrl}
                                        className="flex items-center gap-1 px-2.5 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-[11px] font-semibold disabled:opacity-40 transition-colors">
                                        {isUploadingThumb ? <Loader2 size={10} className="animate-spin" /> : <Youtube size={10} />}
                                        {vn ? 'Push YouTube' : 'Push to YT'}
                                    </button>
                                    {thumbDataUrl && (
                                        <button onClick={() => handleDownloadThumb(thumbDataUrl)}
                                            className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg text-[11px] font-semibold text-gray-600 dark:text-gray-300 transition-colors">
                                            <Download size={10} /> PNG
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Edit title + description */}
                        {selectedVideo && (
                            <div className="space-y-3">
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{vn ? 'Tiêu đề YouTube' : 'YouTube Title'}</label>
                                        {/* "AI tạo" moved into the "Chuẩn bị video" checklist above — Sync stays here since
                                            it's a maintenance action (pull from YouTube), not a generation step. */}
                                        <button
                                            onClick={handleSyncFromYouTube}
                                            disabled={isSyncingFromYT || !ytConnected}
                                            title={vn ? 'Lấy title/mô tả hiện tại từ YouTube (dùng khi ở đây bị lệch so với YouTube thật)' : "Pull the current title/description from YouTube (use if this got out of sync with what's actually live)"}
                                            className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 text-[10px] font-semibold disabled:opacity-40 transition-colors"
                                        >
                                            {isSyncingFromYT ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                            {vn ? 'Đồng bộ từ YouTube' : 'Sync from YouTube'}
                                        </button>
                                    </div>
                                    {titleGenMsg && <p className="text-[11px] text-red-500 mb-1">{titleGenMsg}</p>}
                                    <input
                                        value={editTitle}
                                        onChange={e => setEditTitle(e.target.value)}
                                        maxLength={100}
                                        className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                                    />
                                    <p className="text-right text-[10px] text-gray-400 mt-0.5">{editTitle.length}/100</p>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{vn ? 'Mô tả' : 'Description'}</label>
                                        <button onClick={() => { setLinkQuery(''); setLinkPicked([]); setShowLinkPicker(true); }}
                                            title={vn ? 'Chọn video khác để chèn link của chúng vào mô tả này' : 'Pick other videos to put their links into this description'}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-500 hover:bg-blue-600 text-white shadow-sm transition-colors">
                                            <ListVideo size={14} /> {vn ? 'Thêm link video khác' : 'Add links to other videos'}
                                        </button>
                                    </div>
                                    {/* "AI tóm tắt"/"Hashtag" moved into the "Chuẩn bị video" checklist above. */}
                                    <textarea
                                        value={editDesc}
                                        onChange={e => setEditDesc(e.target.value)}
                                        rows={10}
                                        className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300 resize-y"
                                    />
                                </div>

                                {/* Note link section — shows computed specific GitHub link and lets user insert it */}
                                {(() => {
                                    const branchTitle = branches.find(b => b.id === selectedNode._branchId)?.title;
                                    const specificLink = notebookPublishUrl ? resolveNoteLink(notebookPublishUrl, branchTitle, selectedNode.id) : null;
                                    if (!specificLink) return null;
                                    const alreadyInserted = editDesc.includes(specificLink);
                                    // A link to this same note under another address: the note has moved
                                    // to a different chapter (or the notebook was republished elsewhere)
                                    // since the description was written.
                                    const staleLink = alreadyInserted ? undefined
                                        : (editDesc.match(new RegExp(`https?://\\S+#node-${selectedNode.id}(?![\\w-])`)) || [])[0];
                                    const applyLink = () => {
                                        const genericLink = notebookPublishUrl ?? '';
                                        let updated = editDesc;
                                        if (staleLink) {
                                            updated = updated.split(staleLink).join(specificLink);
                                        } else if (genericLink && updated.includes(genericLink)) {
                                            // Replace existing generic notebook link with specific note link
                                            updated = updated.replace(genericLink, specificLink);
                                        } else if (!alreadyInserted) {
                                            // Insert before StudyBoard footer, or append
                                            const footerMarker = '🎓 Recorded with StudyBoard';
                                            const idx = updated.indexOf(footerMarker);
                                            const linkLine = `📖 Full notes: ${specificLink}\n`;
                                            updated = idx >= 0
                                                ? updated.slice(0, idx) + linkLine + updated.slice(idx)
                                                : updated.trimEnd() + '\n' + linkLine;
                                        }
                                        setEditDesc(updated);
                                    };
                                    return (
                                        <div className="rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/10 px-3 py-2.5 flex items-start gap-2.5">
                                            <LinkIcon size={13} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1">
                                                    {vn ? 'Link note cụ thể' : 'Specific note link'}
                                                </p>
                                                <p className="text-[11px] text-gray-600 dark:text-gray-300 font-mono break-all leading-relaxed">{specificLink}</p>
                                                {staleLink && (
                                                    <p className="mt-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                                                        {vn ? 'Mô tả đang chứa link cũ của note này (note đã đổi chương). Bấm "Cập nhật link" rồi "Lưu thay đổi".' : 'The description holds an older link to this note (it changed chapter). Press "Update link", then Save.'}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                onClick={applyLink}
                                                disabled={alreadyInserted}
                                                className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors disabled:opacity-40
                                                    bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-300 dark:disabled:bg-gray-700"
                                            >
                                                {alreadyInserted
                                                    ? (vn ? '✓ Đã có' : '✓ Applied')
                                                    : staleLink ? (vn ? '↻ Cập nhật link' : '↻ Update link')
                                                    : (vn ? '↑ Thêm vào mô tả' : '↑ Insert')}
                                            </button>
                                        </div>
                                    );
                                })()}

                                {(selectedVideo.youtubeStatus || 'private') !== 'public' && (
                                    <div>
                                        <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                            <Calendar size={11} /> {vn ? 'Lên lịch đăng' : 'Schedule publish'}
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={scheduleDate}
                                            onChange={e => setScheduleDate(e.target.value)}
                                            min={new Date().toISOString().slice(0, 16)}
                                            className="mt-1 w-full text-sm border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                                        />
                                    </div>
                                )}

                                <div className="flex items-center gap-2 pt-1">
                                    <button
                                        onClick={handleSaveDetails}
                                        disabled={isSaving || !ytConnected}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-100 text-white dark:text-gray-900 text-xs font-bold rounded-xl disabled:opacity-40 transition-colors"
                                    >
                                        {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                        {vn ? 'Lưu thay đổi' : 'Save changes'}
                                    </button>
                                    {(selectedVideo.youtubeStatus || 'private') !== 'public' && !scheduleDate && (
                                        <button
                                            onClick={handlePublish}
                                            disabled={isSaving || !ytConnected}
                                            className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-xl disabled:opacity-40 transition-colors"
                                        >
                                            <Globe size={12} />
                                            {vn ? 'Publish ngay' : 'Publish now'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setDeleteError(''); setShowDeleteConfirm(true); }}
                                        disabled={!ytConnected}
                                        title={vn ? 'Xóa video này khỏi YouTube và khỏi note' : 'Delete this video from YouTube and this note'}
                                        className="ml-auto flex items-center gap-1.5 px-3 py-2 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-bold rounded-xl disabled:opacity-40 transition-colors"
                                    >
                                        <X size={12} /> {vn ? 'Xóa video' : 'Delete video'}
                                    </button>
                                    {saveMsg && <span className={`text-xs font-medium max-w-md ${saveMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>{saveMsg}</span>}
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* "Bản đồ" — every note of the active notebook as a square coloured by its
                    Video-Worthy Score, to see coverage at a glance (see NoteMapGrid). */}
                {viewMode === 'map' && (
                    <NoteMapGrid
                        nodes={allNodes}
                        branches={branchSortMode === 'alpha'
                            ? [...branches].sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' }))
                            : branches}
                        notebooks={allNotebooksMeta}
                        activeNotebookId={activeNotebookMeta.id}
                        onSelectNotebook={id => {
                            const meta = allNotebooksMeta.find(m => m.id === id);
                            if (meta) setActiveNotebookMeta(meta);
                        }}
                        onReload={() => loadNodes(activeNotebookMeta)}
                        loading={loadingNodes}
                        vn={vn}
                        onCombine={picked => openCombinePanelWith(picked.map(({ node, video }) => ({
                            key: `${node.id}::${video.youtubeVideoId}`, node: node as EnhancedNode, video,
                        })))}
                        sortAlpha={branchSortMode === 'alpha'}
                        onToggleSort={() => setBranchSortMode(m => m === 'default' ? 'alpha' : 'default')}
                        onOpenNote={n => {
                            setViewMode('notes');
                            setSelectedNodeId(n.id);
                            if (n._branchId) setSelectedBranchIds(new Set([n._branchId]));
                        }}
                    />
                )}

                {viewMode === 'ideas' && (() => {
                    // "Coverage" excludes dismissed ideas from the denominator — a dismissed idea
                    // isn't unfinished backlog, it's explicitly no longer work. recorded/reviewed/
                    // published all count as "acted on"; suggested/planned are still open.
                    // Notebook filter applies to the stats line too, so it doubles as per-book progress.
                    const ideaNotebooks = [...new Map(videoIdeas.map(v => [v.notebookId, v.notebookTitle || v.notebookId])).entries()]
                        .map(([id, title]) => ({ id, title, count: videoIdeas.filter(v => v.notebookId === id && v.status !== 'dismissed').length }))
                        .sort((a, b) => a.title.localeCompare(b.title));
                    const scopedIdeas = ideaNotebookFilter ? videoIdeas.filter(v => v.notebookId === ideaNotebookFilter) : videoIdeas;
                    const activeIdeas = scopedIdeas.filter(v => v.status !== 'dismissed');
                    const doneIdeas = activeIdeas.filter(v => v.status === 'recorded' || v.status === 'reviewed' || v.status === 'published');
                    const dismissedCount = scopedIdeas.length - activeIdeas.length;
                    const coveredPct = activeIdeas.length > 0 ? Math.round((doneIdeas.length / activeIdeas.length) * 100) : 0;
                    const sortedIdeas = [...scopedIdeas].sort((a, b) => {
                        const diff = (a.score ?? 0) - (b.score ?? 0);
                        return ideaSort === 'score-desc' ? -diff : diff;
                    });
                    const selectedIdea = videoIdeas.find(v => v.id === selectedIdeaId) ?? null;
                    return (
                        <>
                        {/* Column 1 — flat idea list, sorted by score. Clicking a row only updates the
                            detail panel on the right (state: selectedIdeaId); it deliberately does NOT
                            jump tabs — that's what the dedicated icon button on each row (and in the
                            detail header) is for, per explicit feedback that the two should be separate. */}
                        <div className="border-r border-gray-100 dark:border-slate-800 flex flex-col flex-shrink-0 bg-gray-50 dark:bg-slate-900/50 w-[380px]">
                            <div className="px-4 pt-3 pb-2 border-b border-gray-100 dark:border-slate-800">
                                <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                    {vn ? 'Kho ý tưởng video' : 'Video idea backlog'}
                                </p>
                                {!loadingIdeas && videoIdeas.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {vn
                                            ? `Đã quay ${doneIdeas.length}/${activeIdeas.length} ý tưởng (${coveredPct}%)${dismissedCount > 0 ? ` · ${dismissedCount} đã bỏ qua` : ''}`
                                            : `Recorded ${doneIdeas.length}/${activeIdeas.length} ideas (${coveredPct}%)${dismissedCount > 0 ? ` · ${dismissedCount} dismissed` : ''}`}
                                    </p>
                                )}
                                {ideaNotebooks.length > 1 && (
                                    <select
                                        value={ideaNotebookFilter}
                                        onChange={e => setIdeaNotebookFilter(e.target.value)}
                                        className="mt-2 w-full text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300"
                                    >
                                        <option value="">{vn ? `Tất cả notebook (${videoIdeas.filter(v => v.status !== 'dismissed').length})` : `All notebooks (${videoIdeas.filter(v => v.status !== 'dismissed').length})`}</option>
                                        {ideaNotebooks.map(nb => (
                                            <option key={nb.id} value={nb.id}>{nb.title} ({nb.count})</option>
                                        ))}
                                    </select>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                    <select
                                        value={ideaSort}
                                        onChange={e => setIdeaSort(e.target.value as 'score-desc' | 'score-asc')}
                                        className="flex-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300"
                                    >
                                        <option value="score-desc">{vn ? 'Điểm cao nhất' : 'Highest score'}</option>
                                        <option value="score-asc">{vn ? 'Điểm thấp nhất' : 'Lowest score'}</option>
                                    </select>
                                    <button
                                        onClick={loadAllVideoIdeas}
                                        disabled={loadingIdeas}
                                        title={vn ? 'Làm mới' : 'Refresh'}
                                        className="text-indigo-500 hover:text-indigo-700 disabled:opacity-40 p-1.5"
                                    >
                                        {loadingIdeas ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                                    </button>
                                </div>
                                {/* One-time action — hides itself once any migrated idea is found in the
                                    already-loaded data, so it's not sitting there forever inviting an
                                    accidental re-click (harmless since the import is idempotent, but
                                    pointless clutter once it's already been run). */}
                                {!videoIdeas.some(v => v.legacySource === 'youtubeQuality') && (
                                    <button
                                        onClick={handleMigrateLegacyIdeas}
                                        disabled={isMigratingIdeas}
                                        title={vn ? 'Nhập các note đã có Video-Worthy Score từ hệ thống cũ vào đây (chạy an toàn nhiều lần)' : 'Import notes already scored under the old system into this backlog (safe to run more than once)'}
                                        className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-40 px-2 py-1.5 border border-dashed border-gray-300 dark:border-slate-600 rounded-lg"
                                    >
                                        {isMigratingIdeas ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                                        {vn ? 'Nhập ý tưởng từ note đã chấm điểm cũ' : 'Import ideas from legacy-scored notes'}
                                    </button>
                                )}
                                {migrateIdeasMsg && (
                                    <p className={`text-[11px] mt-1 ${migrateIdeasMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{migrateIdeasMsg}</p>
                                )}
                            </div>

                            <div className="flex-1 overflow-y-auto p-2">
                                {loadingIdeas && videoIdeas.length === 0 ? (
                                    <div className="flex items-center justify-center py-16 text-gray-400">
                                        <Loader2 size={20} className="animate-spin" />
                                    </div>
                                ) : videoIdeas.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center py-10 px-2 leading-relaxed">
                                        {vn
                                            ? 'Chưa có note nào được phân tích. Vào tab Ghi chú và bấm "Kiểm tra" ở từng note (hoặc kiểm tra hàng loạt) để bắt đầu tích lũy ý tưởng ở đây.'
                                            : 'No note has been analyzed yet. Run "Check" on notes in the Notes tab (or batch-check) to start building this backlog.'}
                                    </p>
                                ) : (
                                    <div className="space-y-1.5">
                                        {sortedIdeas.map(v => {
                                            const isSelected = v.id === selectedIdeaId;
                                            const statusInfo = IDEA_STATUS_INFO[v.status];
                                            return (
                                                <div
                                                    key={v.id}
                                                    onClick={() => setSelectedIdeaId(v.id)}
                                                    className={`w-full text-left flex items-start gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${isSelected ? 'bg-red-100 dark:bg-red-900/30' : 'bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700/70'}`}
                                                >
                                                    <span className={`text-xs font-black px-2 py-1 rounded-lg flex-shrink-0 ${qualityColor(v.score)}`}>
                                                        {v.score}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{v.title || (vn ? '(Không có tiêu đề)' : '(Untitled)')}</p>
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <p className="text-[10px] text-gray-400 truncate flex-1">{v.notebookTitle} · {v.noteTitle}</p>
                                                            <span className={`text-[9px] font-bold px-1 rounded flex-shrink-0 ${statusInfo.color}`}>{vn ? statusInfo.vi : statusInfo.en}</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={e => { e.stopPropagation(); handleGoToIdeaNote(v); }}
                                                        title={vn ? 'Mở note ở tab Ghi chú' : 'Open note in Notes tab'}
                                                        className="text-gray-300 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 flex-shrink-0 p-0.5"
                                                    >
                                                        <ExternalLink size={13} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Column 2 — detail for whichever idea is selected on the left. */}
                        <div className="flex-1 overflow-y-auto p-5">
                            {!selectedIdea ? (
                                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                                    {vn ? 'Chọn 1 ý tưởng bên trái để xem chi tiết' : 'Select an idea on the left to see its detail'}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900 dark:text-white">{selectedIdea.title || (vn ? '(Không có tiêu đề)' : '(Untitled)')}</h3>
                                            <p className="text-xs text-gray-400 mt-0.5">{selectedIdea.notebookTitle} · {selectedIdea.noteTitle}</p>
                                        </div>
                                        <button
                                            onClick={() => handleGoToIdeaNote(selectedIdea)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 text-xs font-bold rounded-lg flex-shrink-0"
                                        >
                                            <ExternalLink size={12} /> {vn ? 'Mở note ở tab Ghi chú' : 'Open in Notes tab'}
                                        </button>
                                    </div>

                                    <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800 rounded-xl space-y-2">
                                        <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1">
                                            <Zap size={10} className="text-amber-500" /> {vn ? 'Điểm ý tưởng' : 'Idea score'}
                                        </p>
                                        <div className="flex items-start gap-3">
                                            <span className={`text-lg font-black px-2.5 py-1 rounded-lg flex-shrink-0 ${qualityColor(selectedIdea.score)}`}>
                                                {selectedIdea.score}
                                            </span>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 flex-1 leading-relaxed">{selectedIdea.reason}</p>
                                        </div>
                                        {selectedIdea.angle && (
                                            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/10 rounded-lg">
                                                <Play size={11} className="text-red-400 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed italic">{selectedIdea.angle}</p>
                                            </div>
                                        )}
                                        {selectedIdea.scope && (
                                            <p className="text-[11px] text-gray-400 leading-relaxed">
                                                <span className="font-semibold">{vn ? 'Phạm vi: ' : 'Scope: '}</span>{selectedIdea.scope}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-slate-800 rounded-xl">
                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex-shrink-0">{vn ? 'Trạng thái' : 'Status'}</span>
                                        <select
                                            value={selectedIdea.status}
                                            onChange={e => handleUpdateIdeaStatus(selectedIdea.id, e.target.value as VideoIdeaStatus)}
                                            className="flex-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200"
                                        >
                                            {VIDEO_IDEA_STATUS_LIST.map(s => (
                                                <option key={s} value={s}>{vn ? IDEA_STATUS_INFO[s].vi : IDEA_STATUS_INFO[s].en}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => onOpenUpload(selectedIdea.nodeId, selectedIdea.notebookId)}
                                            className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg flex-shrink-0"
                                        >
                                            {vn ? 'Quay video' : 'Record video'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        </>
                    );
                })()}
            </div>
        </div>

        {pendingScoreNodeIds && (() => {
            const idSet = new Set(pendingScoreNodeIds);
            const total = pendingScoreNodeIds.length;
            const unscored = allNodes.filter(n => idSet.has(n.id) && !n.videoAnalysisCheckedAt).length;
            return (
            <div className="fixed inset-0 z-[300] flex items-center justify-center">
                <div className="absolute inset-0 bg-black/50" onClick={() => setPendingScoreNodeIds(null)} />
                <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-[420px] max-w-[95vw] p-5 flex flex-col gap-3">
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">
                        {vn ? 'Chấm điểm — chọn chế độ' : 'Score — choose a mode'}
                    </h3>
                    <button
                        onClick={() => { const ids = pendingScoreNodeIds; setPendingScoreNodeIds(null); runBatchScore(ids, 'skip-scored'); }}
                        disabled={unscored === 0}
                        className="text-left px-4 py-3 border border-gray-200 dark:border-slate-600 hover:border-red-400 dark:hover:border-red-500 rounded-xl transition-colors disabled:opacity-40 disabled:hover:border-gray-200 dark:disabled:hover:border-slate-600 disabled:cursor-not-allowed"
                    >
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
                            {vn ? 'Chỉ note chưa chấm' : 'Only unscored notes'}
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">{unscored}/{total}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{vn ? 'Bỏ qua note đã phân tích rồi — dùng khi chỉ mới thêm note mới vào nhánh.' : 'Skip notes already analyzed — use when you just added new notes to the branch.'}</p>
                    </button>
                    <button
                        onClick={() => { const ids = pendingScoreNodeIds; setPendingScoreNodeIds(null); runBatchScore(ids, 'rescore-all'); }}
                        className="text-left px-4 py-3 border border-gray-200 dark:border-slate-600 hover:border-red-400 dark:hover:border-red-500 rounded-xl transition-colors"
                    >
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
                            {vn ? 'Chấm lại toàn bộ' : 'Rescore everything'}
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400">{total}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{vn ? 'Phân tích lại tất cả note đã chọn, kể cả note đã có kết quả — dùng khi prompt/tiêu chí vừa thay đổi.' : 'Re-analyze every selected note, even ones already scored — use after the prompt or criteria changed.'}</p>
                    </button>
                    <button
                        onClick={() => setPendingScoreNodeIds(null)}
                        className="text-xs font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 self-end"
                    >
                        {vn ? 'Huỷ' : 'Cancel'}
                    </button>
                </div>
            </div>
            );
        })()}

        {showCombinePanel && (
            <div className="fixed inset-0 z-[300] flex items-center justify-center">
                <div className="absolute inset-0 bg-black/50" onClick={() => { if (combinePhase === 'idle' || combinePhase === 'error') setShowCombinePanel(false); }} />
                <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-[440px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <h3 className="font-bold text-gray-900 dark:text-white text-sm">{vn ? 'Ghép video' : 'Combine videos'}</h3>
                        {(combinePhase === 'idle' || combinePhase === 'error') ? (
                            <button onClick={() => setShowCombinePanel(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                                <X size={14} className="text-gray-400" />
                            </button>
                        ) : (
                            <button onClick={() => setShowCombinePanel(false)} className="px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                                {vn ? 'Chạy nền' : 'Run in background'}
                            </button>
                        )}
                    </div>
                    {combinePhase === 'done' && combineAuto ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 py-12 px-8 text-center">
                            {combineAuto.state === 'done'
                                ? <CheckCircle2 size={26} className="text-emerald-500" />
                                : <AlertTriangle size={26} className="text-red-500" />}
                            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                {combineAuto.state === 'done'
                                    ? (vn ? '🤖 Server đã upload video ghép lên YouTube. Tiêu đề, mô tả, AI check, playlist… sẽ tự xong khi YouTube có phụ đề, xong sẽ có email báo.' : '🤖 The server uploaded the combined video. Title, description, AI check and playlist follow once YouTube has captions; an email arrives when done.')
                                    : (vn ? 'Server không upload được video ghép: ' : 'The server could not upload the combined video: ') + (combineAuto.error || '')}
                            </p>
                            <button onClick={() => { setShowCombinePanel(false); setCombinePhase('idle'); setCombineAuto(null); }}
                                className="px-4 py-1.5 bg-gray-900 dark:bg-slate-600 text-white text-xs font-bold rounded-lg">{vn ? 'Đóng' : 'Close'}</button>
                        </div>
                    ) : combinePhase === 'idle' || combinePhase === 'error' ? (
                        <>
                            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
                                <p className="text-[11px] text-gray-400">{vn ? 'Thứ tự ghép — dùng mũi tên để sắp xếp' : 'Combine order — use the arrows to reorder'}</p>
                                {combineOrder.map((item, idx) => (
                                    <div key={item.key} className="flex items-center gap-2 px-2.5 py-2 bg-gray-50 dark:bg-slate-900/50 rounded-xl">
                                        <span className="text-[10px] font-bold text-gray-400 w-4 flex-shrink-0">{idx + 1}</span>
                                        <YtThumb video={item.video} size="mqdefault" className="w-14 h-8 rounded-md object-cover bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                                        <p className="flex-1 min-w-0 text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{item.video.youtubeTitle || item.video.videoTitle || item.video.youtubeVideoId}</p>
                                        <button
                                            onClick={() => setCombineOrder(prev => prev.filter(x => x.key !== item.key))}
                                            title={vn ? 'Bỏ video này khỏi danh sách ghép' : 'Remove from this combine'}
                                            className="text-gray-300 hover:text-red-500 flex-shrink-0"
                                        >
                                            <X size={13} />
                                        </button>
                                        <div className="flex flex-col flex-shrink-0">
                                            <button onClick={() => moveCombineItem(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-20 disabled:cursor-not-allowed">
                                                <ChevronUp size={13} />
                                            </button>
                                            <button onClick={() => moveCombineItem(idx, 1)} disabled={idx === combineOrder.length - 1} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-20 disabled:cursor-not-allowed">
                                                <ChevronDown size={13} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <div className="mt-1">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{vn ? 'Gắn video mới vào note' : 'Attach new video to note'}</label>
                                    <select
                                        value={combineTargetNodeId ?? ''}
                                        onChange={e => setCombineTargetNodeId(e.target.value)}
                                        className="w-full mt-1 text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300"
                                    >
                                        {combineOrder.map(item => (
                                            <option key={item.node.id} value={item.node.id}>{item.node.title}</option>
                                        ))}
                                    </select>
                                </div>
                                {autopilotOn && <p className="text-[11px] text-emerald-600 dark:text-emerald-400">{vn ? '🤖 Chế độ tự động đang bật: ghép xong server tự upload lên YouTube và xử lý như video vừa quay.' : '🤖 Autopilot is on: once combined, the server uploads and processes it like a fresh recording.'}</p>}
                                {combineError && <p className="text-[11px] text-red-500">{combineError}</p>}
                            </div>
                            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                                <button onClick={() => setShowCombinePanel(false)} className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                    {vn ? 'Hủy' : 'Cancel'}
                                </button>
                                <button
                                    onClick={handleStartCombine}
                                    disabled={combineOrder.length < 2 || !combineTargetNodeId}
                                    className="px-4 py-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white text-xs font-bold rounded-lg transition-colors"
                                >
                                    {vn ? 'Bắt đầu ghép' : 'Start combining'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 py-12">
                            <Loader2 size={24} className="animate-spin text-red-400" />
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {combineAuto?.state === 'uploading' ? (vn ? 'Server đang upload lên YouTube...' : 'The server is uploading to YouTube...') : combinePhase === 'pending' ? (vn ? 'Đang khởi động...' : 'Starting...') : (combineProgress != null ? (vn ? `Đang xử lý ${combineProgress}%...` : `Processing ${combineProgress}%...`) : (vn ? 'Đang xử lý...' : 'Processing...'))}
                            </p>
                            <p className="text-[11px] text-gray-400 text-center px-8 leading-relaxed">
                                {autopilotOn
                                    ? (vn ? '🤖 Chế độ tự động: có thể đóng tab. Server sẽ tự upload lên YouTube và xử lý video.' : '🤖 Autopilot: you can close the tab. The server uploads and processes the video.')
                                    : vn
                                    ? 'Video được ghép trên server. Bấm "Chạy nền" để làm việc khác — xong sẽ tự mở hộp upload. Đóng tab cũng không mất: mở lại note đích để lấy video.'
                                    : 'The video is combined on the server. Press "Run in background" to keep working — the upload dialog opens when it is done. Closing the tab loses nothing: reopen the target note to get the video.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        )}

        {/* Jobs running in the background: one pill each, stacked in the corner; a click reopens the panel. */}
        {(() => {
            const pill = 'flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-full shadow-xl text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700';
            const combining = !showCombinePanel && (combinePhase === 'pending' || combinePhase === 'processing');
            const trimBusy = trimPhase === 'pending' || trimPhase === 'processing' || trimPhase === 'fetching';
            const trimming = !!trimTarget && trimMinimized && (trimBusy || trimPhase === 'error');
            const chaptering = showBatchChapters && chaptersMinimized;
            if (!combining && !trimming && !chaptering) return null;
            return (
                <div className="fixed bottom-5 right-5 z-[300] flex flex-col items-end gap-2">
                    {combining && (
                        <button onClick={() => setShowCombinePanel(true)} className={pill}>
                            <Loader2 size={14} className="animate-spin text-red-500" />
                            {combinePhase === 'pending' ? (vn ? 'Đang ghép video — khởi động' : 'Combining — starting') : (vn ? `Đang ghép video ${combineProgress ?? 0}%` : `Combining ${combineProgress ?? 0}%`)}
                        </button>
                    )}
                    {trimming && (
                        <button onClick={() => setTrimMinimized(false)} className={pill}>
                            {trimPhase === 'error'
                                ? <><AlertTriangle size={14} className="text-red-500" />{vn ? 'Cắt video thất bại — bấm để xem' : 'Trim failed — click to view'}</>
                                : <><Loader2 size={14} className="animate-spin text-indigo-500" />
                                    {trimPhase === 'fetching' ? (vn ? 'Đang tải video đã cắt…' : 'Fetching the trimmed video…')
                                        : trimProgress != null ? (vn ? `Đang cắt video ${Math.round(trimProgress)}%` : `Trimming ${Math.round(trimProgress)}%`)
                                        : (vn ? 'Đang cắt video — khởi động' : 'Trimming — starting')}</>}
                        </button>
                    )}
                    {chaptering && (
                        <button onClick={() => setChaptersMinimized(false)} className={pill}>
                            {chaptersBusy
                                ? <><Loader2 size={14} className="animate-spin text-indigo-500" />{vn ? 'Đang thêm mốc thời gian hàng loạt…' : 'Adding chapters in bulk…'}</>
                                : <><CheckCircle2 size={14} className="text-emerald-500" />{vn ? 'Thêm mốc thời gian xong — bấm để xem' : 'Chapters done — click to view'}</>}
                        </button>
                    )}
                </div>
            );
        })()}

        {showThumbBatch && (
            <ThumbnailBatchPanel language={language} onClose={() => setShowThumbBatch(false)}
                onOpenStudio={videoId => { setShowThumbBatch(false); openVideoFromChat(videoId); setShowThumbStudio(true); }} />
        )}

        {showThumbStudio && selectedNode && selectedVideo && (
            <ThumbnailStudio
                key={selectedVideo.youtubeVideoId}
                language={language}
                title={selectedVideo.youtubeTitle || selectedNode.title || 'StudyBoard'}
                notebookTitle={selectedNode._notebookTitle || ''}
                notebookId={selectedNode._notebookId}
                noteContent={selectedNode.content || ''}
                images={studioImagesOf(selectedNode)}
                saved={selectedVideo.thumbnailStudio}
                seedHook={(selectedVideo.thumbnailSettings as Partial<ThumbSettings> | undefined)?.title}
                tabs={<ThumbTabs tab="studio" onTab={setThumbTab} vn={vn} />}
                ytConnected={ytConnected}
                onClose={() => setShowThumbStudio(false)}
                onSave={st => { patchVideoById(selectedVideo.youtubeVideoId, () => ({ thumbnailStudio: JSON.parse(JSON.stringify(st)) as Record<string, unknown> })).catch(() => {}); }}
                onPush={async (st, jpeg) => {
                    const [, mime, b64] = jpeg.match(/^data:([^;]+);base64,(.+)$/) ?? [];
                    await updateYouTubeVideo(selectedVideo.youtubeVideoId, { thumbnailBase64: b64, thumbnailMime: mime });
                    await patchVideoById(selectedVideo.youtubeVideoId, () => ({ thumbnailStudio: JSON.parse(JSON.stringify(st)) as Record<string, unknown>, thumbnailPushedAt: Date.now() }));
                    setThumbDataUrl(jpeg);
                }}
            />
        )}

        {showThumbEditor && selectedNode && (() => {
            const noteImages = buildNoteImages(selectedNode);
            return (
                <ThumbnailEditorModal
                    title={selectedVideo?.youtubeTitle || selectedNode.title || 'StudyBoard'}
                    notebookTitle={selectedNode._notebookTitle || ''}
                    noteImages={noteImages}
                    notebookId={selectedNode._notebookId}
                    language={language}
                    youtubeVideoId={selectedVideo?.youtubeVideoId}
                    ytConnected={ytConnected}
                    noteContent={selectedNode.content}
                    noteImageBase64={selectedNode.imageBase64}
                    noteImageMimeType={selectedNode.imageMimeType}
                    onClose={() => setShowThumbEditor(false)}
                    onThumbnailChange={setThumbDataUrl}
                    initialSettings={selectedVideo?.thumbnailSettings as Partial<ThumbSettings> | undefined}
                    settingsSavedAt={selectedVideo?.thumbnailPushedAt}
                    onPushed={s => persistThumbSettings(s, { pushed: true })}
                    onSettingsChange={s => { latestThumbSettingsRef.current = s; if (s) persistThumbSettings(s); }}
                    drafts={((selectedVideo?.thumbnailDrafts || []) as unknown as ThumbDraft[])}
                    onSaveDraft={saveThumbDraft}
                    onDeleteDraft={deleteThumbDraft}
                    liveThumbUrl={selectedVideo ? ytThumbUrl(selectedVideo, 'hqdefault') : undefined}
                    tabs={selectedVideo ? <ThumbTabs tab="editor" onTab={setThumbTab} vn={vn} /> : undefined}
                />
            );
        })()}

        {pipe && (
            <div className="fixed inset-0 z-[310] flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col">
                    <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                        <p className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            {pipe.running && <Loader2 size={14} className="animate-spin text-red-500" />}
                            {pipe.running
                                ? (vn ? `Đang chạy ${pipe.items.filter(i => i.status !== 'waiting' && i.status !== 'running').length + 1}/${pipe.items.length}` : `Running ${pipe.items.filter(i => i.status !== 'waiting' && i.status !== 'running').length + 1}/${pipe.items.length}`)
                                : (vn ? `Xong: ${pipe.items.filter(i => i.status === 'done').length}/${pipe.items.length} video đã lưu lên YouTube` : `Finished: ${pipe.items.filter(i => i.status === 'done').length}/${pipe.items.length} videos saved to YouTube`)}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{vn ? 'Mỗi video được lưu lên YouTube ngay khi xong, không cần bấm gì thêm. Giữ tab này mở và đừng để máy ngủ; đóng tab là dừng.' : 'Each video is saved to YouTube as soon as it finishes; nothing more to press. Keep this tab open and the computer awake; closing the tab stops it.'}</p>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-1">
                        {pipe.items.map(it => (
                            <div key={it.key} className="flex items-start gap-2 px-2 py-1.5 rounded-lg text-xs">
                                <span className="flex-shrink-0 w-4 mt-0.5">
                                    {it.status === 'running' ? <Loader2 size={12} className="animate-spin text-red-500" />
                                        : it.status === 'done' ? <CheckCircle2 size={12} className="text-green-500" />
                                        : it.status === 'error' ? <AlertTriangle size={12} className="text-red-500" />
                                        : it.status === 'skipped' ? <AlertTriangle size={12} className="text-amber-500" />
                                        : <span className="block w-2 h-2 mt-0.5 ml-0.5 rounded-full bg-gray-300 dark:bg-slate-600" />}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-gray-800 dark:text-gray-100">{it.title}</span>
                                    {(it.step || it.note) && <span className={`block text-[11px] ${it.status === 'error' ? 'text-red-500' : it.status === 'skipped' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>{it.step || it.note}</span>}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-slate-700">
                        {pipe.running
                            ? <button onClick={() => { pipeStop.current = true; }} className="px-4 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">{vn ? 'Dừng sau video này' : 'Stop after this video'}</button>
                            : <button onClick={() => { setPipe(null); setCheckedVideoIds(new Set()); }} className="px-4 py-2 rounded-xl text-sm font-bold bg-gray-900 dark:bg-white text-white dark:text-gray-900">{vn ? 'Đóng' : 'Close'}</button>}
                    </div>
                </div>
            </div>
        )}

        {showOrphans && (
            <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4" onClick={() => !orphanBusy && setShowOrphans(false)}>
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{vn ? 'Video không còn note' : 'Videos without a note'}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{vn ? 'Note của các video này đã bị xóa. Video vẫn còn trên YouTube. Gõ tên một note để gắn video vào đó; transcript, AI Check và thumbnail đã lưu sẽ đi theo.' : 'The notes of these videos were deleted. The videos are still on YouTube. Type a note name to attach a video to it; its saved transcript, AI Check and thumbnail come along.'}</p>
                        {orphanMsg && <p className={`text-[11px] font-medium mt-2 ${orphanMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{orphanMsg}</p>}
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
                        {orphans.length === 0 && <p className="py-8 text-center text-xs text-gray-400">{vn ? 'Không còn video nào.' : 'No videos left.'}</p>}
                        {orphans.map(o => {
                            const id = o.video.youtubeVideoId;
                            const q = (orphanQuery[id] || '').trim().toLowerCase();
                            const matches = q.length < 2 ? [] : allNodes.filter(n => (n.title || '').toLowerCase().includes(q)).slice(0, 8);
                            return (
                                <div key={id} className="rounded-xl border border-gray-200 dark:border-slate-700 p-3">
                                    <div className="flex items-start gap-3">
                                        <YtThumb video={o.video} size="mqdefault" className="w-24 h-14 rounded-md object-cover bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{o.video.youtubeTitle || o.video.videoTitle || id}</p>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{vn ? 'Note cũ' : 'Was on'}: {o.fromNodeTitle || '—'}{o.fromNotebookTitle ? ` · ${o.fromNotebookTitle}` : ''}</p>
                                            <p className="text-[10px] text-gray-400">{vn ? 'Note bị xóa lúc' : 'Note deleted'} {new Date(o.deletedAt).toLocaleString(vn ? 'vi-VN' : undefined)}</p>
                                            <div className="flex items-center gap-3 mt-1">
                                                <a href={`https://www.youtube.com/watch?v=${id}`} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-red-500 hover:underline">{vn ? 'Xem trên YouTube' : 'View on YouTube'}</a>
                                                <button onClick={() => handleDismissOrphan(o)} disabled={orphanBusy === id} className="text-[11px] font-semibold text-gray-400 hover:text-red-500 disabled:opacity-40">{vn ? 'Bỏ khỏi danh sách' : 'Remove from list'}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <input value={orphanQuery[id] || ''} onChange={e => setOrphanQuery(p => ({ ...p, [id]: e.target.value }))}
                                        placeholder={vn ? 'Gắn vào note… (gõ tên note)' : 'Attach to note… (type its name)'}
                                        className="mt-2 w-full text-xs border border-gray-200 dark:border-slate-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 outline-none focus:border-blue-400" />
                                    {matches.length > 0 && (
                                        <div className="mt-1 space-y-0.5">
                                            {matches.map(n => (
                                                <button key={n.id} onClick={() => handleAttachOrphan(o, n)} disabled={orphanBusy === id}
                                                    className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs hover:bg-blue-50 dark:hover:bg-slate-800 disabled:opacity-40 flex items-center gap-2">
                                                    {orphanBusy === id ? <Loader2 size={11} className="animate-spin flex-shrink-0" /> : <LinkIcon size={11} className="text-blue-500 flex-shrink-0" />}
                                                    <span className="truncate text-gray-800 dark:text-gray-100">{n.title || '(no title)'}</span>
                                                    <span className="ml-auto flex-shrink-0 text-[10px] text-gray-400">{n._notebookTitle}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {q.length >= 2 && matches.length === 0 && <p className="mt-1 px-1 text-[11px] text-gray-400">{vn ? 'Không tìm thấy note nào trong notebook đang mở ở đây.' : 'No note found in the notebook loaded here.'}</p>}
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-end px-5 py-3 border-t border-gray-100 dark:border-slate-700">
                        <button onClick={() => setShowOrphans(false)} disabled={!!orphanBusy} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-40">{vn ? 'Đóng' : 'Close'}</button>
                    </div>
                </div>
            </div>
        )}

        {showLinkPicker && selectedVideo && selectedNode && (() => {
            const q = linkQuery.trim().toLowerCase();
            const rank = (x: { node: EnhancedNode }) => (x.node.id === selectedNode.id ? 0 : x.node._notebookId === selectedNode._notebookId ? 1 : 2);
            const titleOf = (x: { video: YouTubeVideoEntry; node: EnhancedNode }) => x.video.youtubeTitle || x.video.videoTitle || x.node.title || 'Video';
            const candidates = flatVideos
                .filter(x => x.video.youtubeVideoId !== selectedVideo.youtubeVideoId)
                .filter(x => !q || titleOf(x).toLowerCase().includes(q) || (x.node.title || '').toLowerCase().includes(q))
                .sort((a, b) => rank(a) - rank(b) || (b.video.youtubeUploadedAt || 0) - (a.video.youtubeUploadedAt || 0));
            const shown = candidates.slice(0, 80);
            const urlOf = (id: string) => `https://www.youtube.com/watch?v=${id}`;
            const groupLabel = (r: number) => r === 0 ? (vn ? 'Cùng note' : 'Same note') : r === 1 ? (vn ? 'Cùng notebook' : 'Same notebook') : (vn ? 'Notebook khác' : 'Other notebooks');
            const add = () => {
                const links = linkPicked
                    .map(id => flatVideos.find(x => x.video.youtubeVideoId === id))
                    .filter((x): x is NonNullable<typeof x> => !!x)
                    .map(x => ({ title: titleOf(x), url: urlOf(x.video.youtubeVideoId) }));
                setEditDesc(d => addRelatedLinksToDescription(d, links, vn));
                setShowLinkPicker(false);
            };
            return (
                <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowLinkPicker(false)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{vn ? 'Thêm link video khác vào mô tả' : 'Add links to other videos'}</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">{vn ? 'Link được chèn vào ô mô tả; bấm "Lưu thay đổi" thì mới lên YouTube.' : 'Links go into the description box; they reach YouTube when you press Save.'}</p>
                            <input autoFocus value={linkQuery} onChange={e => setLinkQuery(e.target.value)} placeholder={vn ? 'Tìm theo tiêu đề video hoặc tên note…' : 'Search by video title or note name…'}
                                className="mt-3 w-full text-sm border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 outline-none focus:border-blue-400" />
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
                            {shown.length === 0 && <p className="px-2 py-6 text-center text-xs text-gray-400">{vn ? 'Không có video nào khớp.' : 'No video matches.'}</p>}
                            {shown.map((x, i) => {
                                const id = x.video.youtubeVideoId;
                                const on = linkPicked.includes(id);
                                const already = editDesc.includes(urlOf(id));
                                const r = rank(x);
                                const firstOfGroup = i === 0 || rank(shown[i - 1]) !== r;
                                const isPublic = x.video.youtubeStatus === 'public';
                                return (
                                    <React.Fragment key={id}>
                                        {firstOfGroup && <p className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">{groupLabel(r)}</p>}
                                        <label className={`flex items-start gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 ${already ? 'opacity-50' : ''}`}>
                                            <input type="checkbox" className="mt-0.5 accent-blue-500" checked={on || already} disabled={already}
                                                onChange={() => setLinkPicked(p => (on ? p.filter(y => y !== id) : [...p, id]))} />
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{titleOf(x)}</span>
                                                <span className="block text-[11px] text-gray-400 truncate">
                                                    {x.node.title || ''}{r === 2 && x.node._notebookTitle ? ` · ${x.node._notebookTitle}` : ''}
                                                    {already ? ` · ${vn ? 'đã có trong mô tả' : 'already in the description'}` : ''}
                                                </span>
                                            </span>
                                            {!isPublic && (
                                                <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" title={vn ? 'Người xem chưa mở được link này cho tới khi video được công khai' : 'Viewers cannot open this link until the video is public'}>
                                                    {x.video.youtubeStatus === 'scheduled' ? (vn ? 'Đã lên lịch' : 'Scheduled') : (vn ? 'Riêng tư' : 'Private')}
                                                </span>
                                            )}
                                        </label>
                                    </React.Fragment>
                                );
                            })}
                            {candidates.length > shown.length && <p className="px-2 py-2 text-[11px] text-gray-400">{vn ? `Còn ${candidates.length - shown.length} video nữa — gõ để tìm.` : `${candidates.length - shown.length} more — type to search.`}</p>}
                        </div>
                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-slate-700">
                            <button onClick={() => setShowLinkPicker(false)} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800">{vn ? 'Hủy' : 'Cancel'}</button>
                            <button onClick={add} disabled={linkPicked.length === 0} className="px-4 py-2 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-40">
                                {vn ? `Thêm ${linkPicked.length || ''} link`.replace('  ', ' ') : `Add ${linkPicked.length || ''} link${linkPicked.length === 1 ? '' : 's'}`.replace('  ', ' ')}
                            </button>
                        </div>
                    </div>
                </div>
            );
        })()}

        {noteDraft && (() => {
            const target = allNodes.find(n => n.id === noteDraft.nodeId);
            const hasText = !!(target?.content || '').trim();
            return (
                <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4" onClick={() => !isSavingNote && setNoteDraft(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{vn ? 'Ghi chú từ video' : 'Note from video'} — {target?.title || ''}</p>
                            <p className="text-[11px] text-gray-500 mt-1">
                                {noteDraft.source === 'recording'
                                    ? (vn ? 'Nguồn: bản chép lời lúc quay.' : 'Source: the transcript made while recording.')
                                    : (vn ? 'Nguồn: transcript của YouTube (có thể sai thuật ngữ — đọc lại trước khi lưu).' : "Source: YouTube's transcript (terms may be wrong — read it before saving).")}{' '}
                                {hasText
                                    ? (vn ? 'Note đã có nội dung: phần này sẽ được thêm xuống cuối, không đè lên gì.' : 'The note already has text: this is added at the end, nothing is replaced.')
                                    : (vn ? 'Note đang trống: phần này sẽ thành nội dung note.' : 'The note is empty: this becomes its text.')}
                            </p>
                        </div>
                        <textarea
                            value={noteDraft.text}
                            onChange={e => setNoteDraft(d => d ? { ...d, text: e.target.value } : d)}
                            className="flex-1 min-h-[320px] m-4 p-3 text-sm leading-relaxed font-mono rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-800 dark:text-gray-100 outline-none focus:border-blue-400 resize-none"
                        />
                        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-slate-700">
                            <button onClick={() => setNoteDraft(null)} disabled={isSavingNote} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-40">{vn ? 'Hủy' : 'Cancel'}</button>
                            <button onClick={handleSaveNoteFromVideo} disabled={isSavingNote || !noteDraft.text.trim()} className="px-4 py-2 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-40 flex items-center gap-1.5">
                                {isSavingNote && <Loader2 size={13} className="animate-spin" />}
                                {hasText ? (vn ? 'Thêm vào cuối note' : 'Add to the end of the note') : (vn ? 'Lưu vào note' : 'Save to the note')}
                            </button>
                        </div>
                    </div>
                </div>
            );
        })()}

        {/* Explicit "connect YouTube first" prompt — shown instead of auto-triggering OAuth */}
        {rowMenu && (() => {
            const entry = flatVideos.find(x => x.video.youtubeVideoId === rowMenu.videoId);
            if (!entry) return null;
            const canCopy = !!styleSourceOf(entry.video);
            const listed = filteredFlatVideos.filter(x => x.video.youtubeVideoId !== copiedStyle?.fromVideoId);
            const left = Math.min(rowMenu.x, window.innerWidth - 300);
            const top = Math.min(rowMenu.y, window.innerHeight - 190);
            const item = 'w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-transparent transition-colors';
            return (
                <>
                    <div className="fixed inset-0 z-[740]" onClick={() => setRowMenu(null)} onContextMenu={e => { e.preventDefault(); setRowMenu(null); }} />
                    <div className="fixed z-[741] w-72 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-2xl overflow-hidden" style={{ left, top }}>
                        <button className={item} disabled={!canCopy} onClick={() => { copyStyleFrom(entry.video); setRowMenu(null); }}
                            title={canCopy ? undefined : (vn ? 'Video này chưa có style đã lưu — mở Thumbnail Editor, chỉnh và push (hoặc lưu nháp) một lần' : 'No saved style yet — open the Thumbnail Editor and push (or save a draft) once')}>
                            <span className="font-semibold text-gray-800 dark:text-gray-100">{vn ? 'Sao chép style thumbnail' : 'Copy thumbnail style'}</span>
                            {!canCopy && <span className="block text-[10px] text-gray-400">{vn ? 'chưa có style đã lưu' : 'no saved style yet'}</span>}
                        </button>
                        <button className={`${item} border-t border-gray-100 dark:border-slate-700`} disabled={!copiedStyle} onClick={() => { setRowMenu(null); applyStyleToVideos([entry]); }}>
                            <span className="font-semibold text-gray-800 dark:text-gray-100">{vn ? 'Dán style vào video này' : 'Paste style onto this video'}</span>
                            <span className="block text-[10px] text-gray-400 truncate">{copiedStyle ? `${vn ? 'từ' : 'from'}: ${copiedStyle.fromTitle}` : (vn ? 'chưa sao chép style nào' : 'no style copied yet')}</span>
                        </button>
                        <button className={`${item} border-t border-gray-100 dark:border-slate-700`} disabled={!copiedStyle || listed.length < 1} onClick={() => { setRowMenu(null); applyStyleToVideos(listed); }}>
                            <span className="font-semibold text-gray-800 dark:text-gray-100">{vn ? `Dán style vào ${listed.length} video đang hiện trong danh sách` : `Paste style onto the ${listed.length} listed videos`}</span>
                            <span className="block text-[10px] text-gray-400">{vn ? 'tạo bản nháp cho từng video, chưa push lên YouTube' : 'creates a draft per video — nothing is pushed yet'}</span>
                        </button>
                    </div>
                </>
            );
        })()}

        {styleReview && (
            <div className="fixed inset-0 z-[750] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => styleReview.phase === 'review' && setStyleReview(null)}>
                <div className="w-[980px] max-w-full max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <h3 className="font-bold text-sm text-gray-900 dark:text-white flex-1">
                            {styleReview.phase === 'generating' ? (vn ? `Đang tạo bản nháp ${styleReview.done}/${styleReview.total}…` : `Creating drafts ${styleReview.done}/${styleReview.total}…`) : (vn ? 'Kết quả dán style — bản nháp' : 'Pasted style — drafts')}
                        </h3>
                        <button onClick={() => setStyleReview(null)} disabled={styleReview.phase !== 'review'} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-40"><X size={14} className="text-gray-400" /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {styleReview.items.map(it => (
                            <label key={it.key} className={`rounded-xl border overflow-hidden flex flex-col cursor-pointer ${it.status === 'error' ? 'border-red-300 dark:border-red-800' : it.status === 'pushed' ? 'border-green-400 dark:border-green-700' : 'border-gray-200 dark:border-slate-700'}`}>
                                {it.preview ? <img src={it.preview} alt="" className="w-full aspect-video object-cover bg-gray-100 dark:bg-slate-800" /> : <div className="w-full aspect-video bg-gray-100 dark:bg-slate-800" />}
                                <div className="p-2.5 flex items-start gap-2">
                                    <input type="checkbox" className="mt-0.5 accent-red-500" checked={it.selected} disabled={it.status !== 'draft' || styleReview.phase !== 'review'}
                                        onChange={() => setStyleReview(prev => prev ? { ...prev, items: prev.items.map(x => x.key === it.key ? { ...x, selected: !x.selected } : x) } : prev)} />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{it.title}</p>
                                        {it.status === 'draft' && <p className="text-[10px] text-gray-400">{vn ? 'Bản nháp — chưa push' : 'Draft — not pushed'}</p>}
                                        {it.status === 'pushing' && <p className="text-[10px] text-indigo-500 flex items-center gap-1"><Loader2 size={9} className="animate-spin" />{vn ? 'Đang push…' : 'Pushing…'}</p>}
                                        {it.status === 'pushed' && <p className="text-[10px] text-green-600 dark:text-green-400">✓ {vn ? 'Đã đặt lên YouTube' : 'Live on YouTube'}</p>}
                                        {it.status === 'error' && <p className="text-[10px] text-red-500 break-words">{it.error}</p>}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <p className="text-[11px] text-gray-400 flex-1">
                            {styleReview.message ? <span className="text-amber-500">{styleReview.message}</span> : (vn ? 'Mỗi ảnh push tốn khoảng 50 đơn vị quota YouTube. Chỉ những mục được tick mới push; các mục khác vẫn lưu làm bản nháp (xem trong Thumbnail Editor → So sánh).' : 'Each pushed thumbnail costs ~50 YouTube quota units. Only ticked items are pushed; the rest stay as drafts (Thumbnail Editor → Compare).')}
                        </p>
                        <button onClick={() => setStyleReview(null)} disabled={styleReview.phase !== 'review'} className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-40">{vn ? 'Đóng' : 'Close'}</button>
                        <button onClick={pushStyleReview}
                            disabled={styleReview.phase !== 'review' || !ytConnected || !styleReview.items.some(i => i.selected && i.status === 'draft')}
                            className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-colors">
                            {styleReview.phase === 'pushing' ? <Loader2 size={12} className="animate-spin" /> : <Youtube size={12} />}
                            {vn ? `Push ${styleReview.items.filter(i => i.selected && i.status === 'draft').length} mục đã chọn` : `Push ${styleReview.items.filter(i => i.selected && i.status === 'draft').length} selected`}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {descPreview && (() => {
            const entry = flatVideos.find(x => x.video.youtubeVideoId === descPreview.videoId);
            if (!entry) return null;
            const desc = (entry.video.youtubeDescription || '').trim();
            const width = 440;
            const left = descPreview.left + width > window.innerWidth ? Math.max(8, descPreview.left - width - 40) : descPreview.left;
            const top = Math.max(8, Math.min(descPreview.top - 8, window.innerHeight - 330));
            return (
                <div
                    onMouseEnter={cancelHideDescPreview}
                    onMouseLeave={scheduleHideDescPreview}
                    className="fixed z-[700] rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-2xl p-3 flex flex-col"
                    style={{ top, left, width, maxHeight: 400 }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1 flex-shrink-0">{vn ? 'Mô tả' : 'Description'}</p>
                    {desc
                        ? <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words leading-relaxed overflow-y-auto">{desc}</p>
                        : <p className="text-xs text-gray-400 italic">{vn ? '(Chưa có mô tả được lưu — mở video này một lần để tải từ YouTube)' : '(No saved description — open this video once to load it from YouTube)'}</p>}
                </div>
            );
        })()}

        {showChat && !minimized && (
            <YouTubeManagerChat language={language} getSnapshot={buildChatSnapshot} onApplyChange={applyChatChange} onOpenVideo={openVideoFromChat} onClose={() => setShowChat(false)} />
        )}

        {showDeleteConfirm && selectedVideo && (
            <div className="fixed inset-0 z-[650] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !isDeletingVideo && setShowDeleteConfirm(false)}>
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 w-[420px] max-w-[95vw] p-5" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={18} className="text-red-500" />
                        <h3 className="font-bold text-sm text-gray-900 dark:text-white">{vn ? 'Xóa video khỏi YouTube?' : 'Delete video from YouTube?'}</h3>
                    </div>
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2 break-words">{selectedVideo.youtubeTitle || selectedVideo.videoTitle || selectedVideo.youtubeVideoId}</p>
                    <ul className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4 list-disc pl-4 space-y-1">
                        <li>{vn ? 'Video bị xóa vĩnh viễn khỏi kênh YouTube của bạn — không thể khôi phục.' : 'The video is permanently deleted from your YouTube channel — it cannot be restored.'}</li>
                        <li>{vn ? 'Video được gỡ khỏi note này; link trên trang GitHub biến mất ở lần sync kế tiếp.' : 'The video is removed from this note; its GitHub-page link disappears on the next sync.'}</li>
                        <li>{vn ? 'Bản sao lưu trên Storage (nếu có) không bị xóa.' : 'The backup copy in Storage (if any) is not deleted.'}</li>
                    </ul>
                    {deleteError && <p className="text-xs text-red-500 mb-3 break-words">{deleteError}</p>}
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowDeleteConfirm(false)} disabled={isDeletingVideo} className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg transition-colors disabled:opacity-50">
                            {vn ? 'Hủy' : 'Cancel'}
                        </button>
                        <button onClick={handleDeleteVideo} disabled={isDeletingVideo} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50">
                            {isDeletingVideo && <Loader2 size={11} className="animate-spin" />}
                            {vn ? 'Xóa vĩnh viễn' : 'Delete permanently'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {trimTarget && (
            <TrimVideoPanel
                key={trimTarget.video.youtubeVideoId}
                video={trimTarget.video}
                noteTitle={trimTarget.node.title || ''}
                vn={vn}
                language={language}
                phase={trimPhase}
                progress={trimProgress}
                error={trimError}
                onStart={handleStartTrim}
                autoSuggest={trimTarget.autoSuggest}
                hidden={trimMinimized}
                onMinimize={() => setTrimMinimized(true)}
                onClose={() => { trimUnsubRef.current?.(); setTrimTarget(null); setTrimPhase('idle'); }}
            />
        )}
        {showBatchChapters && (
            <BatchChaptersPanel
                items={flatVideos.filter(x => !!x.video.youtubeVideoId)}
                vn={vn}
                language={language}
                ytConnected={ytConnected}
                onClose={() => setShowBatchChapters(false)}
                hidden={chaptersMinimized}
                onMinimize={() => setChaptersMinimized(true)}
                onBusyChange={setChaptersBusy}
                onSaveTranscript={persistTranscriptFor}
                onApplied={async (node, video, description) => {
                    const uid = auth.currentUser?.uid;
                    if (!uid) return;
                    const patch = buildVideoUpdatePatch(node, video.youtubeVideoId, { youtubeDescription: description, youtubeEditedAt: Date.now() });
                    await updateDoc(doc(db, `users/${uid}/notebooks/${node._notebookId}/nodes/${node.id}`), patch);
                    setAllNodes(prev => prev.map(n => n.id === node.id ? { ...n, ...patch } : n));
                    // The open editor shows this video's description: keep it in step.
                    if (selectedVideo?.youtubeVideoId === video.youtubeVideoId) setEditDesc(description);
                }}
            />
        )}
        {showBatchConnectPrompt && (
            <div className="fixed inset-0 z-[650] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowBatchConnectPrompt(false)}>
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 w-[380px] max-w-[95vw] p-5" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2 mb-2">
                        <Youtube size={18} className="text-red-500" />
                        <h3 className="font-bold text-sm text-gray-900 dark:text-white">{vn ? 'Cần kết nối YouTube' : 'YouTube connection needed'}</h3>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                        {vn
                            ? 'Kiểm tra hàng loạt cần lấy transcript từ YouTube, nên phải kết nối tài khoản YouTube trước.'
                            : 'Batch checking needs to fetch transcripts from YouTube, so your YouTube account has to be connected first.'}
                    </p>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowBatchConnectPrompt(false)} className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg transition-colors">
                            {vn ? 'Để sau' : 'Not now'}
                        </button>
                        <button onClick={() => { connectYouTube(); setShowBatchConnectPrompt(false); }} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-colors">
                            {vn ? 'Kết nối YouTube' : 'Connect YouTube'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* "Argue with AI Check" — ephemeral chat docked to the right edge of the screen */}
        {showDiscussPanel && (
            <div className="fixed inset-y-0 right-0 w-[380px] max-w-[92vw] z-[650] bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700 shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-800 shrink-0">
                    <div className="flex items-center gap-2">
                        <MessageSquare size={15} className="text-indigo-500" />
                        <span className="font-bold text-sm text-gray-900 dark:text-white">{vn ? 'Tranh luận với AI Check' : 'Argue with AI Check'}</span>
                    </div>
                    <button onClick={() => setShowDiscussPanel(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                        <X size={14} className="text-gray-400" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                    {discussMessages.length === 0 && (
                        <div className="text-center py-10 space-y-2">
                            <MessageSquare size={28} className="text-gray-200 dark:text-gray-700 mx-auto" />
                            <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed px-4">
                                {vn
                                    ? 'Không đồng ý với kết quả AI Check? Giải thích lý do ở đây — AI sẽ xem lại transcript và tự bảo vệ quan điểm hoặc sửa lại nếu bạn đúng.'
                                    : "Disagree with the AI Check verdict? Explain why here — the AI will re-examine the transcript and either defend its call or revise it if you're right."}
                            </p>
                        </div>
                    )}
                    {discussMessages.map((msg, i) => msg.role === 'user' ? (
                        <div key={i} className="flex justify-end">
                            <div className="max-w-[88%] px-3 py-2 bg-indigo-600 text-white rounded-2xl rounded-br-sm text-xs leading-relaxed whitespace-pre-wrap">
                                {msg.text}
                            </div>
                        </div>
                    ) : (
                        <div key={i} className="flex flex-col gap-1.5">
                            <div className="max-w-[92%] px-3 py-2 bg-white dark:bg-slate-700 border border-gray-100 dark:border-slate-600 rounded-2xl rounded-tl-sm text-xs text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                                {msg.text}
                            </div>
                            {msg.revisedVerdict && (
                                <div className="max-w-[92%] rounded-xl border border-indigo-200 dark:border-indigo-700/60 overflow-hidden">
                                    <div className="px-3 py-2 bg-indigo-50/60 dark:bg-indigo-900/20 space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                                                msg.revisedVerdict.recommendation === 're_record' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                                                msg.revisedVerdict.recommendation === 'publish_with_correction' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                                                'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                            }`}>
                                                {msg.revisedVerdict.recommendation === 're_record' ? (vn ? '⛔ Cần quay lại' : '⛔ Re-record')
                                                    : msg.revisedVerdict.recommendation === 'publish_with_correction' ? (vn ? '✓ Publish (kèm correction)' : '✓ Publish with correction')
                                                    : (vn ? '✓ Publish' : '✓ Publish')}
                                            </span>
                                            <span className="text-[10px] text-gray-400">{msg.revisedVerdict.score}/100</span>
                                        </div>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">{vn ? 'Kết quả đã được xem lại' : 'Verdict revised'}</p>
                                    </div>
                                    <div className="px-2 py-1.5 bg-white dark:bg-slate-700 border-t border-indigo-100 dark:border-indigo-800 flex justify-end">
                                        <button onClick={() => handleAcceptRevisedVerdict(msg.revisedVerdict!)}
                                            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 rounded-lg transition-colors">
                                            {vn ? 'Cập nhật kết quả' : 'Apply update'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {discussLoading && (
                        <div className="flex items-center gap-2 px-1 py-1">
                            <Loader2 size={12} className="animate-spin text-indigo-400" />
                            <span className="text-xs text-gray-400">{vn ? 'AI đang xem lại...' : 'AI is reconsidering…'}</span>
                        </div>
                    )}
                </div>
                <div className="px-3 py-2.5 border-t dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={discussInput}
                            onChange={e => setDiscussInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendDiscuss(); } }}
                            placeholder={vn ? 'Giải thích vì sao bạn không đồng ý...' : 'Explain why you disagree…'}
                            rows={2}
                            className="flex-1 px-3 py-2 text-xs rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 outline-none text-gray-700 dark:text-gray-200 placeholder-gray-400 resize-none leading-relaxed focus:border-indigo-400 dark:focus:border-indigo-500"
                        />
                        <button onClick={handleSendDiscuss} disabled={!discussInput.trim() || discussLoading}
                            className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-40 shrink-0 self-end">
                            <Send size={14} />
                        </button>
                    </div>
                </div>
            </div>
        )}

        {(combinedSuggestion || combinedSuggestError) && (
            <div className="fixed inset-0 z-[600] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => { setCombinedSuggestion(null); setCombinedSuggestError(''); }}>
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 w-[520px] max-w-[95vw] max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                        <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
                            <Sparkles size={16} className="text-violet-600 dark:text-violet-400" />
                        </div>
                        <h3 className="font-bold text-gray-900 dark:text-white text-sm flex-1">
                            {vn ? 'Gợi ý video tổng hợp' : 'Combined video suggestion'}
                        </h3>
                        <button onClick={() => { setCombinedSuggestion(null); setCombinedSuggestError(''); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                            <X size={14} className="text-gray-400" />
                        </button>
                    </div>
                    <div className="p-5">
                        {combinedSuggestError ? (
                            <p className="text-sm text-red-500">{combinedSuggestError}</p>
                        ) : combinedSuggestion && (
                            <div className="flex flex-col gap-4">
                                <div>
                                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Title</p>
                                    <p className="text-sm font-bold text-gray-900 dark:text-white leading-snug">{combinedSuggestion.title}</p>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                    <Play size={11} className="text-violet-400 flex-shrink-0" />
                                    {vn ? `Ước tính: ~${combinedSuggestion.estimatedMinutes} phút` : `Estimated: ~${combinedSuggestion.estimatedMinutes} min`}
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{vn ? 'Chủ đề liên kết' : 'Connecting theme'}</p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{combinedSuggestion.connectingTheme}</p>
                                </div>
                                <div className="px-3 py-2.5 bg-violet-50 dark:bg-violet-900/10 rounded-xl border border-violet-100 dark:border-violet-900/30">
                                    <p className="text-[10px] font-semibold text-violet-500 uppercase tracking-wider mb-1">Hook</p>
                                    <p className="text-sm text-violet-700 dark:text-violet-300 italic leading-relaxed">{combinedSuggestion.hook}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">{vn ? 'Thứ tự trình bày & dàn ý' : 'Presentation order & outline'}</p>
                                    <div className="flex flex-col gap-2">
                                        {combinedSuggestion.order.map((title, i) => (
                                            <div key={i} className="flex gap-2.5">
                                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">{title}</p>
                                                    {combinedSuggestion.outline[i] && (
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">{combinedSuggestion.outline[i]}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
